import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	AsyncSelect,
	Box,
	Button,
	Divider,
	Heading,
	Icons,
	Inline,
	InputValidationErrorMessage,
	Stack,
	Table,
	Tabs,
	TabsList,
	TabContent,
	TabItem,
	Text,
	Time,
	Select,
	Dropdown,
	Component,
	CheckboxInput,
	Badge,
	noSpinnersClassName,
	BoxOwnProps,
	SwitchInput,
	Tooltip,
	Spinner,
} from "@sembark-travel/ui-base"
import {
	Dialog,
	DialogBody,
	DialogFooter,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	CheckboxInputField,
	DatePickerField,
	FieldArray,
	Form,
	useForm,
	useFormState,
	GetFieldValue,
	isTruthy,
	RenderError,
	SelectField,
	SwitchInputField,
	TextInputField,
	FieldArrayErrorMessage,
	useField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
	SubmissionError,
	type FormApi,
	RadioInputField,
} from "@sembark-travel/ui-form"
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
	memo,
	Fragment,
} from "react"
import { themeContractVars } from "@sembark-travel/ui-theme"
import * as Validator from "yup"
import {
	NavigationPromptDialog,
	queryToSearch,
	useGetBackUrlFromLocation,
	useNavigationPrompt,
} from "@sembark-travel/ui-router"
import { IHotel, SelectHotels, useHotel } from "../Hotels"
import type { THotelOtaMealPlan, TOtaMealPlan } from "../HotelOtaMealPlans"
import type { THotelOtaRoomType, TOtaRoomType } from "../HotelOtaRoomTypes"
import {
	rateSheetsXHR,
	TDay,
	type TPricingType,
	type TRoundingPointSchema,
	usePinnedPolicyVersion,
	useIsMobile,
	useRateSheetFormData,
	useSheetHotelDetails,
	useSheetPolicyOptions,
	useSheetSelection,
} from "./store"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	blockGridOverrides,
	blockNeedsMealRates,
	buildAboveRowIndicesMap,
	buildGridGeometry,
	buildGridRates,
	buildPaxTypes,
	buildRoomCols,
	deriveGridRates,
	getInitialColumnDerivations,
	buildRooms,
	baseOccupancyChanges,
	resetChangedBaseOccupancyRates,
	buildSheetRatesRef,
	collectRoomRateCellWarnings,
	collectRoomRateWarnings,
	columnDerivationChanged,
	columnDerivationsFromPaxTypes,
	compareMealPlanEntriesByPriority,
	computeSheetSummary,
	describeIssueSources,
	getHierarchyWarnings,
	getMealCourses,
	validateBlockMealRates,
	getOccupancyInfo,
	computeBlockErrors,
	derivedOccupanciesFromColumns,
	isDerivedRate,
	getRoomBaseOccupancy,
	mealCourseKey,
	occupancyKeysOf,
	paxColumnsChanged,
	mealPlansWithSavedBase,
	pruneDeletedMealPlanEntries,
	pruneDeletedMealPlans,
	rateKey,
	reconcileBlockColumns,
	pluralize,
	roomColChanges,
	splitMealPlans,
	THotelIndicators,
	THotelRateErrors,
	TSeasonIndicators,
	TSeasonMeta,
	TSheetSummary,
	TSummaryEntry,
	TSummaryKind,
	syncBlockMealRates,
	toBlockRates,
	toNum,
	TColumnDerivation,
	TRoomCol,
	TLowerCol,
	createSeasonUid,
	buildSeasonValidationSchema,
	roudingOptions,
	TPolicyHeads,
	type TAnyPolicy,
	type THotelPolicyMaps,
	type TPaxType,
	type TPayloadMealRate,
	type TPayloadRate,
	type TRoomConfig,
	applyHotelDefaultPolicies,
	applyHotelPoliciesToSeasons,
	computePolicyIssues,
	policyHeadName,
	policyKindLabel,
	type TPolicyKind,
	DEFAULT_BASE_MEAL_PLAN_ORDER,
	getPayloadData,
	rateSheetNameValidationSchema,
	isPolicyStale,
	latestPolicyFor,
	pickPolicyHead,
	policyHeadId,
	withPolicyHead,
	type TAnyPolicyDetail,
	type TRoomRateWarningGeometry,
	type TBlockSection,
	pickFocusSource,
	MAX_RATE,
	rateExceedsMaxMessage,
	ALL_DAYS,
	newInterval,
} from "./utils"
import { isNumeric } from "@sembark-travel/number-utils"
import {
	cancellationPoliciesXHR,
	PolicyRuleCard,
	TCancellationPolicy,
	type TCancellationPolicyRule,
} from "../CancellationPolicy"
import {
	paymentPoliciesXHR,
	RuleBlock,
	TPaymentPolicy,
	type TPaymentRuleSet,
} from "../PaymentPolicy"
import {
	columnPillVariant,
	GRID_BORDER_COLOR,
	NOT_SELLABLE_BACKGROUND,
	RATE_CELL_BACKGROUND,
	RATE_COL_MIN_WIDTH,
	ROOM_OCCUPANCY_COL_WIDTH,
} from "./gridStyles"
import {
	ColumnHeaderPill,
	MealPlanRowCell,
	MiniCurrency,
	OccupancyBadgeSlot,
	RoomInfoCell,
} from "./RateGridPrimitives"
import { RoomRatesPreviewTable } from "./Item"
import { generatePath, useNavigate } from "../router-utils"
import { useXHR } from "@sembark-travel/xhr"
import { mutate } from "swr"

export interface IRateBlockSchema {
	hotel: number
	rounding_point: TRoundingPointSchema | null
	pricing_type: { id: TPricingType; name: string }
	child_meal_rate_same_as_adult: boolean
	is_lower_occupancy_rate_configured: boolean
	cancellation_policy: TCancellationPolicy | null
	cancellation_policy_is_manual: boolean
	payment_policy: TPaymentPolicy | null
	payment_policy_is_manual: boolean
	meal_plans: {
		ota_meal_plan: TOtaMealPlan
		category?: string
		is_base: boolean
		includes_all_room_types: boolean
		ota_room_types: TOtaRoomType[] | null
	}[]
	meal_rates: TPayloadMealRate[]
	rooms: TRoomConfig[]
	pax_types: TPaxType[]
	// Key for key what the payload sends, except `occupancy`, which is held as the
	// grid's column key ("base", "single", "CNB (6-8)") and converted on the way
	// out by `toPaxOccupancy`.
	rates: Array<Omit<TPayloadRate, "is_manual"> & { is_manual: boolean }>
}

// Internal to the room rates grid. The grid keeps a dense row x column array so
// a cell can be addressed positionally, which means it also holds entries for
// occupancies a room type cannot take (e.g Single for a room whose base
// occupancy is 1). Those carry `enabled: 0` and are dropped by `toBlockRates`
// before the rates leave the grid.
export type TGridRate = IRateBlockSchema["rates"][number] & {
	enabled?: 1 | 0 | boolean
}

export type TSeasonFormInterval = {
	from: Date | null
	to: Date | null
	days: TDay[]
}

export interface ISeasonSchema {
	_uid?: string
	name: string
	intervals: TSeasonFormInterval[]
	rate_blocks: IRateBlockSchema[]
}

function ensureSeasonUids<T extends { _uid?: string }>(seasons?: T[]): T[] {
	if (!seasons) return []
	return seasons.map((s) => ({
		...s,
		_uid: s._uid || createSeasonUid(),
	}))
}

export interface INewRateSheetFormSchema extends Partial<THotelPolicyMaps> {
	name: string
	hotels?: IHotel[]
	seasons: ISeasonSchema[]
}

// Rate errors by season key, then by hotel index within that season.
type TTableErrors = {
	[seasonKey: string]: {
		[hotelIdx: number]: THotelRateErrors
	}
}

type THotelRatesRef = Map<string, IRateBlockSchema>

// Stable empty fallbacks, so an absent baseline does not hand a fresh array to
// a memo'd child on every render
const EMPTY_RATES: IRateBlockSchema["rates"] = []
const EMPTY_MEAL_RATES: IRateBlockSchema["meal_rates"] = []
const EMPTY_PAX_TYPES: IRateBlockSchema["pax_types"] = []
const EMPTY_ROOMS: IRateBlockSchema["rooms"] = []

function assembleSeasons(
	seasonsMeta: Pick<ISeasonSchema, "_uid" | "name" | "intervals">[],
	ratesRef: THotelRatesRef,
	hotels: IHotel[]
): ISeasonSchema[] {
	return seasonsMeta.map((meta, sIdx) => {
		const sKey = meta._uid || sIdx
		return {
			...meta,
			rate_blocks: hotels.map((hotel) => {
				const key = rateKey(sKey, hotel.id)
				return (
					ratesRef.get(key) ||
					ratesRef.get(rateKey(sIdx, hotel.id)) || {
						hotel: Number(hotel.id),
						rounding_point: { id: null, name: "None" },
						pricing_type: { id: "RBP", name: "Room Rate" },
						child_meal_rate_same_as_adult: true,
						is_lower_occupancy_rate_configured: false,
						cancellation_policy: null,
						cancellation_policy_is_manual: false,
						payment_policy: null,
						payment_policy_is_manual: false,
						rates: [],
						meal_plans: [],
						meal_rates: [],
						pax_types: [],
						rooms: [],
					}
				)
			}),
		}
	})
}

// Debounce used to update the hotelRates's ref with hotelcard's form data.
// Updates the ref on flush events, e.g. on blur of any input.
function useDebounce<A extends unknown[]>(
	fn: (...args: A) => void,
	delay: number
) {
	const ref = useRef(fn)
	ref.current = fn
	// The pending timer has to be cancelled on each call: callers use this as an
	// event handler and drop any returned cleanup, so without this a burst of
	// blurs schedules one flush each instead of collapsing into a single one.
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(
		() => () => {
			if (timerRef.current !== null) clearTimeout(timerRef.current)
		},
		[]
	)

	return useCallback(
		(...args: A) => {
			if (timerRef.current !== null) clearTimeout(timerRef.current)
			timerRef.current = setTimeout(() => {
				timerRef.current = null
				ref.current(...args)
			}, delay)
		},
		[delay]
	)
}

type TBatchableForm = {
	batch: (fn: () => void) => void
	pauseValidation: (preventNotification?: boolean) => void
	resumeValidation: () => void
}

// Every `form.change` runs the record-level validator and then republishes the
// state of *all* registered fields. The rate grid registers a field per cell
// (plus its neighbours and change dot), so rewriting the grid one change at a
// time is quadratic in cell count — which is what makes a meal plan toggle
// stutter. Wrapping a burst of changes here collapses it into a single
// validation pass and a single notification sweep.
function bulkChange(form: TBatchableForm, fn: () => void) {
	form.batch(() => {
		// Resumed inside the batch on purpose: that leaves the one validation pass
		// resumeValidation triggers still suppressed, so the single sweep the batch
		// fires on the way out is the one carrying the settled errors.
		form.pauseValidation()
		try {
			fn()
		} finally {
			form.resumeValidation()
		}
	})
}

// Resolves after the next paint. `setTimeout(0)` alone can fire before the
// browser paints, leaving a just-set spinner invisible behind a heavy render.
function waitForPaint() {
	return new Promise<void>((resolve) =>
		requestAnimationFrame(() => setTimeout(resolve, 0))
	)
}

const WEEKDAYS: TDay[] = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
]

const WEEKENDS: TDay[] = ["Saturday", "Sunday"]

// MEAL_TO_MEAL_TYPES is imported from ./utils

export function IntervalDaysSelector({ name }: { name: string }) {
	const { value, onChange } = useFieldValue<TDay[]>(name)
	const currentDays = value || []
	const hasAllDays = currentDays.length === ALL_DAYS.length

	return (
		<IntervalDaysSelectorInner
			key={name}
			name={name}
			currentDays={currentDays}
			hasAllDays={hasAllDays}
			onChangeDays={onChange}
		/>
	)
}

function IntervalDaysSelectorInner({
	name,
	currentDays,
	hasAllDays,
	onChangeDays,
}: {
	name: string
	currentDays: TDay[]
	hasAllDays: boolean
	onChangeDays: (days: TDay[]) => void
}) {
	const [showCheckboxes, setShowCheckboxes] = useState<boolean>(!hasAllDays)

	const uniqueDays = useMemo(() => new Set(currentDays), [currentDays])
	const isWeekdays = useMemo(
		() =>
			uniqueDays.size === WEEKDAYS.length &&
			WEEKDAYS.every((d) => uniqueDays.has(d)),
		[uniqueDays]
	)
	const isWeekends = useMemo(
		() =>
			uniqueDays.size === WEEKENDS.length &&
			WEEKENDS.every((d) => uniqueDays.has(d)),
		[uniqueDays]
	)

	const handleSelectAll = useCallback(() => {
		setShowCheckboxes(false)
		onChangeDays([...ALL_DAYS])
	}, [onChangeDays])

	const handleSelectWeekDays = useCallback(() => {
		setShowCheckboxes(true)
		onChangeDays([...WEEKDAYS])
	}, [onChangeDays])

	const handleSelectWeekends = useCallback(() => {
		setShowCheckboxes(true)
		onChangeDays([...WEEKENDS])
	}, [onChangeDays])

	const handleSpecificDays = useCallback(() => {
		setShowCheckboxes(true)
		if (isWeekdays || isWeekends || currentDays.length === 0) {
			onChangeDays([...ALL_DAYS])
		}
	}, [isWeekdays, isWeekends, currentDays.length, onChangeDays])

	return (
		<Stack gap="2">
			<Inline gap="4" alignItems="center">
				<RadioInputField
					id={`${name}_radio_all`}
					name={`${name}_radio_group`}
					label="All Days"
					checked={!showCheckboxes}
					onClick={handleSelectAll}
				/>
				<RadioInputField
					id={`${name}_radio_weekdays`}
					name={`${name}_radio_group`}
					label="Weekdays"
					checked={showCheckboxes && isWeekdays}
					onClick={handleSelectWeekDays}
				/>
				<RadioInputField
					id={`${name}_radio_weekends`}
					name={`${name}_radio_group`}
					label="Weekends"
					checked={showCheckboxes && isWeekends}
					onClick={handleSelectWeekends}
				/>
				<RadioInputField
					id={`${name}_radio_specific`}
					name={`${name}_radio_group`}
					label="Specific Days"
					checked={showCheckboxes && !isWeekdays && !isWeekends}
					onClick={handleSpecificDays}
				/>
			</Inline>

			{showCheckboxes ? (
				<Inline gap="4" alignItems="center" flexWrap="wrap">
					{ALL_DAYS.map((day) => (
						<CheckboxInputField
							key={day}
							name={name}
							value={day}
							label={day.slice(0, 3)}
							hideErrors
						/>
					))}
				</Inline>
			) : null}

			{currentDays.length === 0 ? (
				<InputValidationErrorMessage>
					<RenderError message="Please select at least one day." />
				</InputValidationErrorMessage>
			) : null}
		</Stack>
	)
}

const SEASON_SUGGESTIONS = ["Peak season", "Low season"]

// Validate the hotel rate tables
function computeTableErrors(
	seasons: ISeasonSchema[],
	hotels: IHotel[]
): TTableErrors {
	const errors: TTableErrors = {}

	for (let sIdx = 0; sIdx < seasons.length; sIdx++) {
		const season = seasons[sIdx]
		const sKey = String(season._uid || sIdx)

		for (let hotelIdx = 0; hotelIdx < hotels.length; hotelIdx++) {
			const hotelErrors = computeBlockErrors(
				season.rate_blocks?.[hotelIdx],
				hotels[hotelIdx]
			)

			if (Object.keys(hotelErrors).length > 0) {
				errors[sKey] = errors[sKey] || {}
				errors[sKey][hotelIdx] = hotelErrors
			}
		}
	}
	return errors
}

// Replaces just one season x hotel entry, so a flush does not have to
// reassemble and revalidate every block in the sheet.
function mergeBlockErrors(
	errors: TTableErrors,
	seasonKey: string | number,
	hotelIdx: number,
	blockErrors: THotelRateErrors
): TTableErrors {
	const hasErrors = Object.keys(blockErrors).length > 0
	const keyStr = String(seasonKey)
	const existing = errors[keyStr]?.[hotelIdx]
	if (!hasErrors && !existing) return errors

	const next: TTableErrors = { ...errors }
	const season = { ...(next[keyStr] || {}) }
	if (hasErrors) {
		season[hotelIdx] = blockErrors
	} else {
		delete season[hotelIdx]
	}
	if (Object.keys(season).length > 0) {
		next[keyStr] = season
	} else {
		delete next[keyStr]
	}
	return next
}

// The policy listings and options, fetched once for the whole wizard.
type TSheetPolicyOptions = ReturnType<typeof useSheetPolicyOptions>

// What a rate block needs to render its policies beyond the values it holds: the
// hotel's policies, which an override falls back to, and the block as it was
// last saved, which a revert puts back.
type TBlockPolicyContext = {
	hotelPolicy: TCancellationPolicy | null
	hotelPaymentPolicy: TPaymentPolicy | null
	baseline: IRateBlockSchema | null
}

// The sheet as it was loaded from the server. Change tracking diffs against
// this, so it has to be captured once and never rewritten.
type TOriginalSheet = THotelPolicyMaps & {
	name?: string
	rates: THotelRatesRef
	seasons: TSeasonMeta[]
	hotels: IHotel[]
}

function buildOriginalSheet(
	initialValues: INewRateSheetFormSchema | undefined
): TOriginalSheet {
	const hotelIds = new Set(
		(initialValues?.hotels || []).map((h) => Number(h.id))
	)
	return {
		name: initialValues?.name,
		rates: buildSheetRatesRef(initialValues?.seasons, hotelIds),
		seasons: (initialValues?.seasons || []).map(
			({ _uid, name, intervals }) => ({
				_uid: _uid || createSeasonUid(),
				name,
				intervals,
			})
		),
		hotels: initialValues?.hotels || [],
		hotelPolicies: initialValues?.hotelPolicies || {},
		hotelPaymentPolicies: initialValues?.hotelPaymentPolicies || {},
	}
}

export function NewRateSheet({
	initialValues,
}: {
	initialValues: INewRateSheetFormSchema | undefined
}) {
	const navigate = useNavigate()
	const xhr = useXHR()

	const handleSubmit = async (
		data: INewRateSheetFormSchema,
		{ disablePrompt }: { disablePrompt: () => void }
	) => {
		try {
			await rateSheetsXHR(xhr).create(getPayloadData(data))
			showSnackbar("Rate sheet created successfully")
			disablePrompt()
			navigate("/org/rate-sheets", { replace: true })
		} catch (err) {
			const error = err as Error
			showSnackbar(error.message)
		}
	}
	return (
		<NewRateSheetForm
			initialValues={initialValues}
			onSubmit={handleSubmit}
			onCancel={() => navigate("/org/rate-sheets")}
		/>
	)
}

export function EditRateSheet({ rateSheetId }: { rateSheetId: string }) {
	const navigate = useNavigate()
	const xhr = useXHR()
	const back = useGetBackUrlFromLocation()

	const sheetKey = `/rate-sheet-heads/${rateSheetId}`
	const { sheet: initialValues, isValidating } =
		useRateSheetFormData(rateSheetId)

	const navigateToDetail = () =>
		navigate(
			(generatePath("/rate-sheets/:rateSheetId", {
				rateSheetId: String(rateSheetId),
			}) + (back ? queryToSearch({ back }) : "")) as never as "/",
			{ replace: true }
		)

	const handleSubmit = async (
		values: INewRateSheetFormSchema,
		{ disablePrompt }: { disablePrompt: () => void }
	) => {
		try {
			const updated = await rateSheetsXHR(xhr).update(
				Number(rateSheetId),
				getPayloadData(values)
			)
			await mutate(sheetKey)
			showSnackbar("Rate sheet saved successfully")
			disablePrompt()
			// `back` is a url read from the location, so it can't be a typed path
			navigate((back || "/org/rate-sheets") as never as "/", {
				replace: true,
				state: updated.applies_to_all_trip_sources
					? undefined
					: { shareRateSheetId: updated.id },
			})
		} catch (err) {
			const error = err as Error
			showSnackbar(error.message)
		}
	}
	return (
		<>
			{isValidating && !initialValues ? (
				<Box padding="6" textAlign="center">
					<Spinner />
				</Box>
			) : initialValues ? (
				<NewRateSheetForm
					initialValues={initialValues}
					onSubmit={handleSubmit}
					onCancel={navigateToDetail}
					isEdit
				/>
			) : (
				<Box padding="6" textAlign="center">
					<Text color="danger">Failed to load the rate sheet.</Text>
				</Box>
			)}
		</>
	)
}

// Parent form which consist the steps forms
function NewRateSheetForm({
	onSubmit,
	onCancel,
	isEdit = false,
	initialValues: propInitialValues,
}: {
	// `disablePrompt` disarms the unsaved-changes blocker. A save redirects, and
	// the redirect would otherwise be caught by the blocker — so call it right
	// before navigating, once the save has actually gone through.
	onSubmit: (
		values: INewRateSheetFormSchema,
		helpers: { disablePrompt: () => void }
	) => Promise<void>
	onCancel: () => void
	isEdit?: boolean
	initialValues?: INewRateSheetFormSchema
}) {
	const [step, changeStep] = useState(1)
	const { blocker, disablePrompt } = useNavigationPrompt(true)

	// Seeded once per mount. The prop can come from router location state, which
	// is dropped by any stray navigation (e.g. a `href="#"` click); recomputing
	// from it would make the form reinitialise and wipe the whole wizard.
	const [initialValues] = useState<INewRateSheetFormSchema>(() => {
		if (propInitialValues) {
			return {
				...propInitialValues,
				// Only the live values: the baseline is built from the saved sheet,
				// so a cleared BASE price still reads as a change against it
				seasons: isEdit
					? resetChangedBaseOccupancyRates(
							ensureSeasonUids(propInitialValues.seasons),
							propInitialValues.hotels || []
						)
					: ensureSeasonUids(propInitialValues.seasons),
			}
		}
		return {
			name: "",
			hotels: undefined,
			hotelPolicies: undefined,
			hotelPaymentPolicies: undefined,
			seasons: [],
		}
	})

	const handleSubmit = useCallback(
		(values: INewRateSheetFormSchema) => onSubmit(values, { disablePrompt }),
		[onSubmit, disablePrompt]
	)

	// Captured here rather than inside Step 2: walking back to step 1 and forward
	// again remounts Step 2, and by then the parent form holds the *edited*
	// seasons — seeding the baseline from those would silently erase every change.
	const originalRef = useRef<TOriginalSheet | null>(null)
	if (originalRef.current === null) {
		originalRef.current = buildOriginalSheet(propInitialValues)
	}

	return (
		<Form<INewRateSheetFormSchema>
			initialValues={initialValues}
			mutators={{ ...arrayMutators }}
			onSubmit={handleSubmit}
		>
			{({ form }) => {
				const handleBack = () => {
					changeStep((s) => s - 1)
				}

				return (
					<Box minWidth="0">
						<NavigationPromptDialog
							blocker={blocker}
							title="You have unsaved changes in the sheet. Are you sure you want to leave? All changes will be lost."
						/>
						{step === 1 ? (
							<Step1
								initialValues={form.getState().values}
								onNext={(value) => {
									form.change("hotels", value.hotels as IHotel[])
									form.change("hotelPolicies", value.hotelPolicies)
									form.change(
										"hotelPaymentPolicies",
										value.hotelPaymentPolicies
									)
									// Blocks hold their own copy of the policies so they can be
									// read without the hotel in hand, which leaves every block
									// that did not override them behind a change made here.
									form.change(
										"seasons",
										applyHotelPoliciesToSeasons(
											form.getState().values.seasons,
											value.hotelPolicies,
											value.hotelPaymentPolicies
										) as never
									)
									changeStep(2)
								}}
								onCancel={() => {
									disablePrompt()
									onCancel()
								}}
								onNameChange={(value) => form.change("name", value)}
							/>
						) : step === 2 ? (
							<Step2
								parentValues={form.getState().values}
								original={originalRef.current as TOriginalSheet}
								isEdit={isEdit}
								onNext={(values) => {
									if (values.length > 0) {
										form.change("seasons", values)
										changeStep((prev) => prev + 1)
									} else if (values.length === 0) {
										showSnackbar("Please add atleast one season")
									}
								}}
								onBack={(values) => {
									form.change("seasons", values)
									changeStep((prev) => prev - 1)
								}}
								onNameChange={(name) => form.change("name", name)}
							/>
						) : (
							<ConnectedRateSheetPreview
								onBack={handleBack}
								onSubmit={form.submit}
								submitting={form.getState().submitting}
								isEdit={isEdit}
								original={originalRef.current as TOriginalSheet}
							/>
						)}
					</Box>
				)
			}}
		</Form>
	)
}

function StepHeader({
	onBack,
	backTitle,
	currentStepNumber,
	currentTitle,
	statusBar,
	actionButton,
}: {
	onBack: () => void
	backTitle: React.ReactNode
	currentStepNumber: number
	currentTitle: React.ReactNode
	statusBar?: React.ReactNode
	actionButton: React.ReactNode
}) {
	const [isBackHovered, setIsBackHovered] = useState(false)
	const isMobile = useIsMobile()
	return (
		<Inline
			gap={isMobile ? "2" : "4"}
			justifyContent="between"
			alignItems={isMobile ? "end" : "center"}
			bgColor="inset"
			paddingY="3"
			paddingX={isMobile ? "3" : "6"}
			borderBottomWidth="1"
			flexWrap="wrap"
		>
			<Inline
				gap={isMobile ? "2" : "4"}
				alignItems={isMobile ? "start" : "center"}
				flexDirection={isMobile ? "col" : "row"}
				flex="1"
				minWidth="0"
			>
				<Inline
					gap={isMobile ? "1" : "3"}
					alignItems="center"
					onClick={onBack}
					cursor="pointer"
					color={isBackHovered ? "primary" : "muted"}
					onMouseEnter={() => setIsBackHovered(true)}
					onMouseLeave={() => setIsBackHovered(false)}
					flexShrink="0"
				>
					<Box display="flex" alignItems="center">
						<Icons.ArrowLeft color={isBackHovered ? "primary" : "muted"} />
					</Box>
					{backTitle}
				</Inline>
				{isMobile ? null : (
					<Box height="6" borderLeftWidth="1" borderColor="emphasis" />
				)}
				<Inline gap={isMobile ? "2" : "3"} alignItems="center" minWidth="0">
					<Inline
						size="6"
						alignItems="center"
						justifyContent="center"
						borderWidth="1"
						rounded="full"
						color="primary"
						flexShrink="0"
						style={{ borderColor: "#3A5BC7" }}
					>
						<Text fontWeight="semibold" color="primary">
							{currentStepNumber}
						</Text>
					</Inline>
					<Stack gap="0" minWidth="0">
						{currentTitle}
					</Stack>
				</Inline>
			</Inline>
			<Inline
				gap={isMobile ? "3" : "6"}
				alignItems="center"
				flexShrink="0"
				flexWrap="wrap"
			>
				{statusBar}
				{actionButton}
			</Inline>
		</Inline>
	)
}

function everyHotelHasPolicy(hotels: IHotel[], value: unknown): boolean {
	if (hotels.length === 0) return true
	if (!value || typeof value !== "object") return false
	const policies = value as Record<number, unknown>
	return hotels.every((hotel) => policies[hotel.id] != null)
}

const step1ValidationSchema = Validator.object().shape({
	hotels: Validator.array()
		.min(1, "Select at least one hotel")
		.required("Select at least one hotel"),
	hotelPolicies: Validator.mixed().test(
		"all-hotels-have-policy",
		"Each hotel must have a cancellation policy",
		function (val) {
			return everyHotelHasPolicy(
				(this.parent.hotels as IHotel[] | undefined) || [],
				val
			)
		}
	),
	hotelPaymentPolicies: Validator.mixed().test(
		"all-hotels-have-payment-policy",
		"Each hotel must have a payment policy",
		function (val) {
			return everyHotelHasPolicy(
				(this.parent.hotels as IHotel[] | undefined) || [],
				val
			)
		}
	),
})

type TStep1FormValues = THotelPolicyMaps & {
	hotels: IHotel[]
}

function Step1({
	initialValues,
	onNext,
	onCancel,
	onNameChange,
}: {
	initialValues: INewRateSheetFormSchema
	onNext: (values: Partial<TStep1FormValues>) => void
	onCancel: () => void
	onNameChange: (value: string) => void
}) {
	const { policyHeads, cancellationOptions, paymentOptions } =
		useSheetPolicyOptions()

	return (
		<Form<TStep1FormValues>
			initialValues={{
				hotels: initialValues.hotels || [],
				hotelPolicies: initialValues.hotelPolicies || {},
				hotelPaymentPolicies: initialValues.hotelPaymentPolicies || {},
			}}
			validate={validateFormValues(step1ValidationSchema)}
			onSubmit={withServerErrors(async (values) => {
				// Step 2 mounts synchronously and can take a while on a large sheet,
				// so let the spinner paint before handing over
				await waitForPaint()
				onNext(values)
			})}
		>
			{({ handleSubmit, form, submitting }) => {
				const { hotels, hotelPolicies, hotelPaymentPolicies } =
					form.getState().values
				// A policy that is missing, or pinned to a version its head has moved
				// past, is what the header counts and what blocks the step
				const policyIssues = computePolicyIssues({
					hotels,
					hotelPolicies,
					hotelPaymentPolicies,
					policyHeads,
				})
				const nextTooltipContent = !hotels.length
					? "Please add at least one hotel"
					: policyIssues.length
						? "Please fix the policy errors first"
						: undefined
				return (
					<form noValidate onSubmit={handleSubmit}>
						<StepHeader
							onBack={onCancel}
							backTitle={
								<Text fontSize="sm" color="muted" fontWeight="semibold">
									Rate Sheet Listing
								</Text>
							}
							currentStepNumber={1}
							currentTitle={
								<Text color="primary" fontSize="md" fontWeight="semibold">
									Hotels & Policies{" "}
									<EditRateSheetName
										initialName={initialValues.name}
										onSuccess={onNameChange}
									/>
								</Text>
							}
							statusBar={<PolicyIssuesBadge issues={policyIssues} />}
							actionButton={
								<Tooltip
									content={nextTooltipContent}
									disabled={!nextTooltipContent}
								>
									<Button
										level="primary"
										onClick={() => {
											if (submitting) return
											if (policyIssues.length) {
												showSnackbar(policyIssues[0].message)
												return
											}
											handleSubmit()
										}}
										disabled={!hotels.length || submitting}
										style={{
											opacity: !hotels.length || submitting ? 0.5 : 1,
										}}
									>
										{submitting ? <Icons.Spinner /> : null}
										Next <Icons.ChevronDown rotate="270" />
									</Button>
								</Tooltip>
							}
						/>
						<Step1HotelLayout
							policyHeads={policyHeads}
							cancellationOptions={cancellationOptions}
							paymentOptions={paymentOptions}
							policyIssues={policyIssues}
						/>
					</form>
				)
			}}
		</Form>
	)
}

// The header's error count, opening onto the list of what is wrong. Mirrors the
// error pill `SheetStatusBar` renders in step 2.
function PolicyIssuesBadge({ issues }: { issues: TSummaryEntry[] }) {
	const [isOpen, setIsOpen] = useState(false)
	if (!issues.length) return null
	return (
		<>
			<Inline
				gap="1"
				alignItems="center"
				color="danger"
				cursor="pointer"
				onClick={() => setIsOpen(true)}
			>
				<Icons.CircleAlert size="4" />
				<Text fontSize="sm" fontWeight="semibold">
					{pluralize(issues.length, "Error")}
				</Text>
			</Inline>
			<Dialog open={isOpen} onClose={() => setIsOpen(false)} sm title="Errors">
				<DialogBody>
					<Stack gap="3">
						{issues.map((issue, idx) => (
							<Inline key={idx} gap="2" alignItems="center">
								<Icons.CircleAlert size="4" color="danger" />
								<Text fontSize="sm" fontWeight="semibold">
									{issue.label}
								</Text>
								<Text fontSize="sm" color="muted">
									{issue.message}
								</Text>
							</Inline>
						))}
					</Stack>
				</DialogBody>
			</Dialog>
		</>
	)
}

function Step1HotelLayout({
	policyHeads,
	cancellationOptions,
	paymentOptions,
	policyIssues,
}: Pick<TSheetPolicyOptions, "cancellationOptions" | "paymentOptions"> & {
	policyHeads: TPolicyHeads
	policyIssues: TSummaryEntry[]
}) {
	const isMobile = useIsMobile()
	const { value: hotels = [], onChange: setHotels } =
		useFieldValue<IHotel[]>("hotels")
	const { value: hotelPolicies = {}, onChange: setPolicies } =
		useFieldValue<THotelPolicyMaps["hotelPolicies"]>("hotelPolicies")
	const { value: hotelPaymentPolicies = {}, onChange: setPaymentPolicies } =
		useFieldValue<THotelPolicyMaps["hotelPaymentPolicies"]>(
			"hotelPaymentPolicies"
		)
	const [activeHotelIndex, setActiveHotelIndex] = useState(0)
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)

	const activeHotel = hotels[activeHotelIndex] || null

	// Which hotels the sidebar marks, and what its tooltip says
	const issuesByHotelId = useMemo(() => {
		const byHotel = new Map<number, string[]>()
		policyIssues.forEach((issue) => {
			const hotelId = Number(issue.hotelId)
			if (!hotelId) return
			byHotel.set(hotelId, [...(byHotel.get(hotelId) || []), issue.message])
		})
		return byHotel
	}, [policyIssues])

	function updateHotels(newHotels: IHotel[]) {
		const {
			hotelPolicies: nextPolicies,
			hotelPaymentPolicies: nextPaymentPolicies,
		} = applyHotelDefaultPolicies(
			newHotels,
			hotelPolicies,
			hotelPaymentPolicies,
			policyHeads
		)
		if (nextPolicies !== hotelPolicies) setPolicies(nextPolicies)
		if (nextPaymentPolicies !== hotelPaymentPolicies)
			setPaymentPolicies(nextPaymentPolicies)
		setHotels(newHotels)
		setActiveHotelIndex(0)
		setIsAddDialogOpen(false)
	}

	function handlePolicyChange(
		hotelId: number,
		policy: TCancellationPolicy | null
	) {
		setPolicies({ ...hotelPolicies, [hotelId]: policy })
	}

	function handlePaymentPolicyChange(
		hotelId: number,
		policy: TPaymentPolicy | null
	) {
		setPaymentPolicies({ ...hotelPaymentPolicies, [hotelId]: policy })
	}

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return
			if (!hotels.length) return
			const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
			if (tag === "input" || tag === "textarea" || tag === "select") return
			e.preventDefault()
			setActiveHotelIndex((currentIdx) => {
				if (e.key === "ArrowDown") return (currentIdx + 1) % hotels.length
				return (currentIdx - 1 + hotels.length) % hotels.length
			})
		}
		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [hotels])

	return (
		<>
			<Stack>
				<Tabs
					layout="row"
					collapseBelow="sm"
					alignItems="stretch"
					style={{ minHeight: isMobile ? undefined : "calc(100vh - 12rem)" }}
				>
					{isMobile ? (
						<Inline
							paddingLeft={isMobile ? "3" : "6"}
							padding="3"
							alignItems="center"
							justifyContent="between"
							bgColor="white"
							borderBottomWidth="1"
						>
							<Text fontWeight="semibold" color="muted">
								Hotels ({hotels.length})
							</Text>
							{hotels.length > 0 ? (
								<Button
									type="button"
									color="primary"
									level="tertiary"
									size="sm"
									onClick={() => setIsAddDialogOpen(true)}
								>
									<Icons.Plus /> Edit
								</Button>
							) : null}
						</Inline>
					) : null}
					<TabsList
						position={isMobile ? undefined : "sticky"}
						style={{
							minWidth: isMobile ? undefined : "12rem",
							maxWidth: isMobile ? undefined : "16rem",
							padding: 0,
							rowGap: 0,
						}}
						flexShrink="0"
						minHeight={isMobile ? undefined : "full"}
					>
						{isMobile ? null : (
							<Inline
								paddingLeft="6"
								padding="3"
								alignItems="center"
								justifyContent="between"
							>
								<Text fontWeight="semibold" color="muted">
									Hotels ({hotels.length})
								</Text>
								{hotels.length > 0 ? (
									<Button
										type="button"
										color="primary"
										level="tertiary"
										size="sm"
										onClick={() => setIsAddDialogOpen(true)}
									>
										<Icons.Plus /> Edit
									</Button>
								) : null}
							</Inline>
						)}
						{hotels.map((hotel, idx) => {
							const hotelIssues = issuesByHotelId.get(Number(hotel.id))
							const active = activeHotelIndex === idx

							return (
								<TabItem
									key={hotel.id}
									active={active}
									onClick={() => setActiveHotelIndex(idx)}
									style={{ padding: 0 }}
									bgColor={active ? "primary" : undefined}
								>
									<Inline
										paddingY="1"
										alignItems="center"
										justifyContent="between"
										gap="2"
									>
										<Inline gap="1">
											<Text
												fontWeight="semibold"
												textOverflow="truncate"
												style={{ maxWidth: "8rem" }}
												title={hotel.name}
											>
												{hotel.name}
											</Text>
											{hotel.currency ? (
												<MiniCurrency currency={hotel.currency} />
											) : null}
										</Inline>
										{hotelIssues?.length ? (
											<Tooltip
												content={
													<Stack gap="1">
														{hotelIssues.map((message) => (
															<Inline key={message} gap="2">
																<Icons.CircleAlert color="danger" />
																{message}
															</Inline>
														))}
													</Stack>
												}
											>
												<Box>
													<Icons.CircleAlert color="danger" size="4" />
												</Box>
											</Tooltip>
										) : null}
									</Inline>
								</TabItem>
							)
						})}
						{hotels.length === 0 && !isMobile ? (
							<Inline justifyContent="center">
								<Box
									paddingX="6"
									paddingY="2"
									borderWidth="2"
									rounded="md"
									style={{ borderStyle: "dashed" }}
								>
									<Text fontSize="sm" color="subtle">
										No Hotels added
									</Text>
								</Box>
							</Inline>
						) : null}
					</TabsList>
					{hotels.length === 0 ? (
						<Stack
							justifyContent="center"
							alignItems="center"
							height="full"
							width="full"
							style={{
								height: "calc(100vh - 11.9rem)",
							}}
						>
							<Stack
								alignItems="center"
								justifyContent="center"
								gap="4"
								paddingY="16"
							>
								<Box>
									<Icons.Bed size="32" color="subtle" />
								</Box>
								<Text color="primary" fontWeight="semibold" fontSize="md">
									Add Hotels and Assign Policies
								</Text>
								<Button
									level="primary"
									type="button"
									onClick={() => setIsAddDialogOpen(true)}
								>
									<Icons.Plus /> Add Hotels
								</Button>
							</Stack>
						</Stack>
					) : (
						<TabContent
							active={true}
							padding={isMobile ? "3" : "6"}
							flex="1"
							overflow="auto"
							style={{
								minHeight: isMobile
									? "calc(100vh - 16rem)"
									: "calc(100vh - 11.9rem)",
							}}
						>
							<Stack gap="6">
								{activeHotel ? (
									<Step1HotelPolicyPanel
										hotel={activeHotel}
										policy={hotelPolicies[Number(activeHotel.id)] || null}
										paymentPolicy={
											hotelPaymentPolicies[Number(activeHotel.id)] || null
										}
										onPolicyChange={(policy) =>
											handlePolicyChange(Number(activeHotel.id), policy)
										}
										onPaymentPolicyChange={(policy) =>
											handlePaymentPolicyChange(Number(activeHotel.id), policy)
										}
										policyHeads={policyHeads}
										cancellationOptions={cancellationOptions}
										paymentOptions={paymentOptions}
									/>
								) : (
									<Text color="muted">
										Select a hotel to configure policies.
									</Text>
								)}
							</Stack>
						</TabContent>
					)}
				</Tabs>
			</Stack>
			<AddHotelsDialog
				isOpen={isAddDialogOpen}
				onClose={() => setIsAddDialogOpen(false)}
				onHotelChange={updateHotels}
				existingHotels={hotels}
			/>
		</>
	)
}

function Step1HotelPolicyPanel({
	hotel,
	policy,
	paymentPolicy,
	onPolicyChange,
	onPaymentPolicyChange,
	policyHeads,
	cancellationOptions,
	paymentOptions,
}: {
	hotel: IHotel
	policy: TCancellationPolicy | null
	paymentPolicy: TPaymentPolicy | null
	onPolicyChange: (policy: TCancellationPolicy | null) => void
	onPaymentPolicyChange: (policy: TPaymentPolicy | null) => void
	policyHeads: TPolicyHeads
	cancellationOptions: { id: number; name: string }[]
	paymentOptions: { id: number; name: string }[]
}) {
	return (
		<Stack gap="6">
			<Heading as="h4" color="primary">
				Policies
			</Heading>
			<Stack gap="4">
				<Inline gap="4" alignItems="end" flexWrap="wrap">
					<Box style={{ minWidth: "20rem" }}>
						<PolicySelect
							kind="cancellation"
							selectKey={hotel.id}
							policy={policy}
							options={cancellationOptions}
							heads={policyHeads.cancellation}
							onChange={(next) =>
								onPolicyChange((next as TCancellationPolicy) || null)
							}
						/>
					</Box>
					<Box paddingBottom="3">
						<UpdateToLatestLink
							policy={policy}
							heads={policyHeads.cancellation}
							onUpdate={(latest) =>
								onPolicyChange(latest as TCancellationPolicy)
							}
						/>
					</Box>
				</Inline>
				<Inline gap="4" alignItems="end" flexWrap="wrap">
					<Box style={{ minWidth: "20rem" }}>
						<PolicySelect
							kind="payment"
							selectKey={hotel.id}
							policy={paymentPolicy}
							options={paymentOptions}
							heads={policyHeads.payment}
							onChange={(next) =>
								onPaymentPolicyChange((next as TPaymentPolicy) || null)
							}
						/>
					</Box>
					<Box paddingBottom="3">
						<UpdateToLatestLink
							policy={paymentPolicy}
							heads={policyHeads.payment}
							onUpdate={(latest) =>
								onPaymentPolicyChange(latest as TPaymentPolicy)
							}
						/>
					</Box>
				</Inline>
			</Stack>
			{policy ? (
				<>
					<Divider />
					<PolicyDetailPreview kind="cancellation" policy={policy} />
				</>
			) : null}
			{paymentPolicy ? (
				<>
					<Divider />
					<PolicyDetailPreview kind="payment" policy={paymentPolicy} />
				</>
			) : null}
		</Stack>
	)
}

// A policy as it reads below the select: who made it and when, then its rules.
function PolicyDetailPreview({
	kind,
	policy,
}: {
	kind: TPolicyKind
	policy: TAnyPolicy
}) {
	return (
		<Stack gap="4">
			<Stack gap="2">
				<Inline gap="2" alignItems="center">
					<Heading as="h5" color="primary">
						{policyKindLabel(kind)}
					</Heading>
					<Text color="muted" fontSize="sm">
						({policyHeadName(policy) || "—"})
					</Text>
					<VersionBadge version={policy.version} />
				</Inline>
				<Inline gap="6">
					<Stack gap="0">
						<Text fontSize="sm" color="muted">
							Created on
						</Text>
						<Time timestamp={policy.created_at} dateFormat />
					</Stack>
					{policy.created_by ? (
						<Stack gap="0">
							<Text fontSize="sm" color="muted">
								Created by
							</Text>
							<Text>{policy.created_by.name}</Text>
						</Stack>
					) : null}
				</Inline>
			</Stack>
			<PolicyRulesPreview kind={kind} policy={policy} />
		</Stack>
	)
}

function AddHotelsDialog({
	isOpen,
	onClose,
	onHotelChange,
	existingHotels,
}: {
	isOpen: boolean
	onClose: () => void
	onHotelChange: (hotels: IHotel[]) => void
	existingHotels: IHotel[]
}) {
	const [selectedHotels, setSelectedHotels] = useState<IHotel[]>([])

	useEffect(() => {
		if (isOpen) {
			setSelectedHotels(existingHotels)
		}
	}, [isOpen, existingHotels])

	if (!isOpen) return null

	return (
		<Dialog open={isOpen} onClose={onClose} centered sm title="Add Hotels">
			<DialogBody>
				<Stack gap="4">
					<SelectHotels
						label="Hotels"
						value={selectedHotels}
						onChange={(hotels) => setSelectedHotels(hotels as IHotel[])}
						multiple
						fetchOnMount
					/>
				</Stack>
			</DialogBody>
			<DialogFooter>
				<Inline gap="4" justifyContent="end" alignItems="center">
					{selectedHotels.length > 0 ? (
						<Text color="primary" fontWeight="semibold">
							{selectedHotels.length} hotel
							{selectedHotels.length !== 1 ? "s" : ""} selected
						</Text>
					) : null}
					<Button
						level="primary"
						type="button"
						onClick={() => onHotelChange(selectedHotels)}
						disabled={selectedHotels.length === 0}
						style={{ opacity: selectedHotels.length === 0 ? 0.5 : 1 }}
					>
						Add
					</Button>
				</Inline>
			</DialogFooter>
		</Dialog>
	)
}

function Step2({
	parentValues,
	original,
	isEdit,
	onNext,
	onBack,
	onNameChange,
}: {
	parentValues: INewRateSheetFormSchema
	original: TOriginalSheet
	isEdit: boolean
	onNext: (value: ISeasonSchema[]) => void
	onBack: (value: ISeasonSchema[]) => void
	onNameChange: (name: string) => void
}) {
	const policyOptions = useSheetPolicyOptions()
	const sheetName = parentValues.name
	const hotels = useMemo(() => parentValues.hotels || [], [parentValues.hotels])
	// Memoised so the sheet summary is not rebuilt on every render of this step
	const hotelPolicies = useMemo(
		() => parentValues.hotelPolicies || {},
		[parentValues.hotelPolicies]
	)
	const hotelPaymentPolicies = useMemo(
		() => parentValues.hotelPaymentPolicies || {},
		[parentValues.hotelPaymentPolicies]
	)

	const hotelRatesRef = useRef<THotelRatesRef>(new Map())
	// The immutable baseline lives in the parent form, so a step 1 round trip
	// cannot reseed it from already edited values
	const originalHotelRatesRef = original.rates
	const isInitialized = useRef(false)
	if (!isInitialized.current) {
		const hotelById = new Map(
			(parentValues.hotels || []).map((h) => [Number(h.id), h])
		)
		parentValues.seasons?.forEach((season, sIdx) => {
			const sKey = season._uid || sIdx
			season.rate_blocks?.forEach((rate) => {
				if (rate.hotel && hotelById.has(rate.hotel)) {
					hotelRatesRef.current.set(rateKey(sKey, rate.hotel), rate)
					hotelRatesRef.current.set(rateKey(sIdx, rate.hotel), rate)
				}
			})
		})
		isInitialized.current = true
	}

	const cardFlushRegistry = useRef<Map<string, () => void>>(new Map())

	const registerCardFlush = useCallback((key: string, flushFn: () => void) => {
		cardFlushRegistry.current.set(key, flushFn)
		return () => cardFlushRegistry.current.delete(key)
	}, [])

	const [tableErrors, setTableErrors] = useState<TTableErrors>({})
	// Blocks live in a ref, so nothing re-renders when one flushes. This counter
	// is what tells the summary that the ref moved.
	const [flushTick, setFlushTick] = useState(0)

	// Only the block that flushed is revalidated — reassembling and rechecking
	// every season x hotel on each 400ms flush is O(seasons x hotels x rates).
	const flushCard = useCallback(
		(seasonKey: string | number, hotelId: number, rates: IRateBlockSchema) => {
			hotelRatesRef.current.set(rateKey(seasonKey, hotelId), rates)
			setFlushTick((tick) => tick + 1)

			const hotelIdx = hotels.findIndex((h) => Number(h.id) === hotelId)
			if (hotelIdx === -1) return
			const blockErrors = computeBlockErrors(rates, hotels[hotelIdx])
			setTableErrors((prev) =>
				mergeBlockErrors(prev, seasonKey, hotelIdx, blockErrors)
			)
		},
		[hotels]
	)

	// Blocks re-cut to their hotel's current columns without ever being mounted.
	// One tick for the whole pass rather than one per block, and no revalidation:
	// errors surface once a block is touched, or on Next.
	const reconcileCards = useCallback(
		(
			updates: {
				seasonKey: string | number
				hotelId: number
				block: IRateBlockSchema
			}[]
		) => {
			updates.forEach(({ seasonKey, hotelId, block }) =>
				hotelRatesRef.current.set(rateKey(seasonKey, hotelId), block)
			)
			setFlushTick((tick) => tick + 1)
		},
		[]
	)

	const imperativeFlushAll = useCallback(() => {
		cardFlushRegistry.current.forEach((fn) => fn())
	}, [])

	const seasonsMeta = useMemo(
		() =>
			(parentValues.seasons || []).map(({ _uid, name, intervals }) => ({
				_uid: _uid || createSeasonUid(),
				name,
				intervals,
			})),
		[parentValues.seasons]
	)

	return (
		<Form<{
			seasons: Pick<ISeasonSchema, "_uid" | "name" | "intervals">[]
		}>
			initialValues={{
				seasons: seasonsMeta,
			}}
			validate={validateFormValues(
				Validator.object().shape({
					seasons: Validator.array()
						.min(1, "Please add at least one season")
						.required("Please add at least one season"),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				if (values.seasons.length === 0) {
					showSnackbar("Please add at least one season")
					return
				}
				imperativeFlushAll()
				const fullSeasons = assembleSeasons(
					values.seasons,
					hotelRatesRef.current,
					hotels
				)

				const errors = computeTableErrors(fullSeasons, hotels)
				const errorVals = Object.values(errors)

				if (errorVals.length > 0) {
					setTableErrors(errors)
					return
				}

				if (!hotels.length) return
				setTableErrors({})
				onNext(fullSeasons)
			})}
			subscription={{ submitting: true, errors: true, submitError: true }}
		>
			{({ handleSubmit, form }) => {
				const handleBack = () => {
					imperativeFlushAll()
					const fullSeasons = assembleSeasons(
						form.getState().values.seasons,
						hotelRatesRef.current,
						hotels
					)
					onBack(fullSeasons)
				}

				return (
					<form noValidate onSubmit={handleSubmit}>
						<Step2Content
							sheetName={sheetName}
							hotels={hotels}
							hotelPolicies={hotelPolicies}
							hotelPaymentPolicies={hotelPaymentPolicies}
							original={original}
							isEdit={isEdit}
							flushTick={flushTick}
							policyOptions={policyOptions}
							tableErrors={tableErrors}
							setTableErrors={setTableErrors}
							hotelRatesRef={hotelRatesRef.current}
							originalHotelRatesRef={originalHotelRatesRef}
							flushCard={flushCard}
							reconcileCards={reconcileCards}
							registerCardFlush={registerCardFlush}
							imperativeFlushAll={imperativeFlushAll}
							onBack={handleBack}
							onNameChange={onNameChange}
							onNext={() => {
								if (form.getState().values.seasons.length === 0) {
									showSnackbar("Please add at least one season")
									return
								}
								handleSubmit()
							}}
						/>
					</form>
				)
			}}
		</Form>
	)
}

// Lives inside the Step 2 form so it can read the live seasons — `parentValues`
// still holds the seasons as they were when step 2 was entered, which would make
// the header counts lag behind every season edit.
function Step2Content({
	sheetName,
	hotels,
	hotelPolicies,
	hotelPaymentPolicies,
	original,
	isEdit,
	flushTick,
	policyOptions,
	tableErrors,
	setTableErrors,
	hotelRatesRef,
	originalHotelRatesRef,
	flushCard,
	reconcileCards,
	registerCardFlush,
	imperativeFlushAll,
	onBack,
	onNameChange,
	onNext,
}: THotelPolicyMaps & {
	sheetName: string
	hotels: IHotel[]
	original: TOriginalSheet
	isEdit: boolean
	flushTick: number
	policyOptions: TSheetPolicyOptions
	tableErrors: TTableErrors
	setTableErrors: React.Dispatch<React.SetStateAction<TTableErrors>>
	hotelRatesRef: THotelRatesRef
	originalHotelRatesRef: THotelRatesRef
	flushCard: (
		seasonKey: string | number,
		hotelId: number,
		rates: IRateBlockSchema
	) => void
	reconcileCards: (
		updates: {
			seasonKey: string | number
			hotelId: number
			block: IRateBlockSchema
		}[]
	) => void
	registerCardFlush: (key: string, flushFn: () => void) => () => void
	imperativeFlushAll: () => void
	onBack: () => void
	onNameChange: (name: string) => void
	onNext: () => void
}) {
	const { value: seasons = [] } =
		useFieldValue<Pick<ISeasonSchema, "_uid" | "name" | "intervals">[]>(
			"seasons"
		)

	const [activeHotelId, setActiveHotelId] = useState<number>(() =>
		hotels.length ? Number(hotels[0].id) : 0
	)
	const [activeSeason, setActiveSeason] = useState(0)

	// The rate grid's geometry is built from each hotel's full configuration,
	// which neither step 1 record carries. Fetched for every hotel up front rather
	// than as each one is visited, so a hotel the user has not opened yet is not
	// measured against a partial record — and shares its cache with step 3.
	const hotelDetailsById = useSheetHotelDetails(hotels)

	// A block the user never opens is never mounted, so nothing else would ever
	// bring it up to the columns its hotel prices today, or drop the meal plans
	// the hotel has since deleted. Waits for every hotel's detail: against a list
	// record a block would look re-cut when it is not.
	useEffect(() => {
		if (hotels.some((hotel) => !hotelDetailsById.has(Number(hotel.id)))) return
		const updates: Parameters<typeof reconcileCards>[0] = []
		hotels.forEach((hotel) => {
			const detail = hotelDetailsById.get(Number(hotel.id))
			if (!detail) return
			seasons.forEach((season, seasonIdx) => {
				const seasonKey = season._uid || seasonIdx
				const block =
					hotelRatesRef.get(rateKey(seasonKey, hotel.id)) ||
					hotelRatesRef.get(rateKey(seasonIdx, hotel.id))
				if (!block) return
				// Pruned first so the columns are re-cut for the plans that remain
				const reconciled = reconcileBlockColumns(
					pruneDeletedMealPlans(block, detail),
					detail
				)
				if (reconciled !== block) {
					updates.push({
						seasonKey,
						hotelId: Number(hotel.id),
						block: reconciled,
					})
				}
			})
		})
		if (updates.length) reconcileCards(updates)
	}, [hotels, hotelDetailsById, seasons, hotelRatesRef, reconcileCards])

	// Recomputed for the whole sheet rather than merged per block: the counts
	// have to include blocks the user never opened, which never flush, so the
	// cross product has to be walked anyway. `flushTick` is what makes an edit
	// buried in a ref visible here.
	const sheetSummary = useMemo(() => {
		// The rates are not reactive — they live in refs. Reading this is what
		// re-runs the memo once a block has flushed.
		void flushTick
		return computeSheetSummary({
			name: sheetName,
			originalName: original.name,
			hotels,
			hotelDetailsById,
			seasons,
			hotelPolicies,
			hotelPaymentPolicies,
			policyHeads: policyOptions.policyHeads,
			ratesRef: hotelRatesRef,
			originalRatesRef: originalHotelRatesRef,
			originalSeasons: original.seasons,
			originalHotels: original.hotels,
			originalHotelPolicies: original.hotelPolicies,
			originalHotelPaymentPolicies: original.hotelPaymentPolicies,
			isEdit,
		})
	}, [
		sheetName,
		hotels,
		hotelDetailsById,
		seasons,
		hotelPolicies,
		hotelPaymentPolicies,
		policyOptions,
		hotelRatesRef,
		originalHotelRatesRef,
		original,
		isEdit,
		flushTick,
	])

	const { focus, requestFocus } = useSectionFocusRequest(
		activeHotelId,
		activeSeason
	)

	const goToBlock = useCallback(
		(
			hotelId: number | undefined,
			seasonIdx: number | undefined,
			source?: TBlockSection
		) => {
			imperativeFlushAll()
			if (hotelId !== undefined) setActiveHotelId(hotelId)
			if (seasonIdx !== undefined) setActiveSeason(seasonIdx)
			requestFocus(
				source && hotelId !== undefined && seasonIdx !== undefined
					? { hotelId, seasonIdx, source }
					: null
			)
		},
		[imperativeFlushAll, requestFocus]
	)

	const nextTooltipContent =
		sheetSummary.errors.length > 0 ? "Resolve all errors to proceed" : undefined

	return (
		<>
			<StepHeader
				onBack={onBack}
				backTitle={
					<Inline gap="2" alignItems="center">
						<Inline
							size="5"
							alignItems="center"
							justifyContent="center"
							borderWidth="1"
							rounded="full"
							color="muted"
							style={{ borderColor: "#60646C" }}
						>
							<Text fontSize="sm" color="muted">
								1
							</Text>
						</Inline>
						<Text fontSize="sm" color="muted" fontWeight="semibold">
							Hotels & Policies
						</Text>
					</Inline>
				}
				currentStepNumber={2}
				currentTitle={
					<>
						<Text color="primary" fontSize="md" fontWeight="semibold">
							Seasons & Rates
						</Text>
						<EditRateSheetName
							initialName={sheetName}
							onSuccess={onNameChange}
						/>
					</>
				}
				statusBar={
					<SheetStatusBar
						summary={sheetSummary}
						isEdit={isEdit}
						onGoToBlock={goToBlock}
					/>
				}
				actionButton={
					<Tooltip content={nextTooltipContent} disabled={!nextTooltipContent}>
						<Button
							level="primary"
							type="button"
							disabled={sheetSummary.errors.length > 0}
							onClick={onNext}
							style={{
								opacity: sheetSummary.errors.length > 0 ? 0.5 : 1,
							}}
						>
							Next <Icons.ChevronDown rotate="270" />
						</Button>
					</Tooltip>
				}
			/>
			<SectionFocusContext.Provider value={focus}>
				<HotelWiseSeasons
					hotels={hotels}
					hotelPolicies={hotelPolicies}
					hotelPaymentPolicies={hotelPaymentPolicies}
					policyOptions={policyOptions}
					tableErrors={tableErrors}
					setTableErrors={setTableErrors}
					summary={sheetSummary}
					hotelRatesRef={hotelRatesRef}
					originalHotelRatesRef={originalHotelRatesRef}
					flushCard={flushCard}
					registerCardFlush={registerCardFlush}
					imperativeFlushAll={imperativeFlushAll}
					activeHotelId={activeHotelId}
					setActiveHotelId={setActiveHotelId}
					activeSeason={activeSeason}
					setActiveSeason={setActiveSeason}
				/>
			</SectionFocusContext.Provider>
		</>
	)
}

// "View" on an issue in the status bar dialog asks the table it points at to
// scroll into view. The click cannot look the table up itself: the block remounts
// on a hotel or season switch, and the room rates grid waits on the hotel's
// details. So the request is held here and the table consumes it once it mounts.
type TSectionFocusRequest = {
	hotelId: number
	seasonIdx: number
	source: TBlockSection
}

type TSectionFocus = {
	source: TBlockSection | null
	done: () => void
}

const SectionFocusContext = createContext<TSectionFocus>({
	source: null,
	done: () => undefined,
})

// Owned by whoever owns the active hotel and season. A request only reaches the
// tables while the block it was made for is the active one, so one that never
// got consumed cannot scroll an unrelated block later.
function useSectionFocusRequest(
	activeHotelId: number,
	activeSeasonIdx: number
) {
	const [request, setRequest] = useState<TSectionFocusRequest | null>(null)
	const done = useCallback(() => setRequest(null), [])
	const focus = useMemo<TSectionFocus>(
		() => ({
			source:
				request &&
				request.hotelId === activeHotelId &&
				request.seasonIdx === activeSeasonIdx
					? request.source
					: null,
			done,
		}),
		[request, activeHotelId, activeSeasonIdx, done]
	)
	return { focus, requestFocus: setRequest }
}

function useScrollOnFocus<T extends HTMLElement>(source: TBlockSection) {
	const ref = useRef<T>(null)
	const { source: target, done } = useContext(SectionFocusContext)
	useEffect(() => {
		if (target !== source) return
		// Held back a frame so the block has laid out before it is measured
		const frame = requestAnimationFrame(() => {
			ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })
			done()
		})
		return () => cancelAnimationFrame(frame)
	}, [target, source, done])
	return ref
}

const SCROLL_TARGET_STYLE = { scrollMarginTop: "1rem" }

const CHANGE_COLOR = "#0284c7"
const WARNING_COLOR = "#E17100"

function groupEntriesByLabel(entries: TSummaryEntry[]) {
	const groups = new Map<string, TSummaryEntry[]>()
	entries.forEach((entry) => {
		const existing = groups.get(entry.label)
		if (existing) existing.push(entry)
		else groups.set(entry.label, [entry])
	})
	return Array.from(groups.entries())
}

function SummaryKindIcon({ kind }: { kind: TSummaryKind }) {
	if (kind === "error") return <Icons.CircleAlert size="4" color="danger" />
	if (kind === "warning")
		return <Icons.Attention size="4" style={{ color: WARNING_COLOR }} />
	return (
		<Text style={{ color: CHANGE_COLOR }} fontSize="sm">
			●
		</Text>
	)
}

// Sheet wide status, shared by steps 2 and 3. Reads only from the flushed block
// summaries, so typing in a rate cell does not re-render it — it updates when a
// block flushes.
function SheetStatusBar({
	summary,
	isEdit,
	onGoToBlock,
}: {
	summary: TSheetSummary
	isEdit: boolean
	onGoToBlock: (
		hotelId?: number,
		seasonIdx?: number,
		source?: TBlockSection
	) => void
}) {
	const [openKind, setOpenKind] = useState<TSummaryKind | null>(null)

	const entriesByKind: Record<TSummaryKind, TSummaryEntry[]> = {
		change: summary.changes,
		warning: summary.warnings,
		error: summary.errors,
	}
	const activeEntries = openKind ? entriesByKind[openKind] : []

	const title =
		openKind === "error"
			? "Errors"
			: openKind === "warning"
				? "Warnings"
				: "Changes"
	const noun =
		openKind === "error"
			? "Error"
			: openKind === "warning"
				? "Warning"
				: "Change"

	return (
		<>
			<Inline gap="4" alignItems="center" flexWrap="wrap">
				{isEdit && summary.changes.length === 0 ? (
					<Inline gap="2" alignItems="center">
						<Box size="2" rounded="full" bgColor="success_emphasis" />
						<Text fontSize="sm" fontWeight="semibold" color="success">
							No Changes
						</Text>
					</Inline>
				) : null}
				{summary.changes.length > 0 ? (
					<Inline
						gap="1"
						alignItems="center"
						cursor="pointer"
						style={{ color: CHANGE_COLOR }}
						onClick={() => setOpenKind("change")}
					>
						<Text fontSize="sm" fontWeight="semibold">
							● {pluralize(summary.changes.length, "Change")}
						</Text>
					</Inline>
				) : null}
				{summary.warnings.length > 0 ? (
					<Inline
						gap="1"
						alignItems="center"
						cursor="pointer"
						style={{ color: WARNING_COLOR }}
						onClick={() => setOpenKind("warning")}
					>
						<Icons.Attention size="4" />
						<Text fontSize="sm" fontWeight="semibold">
							{pluralize(summary.warnings.length, "Warning")}
						</Text>
					</Inline>
				) : null}
				{summary.errors.length > 0 ? (
					<Inline
						gap="1"
						alignItems="center"
						color="danger"
						cursor="pointer"
						onClick={() => setOpenKind("error")}
					>
						<Icons.CircleAlert size="4" />
						<Text fontSize="sm" fontWeight="semibold">
							{pluralize(summary.errors.length, "Error")}
						</Text>
					</Inline>
				) : null}
			</Inline>
			<Dialog
				open={openKind !== null}
				onClose={() => setOpenKind(null)}
				title={
					<Inline gap="2" alignItems="center">
						<Text fontWeight="semibold">{title}</Text>
						<Inline gap="1" alignItems="center">
							{openKind ? <SummaryKindIcon kind={openKind} /> : null}
							<Text
								fontSize="sm"
								fontWeight="semibold"
								color={openKind === "error" ? "danger" : undefined}
								style={
									openKind === "warning"
										? { color: WARNING_COLOR }
										: openKind === "change"
											? { color: CHANGE_COLOR }
											: undefined
								}
							>
								{pluralize(activeEntries.length, noun)} Found
							</Text>
						</Inline>
					</Inline>
				}
				sm
			>
				<DialogBody>
					<Stack gap="4">
						{groupEntriesByLabel(activeEntries).map(([label, entries]) => (
							<Stack key={label} gap="2">
								<Inline gap="2" alignItems="baseline" justifyContent="between">
									<Text fontSize="sm" fontWeight="semibold" color="muted">
										{label}
									</Text>
									{entries[0].seasonIdx !== undefined ? (
										<Button
											size="sm"
											level="secondary"
											onClick={() => {
												onGoToBlock(
													entries[0].hotelId,
													entries[0].seasonIdx,
													pickFocusSource(entries)
												)
												setOpenKind(null)
											}}
										>
											View <Icons.ArrowLeft rotate="180" size="4" />
										</Button>
									) : null}
								</Inline>
								<Stack gap="1">
									{entries.map((entry, idx) => (
										<Inline
											key={`${entry.message}-${idx}`}
											gap="2"
											alignItems="center"
										>
											<SummaryKindIcon kind={entry.kind} />
											<Text fontSize="sm" whiteSpace="preserveAndWrap">
												{entry.message}
											</Text>
										</Inline>
									))}
								</Stack>
							</Stack>
						))}
					</Stack>
				</DialogBody>
			</Dialog>
		</>
	)
}

function HotelWiseSeasons({
	hotels,
	hotelPolicies,
	hotelPaymentPolicies,
	policyOptions,
	tableErrors,
	setTableErrors,
	summary,
	hotelRatesRef,
	originalHotelRatesRef,
	flushCard,
	registerCardFlush,
	imperativeFlushAll,
	activeHotelId,
	setActiveHotelId,
	activeSeason,
	setActiveSeason,
}: THotelPolicyMaps & {
	hotels: IHotel[]
	policyOptions: TSheetPolicyOptions
	tableErrors: TTableErrors
	setTableErrors: React.Dispatch<React.SetStateAction<TTableErrors>>
	summary: TSheetSummary
	hotelRatesRef: THotelRatesRef
	originalHotelRatesRef: THotelRatesRef
	flushCard: (
		seasonKey: string | number,
		hotelId: number,
		rates: IRateBlockSchema
	) => void
	registerCardFlush: (key: string, flushFn: () => void) => () => void
	imperativeFlushAll: () => void
	// Owned by Step2Content so the status bar dialog can jump straight to a block
	activeHotelId: number
	setActiveHotelId: React.Dispatch<React.SetStateAction<number>>
	activeSeason: number
	setActiveSeason: React.Dispatch<React.SetStateAction<number>>
}) {
	const { value: seasons = [], onChange: setSeasons } =
		useFieldValue<ISeasonSchema[]>("seasons")
	// Pure UI state — kept out of the form so opening the dialog or switching
	// season does not push a change through every form subscriber.
	const [dialogState, setDialogState] = useState<{
		open: boolean
		editIndex: number | null
	}>({ open: false, editIndex: null })
	const [isLoading, setIsLoading] = useState(false)
	const isDialogOpen = dialogState.open
	const editIndex = dialogState.editIndex
	const closeDialog = () => {
		setIsLoading(false)
		setDialogState({ open: false, editIndex: null })
	}
	const isMobile = useIsMobile()

	async function handleAddSeason(newSeason: ISeasonSchema) {
		setIsLoading(true)
		await waitForPaint()
		const seasonWithUid: ISeasonSchema = {
			...newSeason,
			_uid: newSeason._uid || createSeasonUid(),
		}
		const next = [...seasons, seasonWithUid]
		setSeasons(next)
		setActiveSeason(next.length - 1)
		closeDialog()
	}

	function handleUpdateSeason(index: number, updated: ISeasonSchema) {
		const next = seasons.map((s, i) =>
			i === index
				? { ...updated, _uid: s._uid || updated._uid || createSeasonUid() }
				: s
		)
		setSeasons(next)
		closeDialog()
	}

	function handleDeleteSeason(index: number) {
		imperativeFlushAll()
		closeDialog()
		const deletedSeason = seasons[index]
		const deletedKey = deletedSeason?._uid || index

		hotels.forEach((hotel) => {
			hotelRatesRef.delete(rateKey(deletedKey, hotel.id))
			hotelRatesRef.delete(rateKey(index, hotel.id))
		})

		setTableErrors((prev) => {
			const next = { ...prev }
			delete next[String(deletedKey)]
			return next
		})

		const next = seasons.filter((_, i) => i !== index)
		setSeasons(next)
		setActiveSeason(
			next.length === 0 ? 0 : Math.min(activeSeason, next.length - 1)
		)
	}

	// What the season dialog validates its name and intervals against. The season
	// being edited is dropped, so reopening it and saving it unchanged is not a
	// conflict with itself.
	const otherSeasons = useMemo(
		() => seasons.filter((_, idx) => idx !== editIndex),
		[seasons, editIndex]
	)

	function openAddDialog() {
		setDialogState({ open: true, editIndex: null })
	}

	function openEditDialog(idx: number) {
		setDialogState({ open: true, editIndex: idx })
	}

	// Inactive hotel tabs unmount, so anything not yet flushed has to be pushed
	// into hotelRatesRef before switching away — same contract the season switch
	// already follows.
	const changeActiveHotel = useCallback(
		(next: number | ((currentId: number) => number)) => {
			imperativeFlushAll()
			setActiveHotelId(next)
		},
		[imperativeFlushAll, setActiveHotelId]
	)

	useEffect(() => {
		if (
			hotels.length > 0 &&
			!hotels.some((h) => Number(h.id) === activeHotelId)
		) {
			setActiveHotelId(Number(hotels[0].id))
		}
	}, [hotels, activeHotelId, setActiveHotelId])

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return
			const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
			if (tag === "input" || tag === "textarea" || tag === "select") return
			e.preventDefault()
			changeActiveHotel((currentId) => {
				const currentIdx = hotels.findIndex((h) => Number(h.id) === currentId)
				if (currentIdx === -1) return currentId
				const nextIdx =
					e.key === "ArrowDown"
						? (currentIdx + 1) % hotels.length
						: (currentIdx - 1 + hotels.length) % hotels.length
				return Number(hotels[nextIdx].id)
			})
		}
		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [hotels, changeActiveHotel])

	return (
		<>
			<Tabs
				layout={isMobile ? "col" : "row"}
				alignItems="stretch"
				style={{ minHeight: "calc(100vh - 12rem)" }}
			>
				{isMobile ? (
					<Inline
						alignItems="center"
						color="muted"
						gap="1"
						fontWeight="semibold"
						bgColor="white"
						padding="4"
						borderBottomWidth="1"
					>
						<Text>{`Hotels (${hotels.length})`}</Text>
					</Inline>
				) : null}
				<TabsList
					position={isMobile ? undefined : "sticky"}
					style={{
						minWidth: isMobile ? undefined : "12rem",
						maxWidth: isMobile ? undefined : "16rem",
						padding: 0,
						rowGap: 0,
					}}
					flexShrink="0"
					minHeight="full"
				>
					{isMobile ? null : (
						<>
							<Inline
								alignItems="center"
								padding="4"
								paddingLeft="6"
								bgColor="white"
							>
								<Text
									fontWeight="semibold"
									color="muted"
									style={{ lineHeight: "28px" }}
								>
									{`Seasons (${seasons.length})`}{" "}
									<Icons.ArrowLeft rotate="180" />
								</Text>
							</Inline>
							<Inline
								paddingLeft="6"
								alignItems="center"
								color="muted"
								gap="1"
								fontWeight="semibold"
								bgColor="white"
								padding="4"
							>
								<Text
									style={{ lineHeight: "28px" }}
								>{`Hotels (${hotels.length})`}</Text>
								<Box>
									<Icons.ArrowLeft rotate="270" />
								</Box>
							</Inline>
						</>
					)}
					{!hotels.length ? (
						<Inline justifyContent="center">
							<Box
								bgColor="danger"
								padding="2"
								rounded="md"
								style={{
									width: "fit-content",
								}}
								fontWeight="semibold"
								fontSize="sm"
								color="danger"
							>
								Please select hotels in Step 1
							</Box>
						</Inline>
					) : null}
					{hotels.map((hotel) => {
						const hotelId = Number(hotel.id)
						return (
							<HotelSidebarItem
								key={hotel.id}
								hotel={hotel}
								active={activeHotelId === hotelId}
								onClick={() => changeActiveHotel(hotelId)}
								indicators={summary.byHotel.get(hotelId)}
							/>
						)
					})}
				</TabsList>
				{hotels.map((hotel, hotelIndex) => {
					const hotelId = Number(hotel.id)
					return (
						<TabContent
							key={hotel.id}
							active={activeHotelId === hotelId}
							unmountOnInactive
							flex="1"
						>
							<SeasonRatesForHotel
								hotel={hotel}
								hotelIndex={hotelIndex}
								seasons={seasons}
								summary={summary}
								activeSeason={activeSeason}
								onSeasonChange={(idx) => {
									imperativeFlushAll()
									setActiveSeason(idx)
								}}
								onAddSeason={openAddDialog}
								onEditSeason={openEditDialog}
								tableErrors={tableErrors}
								hotelRatesRef={hotelRatesRef}
								originalHotelRatesRef={originalHotelRatesRef}
								flushCard={flushCard}
								registerCardFlush={registerCardFlush}
								cancellationPolicy={hotelPolicies[Number(hotel.id)] || null}
								paymentPolicy={hotelPaymentPolicies[Number(hotel.id)] || null}
								policyOptions={policyOptions}
								isMobile={isMobile}
							/>
						</TabContent>
					)
				})}
			</Tabs>
			<Dialog
				open={isDialogOpen}
				onClose={isLoading ? () => undefined : closeDialog}
				title={editIndex === null ? "Add season" : "Update season"}
				sm
			>
				<SeasonForm
					mode={editIndex === null ? "add" : "edit"}
					initialSeason={
						editIndex === null
							? {
									_uid: createSeasonUid(),
									name: "",
									intervals: [newInterval()],
									rate_blocks: [],
								}
							: seasons[editIndex]
					}
					otherSeasons={otherSeasons}
					isLoading={isLoading}
					onSubmit={(updated) =>
						editIndex === null
							? handleAddSeason(updated)
							: handleUpdateSeason(editIndex, updated)
					}
					onClose={closeDialog}
					onDelete={
						editIndex === null ? undefined : () => handleDeleteSeason(editIndex)
					}
				/>
			</Dialog>
		</>
	)
}

function SeasonForm({
	mode,
	initialSeason,
	otherSeasons,
	onSubmit,
	onDelete,
	isLoading,
}: {
	mode: "add" | "edit"
	initialSeason: ISeasonSchema
	// Every other season of the sheet — the one being edited is excluded by the
	// caller, so an edit never conflicts with itself
	otherSeasons: TSeasonMeta[]
	onSubmit: (season: ISeasonSchema) => Promise<void> | void
	onClose: () => void
	onDelete?: () => void
	isLoading?: boolean
}) {
	const intervals = initialSeason?.intervals || [newInterval()]

	// Closes over the sibling seasons, so it cannot be hoisted out of the
	// component the way a static schema would be
	const validate = useMemo(
		() => validateFormValues(buildSeasonValidationSchema(otherSeasons)),
		[otherSeasons]
	)

	return (
		<Form<ISeasonSchema>
			mutators={{ ...arrayMutators }}
			initialValues={{ ...initialSeason, intervals }}
			validate={validate}
			onSubmit={withServerErrors(
				async (values) =>
					await onSubmit({ ...values, name: values.name.trim() })
			)}
			subscription={{ submitting: true, submitError: true, errors: true }}
		>
			{({ handleSubmit, form, submitting }) => {
				const isBusy = submitting || isLoading
				return (
					<form noValidate onSubmit={handleSubmit}>
						<DialogBody>
							<Stack gap="4">
								<Stack gap="2">
									<TextInputField
										label="Season name"
										name="name"
										placeholder="Name"
										maxWidth="xs"
									/>
									{mode === "add" ? (
										<Inline gap="2" alignItems="center" fontSize="sm">
											<Text fontSize="xs" color="muted">
												Suggestions
											</Text>
											{SEASON_SUGGESTIONS.map((s) => (
												<Box
													as="button"
													type="button"
													paddingY="1"
													paddingX="2"
													bgColor="inset"
													rounded="md"
													key={s}
													onMouseDown={(e: React.MouseEvent) => {
														e.preventDefault()
													}}
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														form.change("name", s)
													}}
												>
													{s}
												</Box>
											))}
										</Inline>
									) : null}
								</Stack>
								<Stack gap="4">
									<FieldArray name="intervals">
										{({ fields }) => (
											<>
												{fields.map((name, iIdx) => (
													<>
														{iIdx > 0 ? <Divider marginY="0" /> : null}
														<Stack key={name} gap="2">
															<Inline gap="4" alignItems="end" flexWrap="wrap">
																<Inline gap="4">
																	<DatePickerField
																		label="From"
																		name={`${name}.from`}
																		dateFormat="DD MMM, YYYY"
																	/>
																	<GetFieldValue<
																		ISeasonSchema["intervals"][number]["from"]
																	>
																		name={`${name}.from`}
																	>
																		{({ value: from }) => (
																			<DatePickerField
																				label="To"
																				name={`${name}.to`}
																				min={from || undefined}
																				dateFormat="DD MMM, YYYY"
																			/>
																		)}
																	</GetFieldValue>
																</Inline>
																{fields.length && fields.length > 1 ? (
																	<Button
																		level="tertiary"
																		size="sm"
																		type="button"
																		onClick={() => fields.remove(iIdx)}
																	>
																		<Icons.Cancel />
																	</Button>
																) : null}
															</Inline>
															<IntervalDaysSelector name={`${name}.days`} />
														</Stack>
													</>
												))}
												<Box paddingTop="2">
													<Button
														level="tertiary"
														size="sm"
														type="button"
														color="primary"
														onClick={() => fields.push(newInterval())}
													>
														<Icons.Plus />
														Add Interval
													</Button>
												</Box>
											</>
										)}
									</FieldArray>
									<FieldArrayErrorMessage name="intervals" />
								</Stack>
							</Stack>
						</DialogBody>
						<DialogFooter>
							<Inline justifyContent="between" alignItems="center">
								<Box>
									{mode === "edit" && onDelete ? (
										<Button
											status="danger"
											size="sm"
											type="button"
											disabled={isBusy}
											onClick={onDelete}
											style={{ opacity: isBusy ? 0.5 : 1 }}
										>
											Delete season
										</Button>
									) : null}
								</Box>
								<Button
									level="primary"
									type="submit"
									disabled={isBusy}
									style={{ opacity: isBusy ? 0.5 : 1 }}
								>
									{isBusy ? <Icons.Spinner /> : null}
									{mode === "add" ? "Add season" : "Update"}
								</Button>
							</Inline>
						</DialogFooter>
					</form>
				)
			}}
		</Form>
	)
}

export function HotelSidebarItem({
	hotel,
	active,
	onClick,
	indicators,
}: {
	hotel: IHotel
	active: boolean
	onClick: () => void
	indicators?: THotelIndicators
}) {
	return (
		<TabItem
			active={active}
			onClick={onClick}
			style={{ padding: 0 }}
			bgColor={active ? "primary" : undefined}
		>
			<Inline
				gap="2"
				paddingY="1"
				paddingRight="3"
				alignItems="center"
				justifyContent="between"
			>
				<Inline gap="1">
					<Text
						fontWeight="semibold"
						color={active ? "primary" : undefined}
						textOverflow="truncate"
						style={{ maxWidth: "9rem" }}
						title={hotel.name}
					>
						{hotel.name}
					</Text>
					<MiniCurrency currency={hotel.currency} />
				</Inline>
				<Inline gap="2" alignItems="center" flexShrink="0">
					{indicators?.hasWarnings ? (
						<Tooltip content="Warnings found in seasons">
							<Box display="flex" alignItems="center">
								<Icons.Attention size="4" color="warning_light" />
							</Box>
						</Tooltip>
					) : null}
					{indicators?.hasErrors ? (
						<Tooltip content="Errors found in seasons">
							<Box display="flex" alignItems="center">
								<Icons.CircleAlert size="4" color="danger" />
							</Box>
						</Tooltip>
					) : null}
					{indicators?.changed ? (
						<Box
							size="2"
							rounded="full"
							flexShrink="0"
							style={{ backgroundColor: CHANGE_COLOR }}
						/>
					) : null}
				</Inline>
			</Inline>
		</TabItem>
	)
}

// Change / warning / error markers on a season tab. The sources are named in
// the tooltip so a user knows whether to open the meal rates or the room rates.
function SeasonTabIndicators({
	indicators,
}: {
	indicators?: TSeasonIndicators
}) {
	if (!indicators) return null
	const { changed, warningSources, errorSources, hasPolicyError } = indicators
	if (
		!changed &&
		!warningSources.length &&
		!errorSources.length &&
		!hasPolicyError
	) {
		return null
	}

	return (
		<Inline gap="2" alignItems="center" flexShrink="0">
			{warningSources.length > 0 ? (
				<Tooltip
					content={`Warnings found in ${describeIssueSources(warningSources)}`}
				>
					<Box display="flex" alignItems="center">
						<Icons.Attention size="4" color="warning_light" />
					</Box>
				</Tooltip>
			) : null}
			{errorSources.length > 0 || hasPolicyError ? (
				<Tooltip
					content={`Errors found in ${describeIssueSources(
						hasPolicyError ? [...errorSources, "policies"] : errorSources
					)}`}
				>
					<Box display="flex" alignItems="center">
						<Icons.CircleAlert size="4" color="danger" />
					</Box>
				</Tooltip>
			) : null}
			{changed ? (
				<Box
					size="2"
					rounded="full"
					flexShrink="0"
					style={{ backgroundColor: CHANGE_COLOR }}
				/>
			) : null}
		</Inline>
	)
}

function SeasonRatesForHotel({
	hotel,
	hotelIndex,
	seasons,
	summary,
	activeSeason,
	onSeasonChange,
	onAddSeason,
	onEditSeason,
	tableErrors,
	hotelRatesRef,
	originalHotelRatesRef,
	flushCard,
	registerCardFlush,
	cancellationPolicy,
	paymentPolicy,
	policyOptions,
	isMobile,
}: {
	hotel: IHotel
	hotelIndex: number
	seasons: ISeasonSchema[]
	summary: TSheetSummary
	activeSeason: number
	onSeasonChange: (idx: number) => void
	onAddSeason: () => void
	onEditSeason: (idx: number) => void
	tableErrors: TTableErrors
	hotelRatesRef: THotelRatesRef
	originalHotelRatesRef: THotelRatesRef
	flushCard: (
		seasonKey: string | number,
		hotelId: number,
		rates: IRateBlockSchema
	) => void
	registerCardFlush: (key: string, flushFn: () => void) => () => void
	cancellationPolicy: TCancellationPolicy | null
	paymentPolicy: TPaymentPolicy | null
	policyOptions: TSheetPolicyOptions
	isMobile: boolean
}) {
	const season = seasons[activeSeason]
	const seasonKey = season?._uid || activeSeason

	const otherSeasons = useMemo(() => {
		return seasons
			.map((s, idx) => ({ id: idx, name: s.name, _uid: s._uid }))
			.filter((_, idx) => idx !== activeSeason)
	}, [seasons, activeSeason])

	const getCopyRatesSource = useCallback(
		(fromIdx: number) => {
			const fromSeason = seasons[fromIdx]
			const fromKey = fromSeason?._uid || fromIdx
			const source =
				hotelRatesRef.get(rateKey(fromKey, hotel.id)) ||
				hotelRatesRef.get(rateKey(fromIdx, hotel.id)) ||
				null
			return source ? structuredClone(source) : null
		},
		[hotel.id, hotelRatesRef, seasons]
	)

	// The block form's API, captured from its render prop below. Holding it here
	// lets every handler passed to HotelRateCard be referentially stable, which
	// is what makes its memo() actually hit.
	const blockFormRef = useRef<FormApi<IRateBlockSchema> | null>(null)

	// Bumped after a "Copy Rates from" so RoomRatesSection remounts and reseeds
	// its derivation cache from the copied pax types — see handleCopyRates.
	const [copyRatesTick, setCopyRatesTick] = useState(0)

	const getBlockFormValues = useCallback(
		() => blockFormRef.current?.getState().values as IRateBlockSchema,
		[]
	)

	const handleCopyRates = useCallback(
		(fromIdx: number) => {
			const form = blockFormRef.current
			const fromRates = getCopyRatesSource(fromIdx)
			if (!form || !fromRates) return
			bulkChange(form, () => {
				form.change("pricing_type", fromRates.pricing_type)
				form.change("rounding_point", fromRates.rounding_point)
				form.change("cancellation_policy", fromRates.cancellation_policy)
				form.change(
					"cancellation_policy_is_manual",
					fromRates.cancellation_policy_is_manual
				)
				form.change("payment_policy", fromRates.payment_policy)
				form.change(
					"payment_policy_is_manual",
					fromRates.payment_policy_is_manual
				)
				form.change("rooms", fromRates.rooms)
				form.change("rates", fromRates.rates)
				form.change("pax_types", fromRates.pax_types)
				form.change(
					"is_lower_occupancy_rate_configured",
					fromRates.is_lower_occupancy_rate_configured
				)
				form.change("meal_plans", fromRates.meal_plans)
				form.change(
					"meal_rates",
					syncBlockMealRates(
						fromRates.meal_plans,
						fromRates.meal_rates
					) as never
				)
				form.change(
					"child_meal_rate_same_as_adult",
					fromRates.child_meal_rate_same_as_adult
				)
			})
			// The rate grid caches derivation formulas outside the form (see
			// RoomRatesSection's mountedPaxTypes) so a plain form.change above would
			// have that state clobber the copied pax types right back. Remounting is
			// what makes it reseed from what was just copied in.
			setCopyRatesTick((tick) => tick + 1)
			flushCard(seasonKey, Number(hotel.id), form.getState().values)
		},
		[getCopyRatesSource, flushCard, seasonKey, hotel.id]
	)

	const handleUpdateMealPlans = useCallback(
		(updated: IRateBlockSchema["meal_plans"]) => {
			const form = blockFormRef.current
			if (!form) return
			// Sorted here, once, regardless of which UI action produced `updated` —
			// toggling a plan on/off, restricting its rooms, or setting a new base.
			const sorted = [...updated].sort(compareMealPlanEntriesByPriority)
			const currentMealRates = form.getState().values.meal_rates || []
			const nextMealRates = syncBlockMealRates(sorted, currentMealRates)
			bulkChange(form, () => {
				form.change("meal_plans", sorted)
				form.change("meal_rates", nextMealRates)
			})
			flushCard(seasonKey, Number(hotel.id), form.getState().values)
		},
		[seasonKey, hotel.id, flushCard]
	)

	const handleUpdateRooms = useCallback((rooms: IRateBlockSchema["rooms"]) => {
		blockFormRef.current?.change("rooms", rooms)
	}, [])

	const handleUpdateMealRate = useCallback(
		(
			mealRates: IRateBlockSchema["meal_rates"],
			childSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
		) => {
			const form = blockFormRef.current
			if (!form) return
			bulkChange(form, () => {
				form.change("meal_rates", mealRates)
				form.change("child_meal_rate_same_as_adult", childSameAsAdult)
			})
		},
		[]
	)

	const handleUpdateRatesAndPaxTypes = useCallback(
		(
			rates: IRateBlockSchema["rates"],
			paxTypes: IRateBlockSchema["pax_types"],
			isLowerOccupancyConfigured?: boolean
		) => {
			const form = blockFormRef.current
			if (!form) return
			bulkChange(form, () => {
				form.change("rates", rates)
				form.change("pax_types", paxTypes)
				if (isLowerOccupancyConfigured !== undefined) {
					form.change(
						"is_lower_occupancy_rate_configured",
						isLowerOccupancyConfigured
					)
				}
			})
		},
		[]
	)

	// react-final-form reinitializes whenever `initialValues` fails a shallow
	// compare, and this object holds freshly built arrays. Without the memo the
	// block form reinitializes on every render. The `<Form key>` below covers
	// hotel/season changes, which is what re-reads the refs.
	const blockInitialValues = useMemo<IRateBlockSchema>(() => {
		const flushed =
			hotelRatesRef.get(rateKey(seasonKey, hotel.id)) ||
			hotelRatesRef.get(rateKey(activeSeason, hotel.id)) ||
			null
		return {
			hotel: Number(hotel.id),
			// A block that did not override a policy follows the hotel's, so the
			// hotel's value wins over the one the block was flushed with — that is
			// what makes a change made in step 1 reach every block that follows it.
			cancellation_policy: flushed?.cancellation_policy_is_manual
				? (flushed.cancellation_policy ?? null)
				: (cancellationPolicy ?? null),
			cancellation_policy_is_manual:
				flushed?.cancellation_policy_is_manual ?? false,
			payment_policy: flushed?.payment_policy_is_manual
				? (flushed.payment_policy ?? null)
				: (paymentPolicy ?? null),
			payment_policy_is_manual: flushed?.payment_policy_is_manual ?? false,
			rounding_point: flushed?.rounding_point ?? { id: null, name: "None" },
			pricing_type: flushed?.pricing_type ?? { id: "RBP", name: "Room Rate" },
			child_meal_rate_same_as_adult:
				flushed?.child_meal_rate_same_as_adult ?? true,
			is_lower_occupancy_rate_configured:
				flushed?.is_lower_occupancy_rate_configured ?? false,
			meal_plans: flushed?.meal_plans ?? [],
			meal_rates: syncBlockMealRates(flushed?.meal_plans, flushed?.meal_rates),
			pax_types: flushed?.pax_types ?? [],
			rates: flushed?.rates ?? [],
			// Seeded from whatever the hotel prop carries, then refreshed by
			// HotelRateForm once the full hotel has been fetched
			rooms: flushed?.rooms ?? buildRooms(hotel.ota_room_types),
		}
	}, [
		hotel,
		seasonKey,
		activeSeason,
		cancellationPolicy,
		paymentPolicy,
		hotelRatesRef,
	])

	const originalBlock = useMemo(
		() =>
			originalHotelRatesRef.get(rateKey(seasonKey, hotel.id)) ||
			originalHotelRatesRef.get(rateKey(activeSeason, hotel.id)) ||
			null,
		[originalHotelRatesRef, seasonKey, activeSeason, hotel.id]
	)

	const blockPolicies = useMemo<TBlockPolicyContext>(
		() => ({
			hotelPolicy: cancellationPolicy,
			hotelPaymentPolicy: paymentPolicy,
			baseline: originalBlock,
		}),
		[cancellationPolicy, paymentPolicy, originalBlock]
	)

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
			const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
			if (tag === "input" || tag === "textarea" || tag === "select") return
			e.preventDefault()
			const nextIdx =
				e.key === "ArrowRight"
					? (activeSeason + 1) % seasons.length
					: (activeSeason - 1 + seasons.length) % seasons.length
			onSeasonChange(nextIdx)
		}
		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [seasons, activeSeason, onSeasonChange])

	if (!seasons.length) {
		return (
			<Stack
				padding="4"
				gap="3"
				justifyContent="center"
				alignItems="center"
				style={{ height: "calc(100vh - 11.6rem)" }}
			>
				<Icons.CalendarDays size="32" color="subtle" />
				<Text color="primary" fontSize="lg" fontWeight="semibold">
					Add Seasons to set Rates
				</Text>
				<Box>
					<Button level="primary" type="button" onClick={onAddSeason}>
						<Icons.Plus />
						Add season
					</Button>
				</Box>
			</Stack>
		)
	}

	return (
		<Stack
			style={{ minHeight: "calc(100vh - 12rem)", backgroundColor: "#f5f5f5" }}
		>
			<Tabs layout="col">
				{isMobile ? (
					<Inline
						alignItems="center"
						justifyContent="between"
						padding="2"
						paddingLeft="4"
						bgColor="white"
						borderBottomWidth="1"
					>
						<Text fontWeight="semibold" color="muted">
							{`Seasons (${seasons.length})`}{" "}
						</Text>
						<Button level="tertiary" color="primary" onClick={onAddSeason}>
							<Icons.Plus />
							Add season
						</Button>
					</Inline>
				) : null}
				<Inline>
					<TabsList
						flex="1"
						borderBottomWidth={isMobile ? "1" : "0"}
						style={{ paddingInline: 0 }}
					>
						{seasons.map((s, idx) => (
							<TabItem
								key={s._uid || idx}
								active={activeSeason === idx}
								onClick={() => onSeasonChange(idx)}
								bgColor={activeSeason === idx ? "primary" : undefined}
								style={{
									borderBottom: isMobile ? 0 : undefined,
								}}
							>
								<Stack paddingX={isMobile ? "1" : "2"}>
									<Inline gap="2" alignItems="center">
										<Text
											fontWeight="semibold"
											color={activeSeason === idx ? "primary" : undefined}
										>
											{s.name}
										</Text>
										<Box
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation()
												e.preventDefault()
												onEditSeason(idx)
											}}
											paddingX="1"
											borderWidth="1"
											borderColor={{ default: "transparent", hover: "default" }}
											bgColor={{ default: undefined, hover: "white" }}
											rounded="md"
										>
											<Icons.Pencil title="Edit season" color="subtle" />
										</Box>
										<SeasonTabIndicators
											indicators={summary.bySeason.get(rateKey(idx, hotel.id))}
										/>
									</Inline>
									<Text fontSize="xs" color="muted" fontWeight="normal">
										{pluralize(s.intervals.length, "Interval")}
									</Text>
								</Stack>
							</TabItem>
						))}
					</TabsList>
					{isMobile ? null : (
						<Stack bgColor="white" justifyContent="center" paddingX="6">
							<Button level="tertiary" color="primary" onClick={onAddSeason}>
								<Icons.Plus />
								Add season
							</Button>
						</Stack>
					)}
				</Inline>
				{season ? (
					<TabContent active={true}>
						{season.intervals && season.intervals.length > 0 ? (
							<SeasonIntervals intervals={season.intervals} />
						) : null}
						<Form<IRateBlockSchema>
							key={`${hotel.id}-${season?._uid || activeSeason}`}
							initialValues={blockInitialValues}
							onSubmit={() => undefined}
							mutators={{ ...arrayMutators }}
							validate={validateBlockMealRates}
						>
							{({ form, values }) => {
								blockFormRef.current = form
								return (
									<HotelRateCard
										hotel={hotel}
										seasonKey={seasonKey}
										seasonIndex={activeSeason}
										otherSeasons={otherSeasons}
										onCopyRates={handleCopyRates}
										copyRatesTick={copyRatesTick}
										hotelErrors={tableErrors[String(seasonKey)]?.[hotelIndex]}
										onFlush={flushCard}
										registerFlush={registerCardFlush}
										getFormValues={getBlockFormValues}
										policyOptions={policyOptions}
										blockPolicies={blockPolicies}
										parentMealPlans={values.meal_plans}
										parentMealRates={values.meal_rates}
										parentRates={values.rates}
										parentPaxTypes={values.pax_types}
										parentRooms={values.rooms}
										parentRoundingPoint={values.rounding_point}
										initialBlockRates={originalBlock?.rates || EMPTY_RATES}
										initialBlockMealRates={
											originalBlock?.meal_rates || EMPTY_MEAL_RATES
										}
										initialBlockPaxTypes={
											originalBlock?.pax_types || EMPTY_PAX_TYPES
										}
										initialBlockRooms={originalBlock?.rooms || EMPTY_ROOMS}
										onUpdateMealPlans={handleUpdateMealPlans}
										onUpdateRooms={handleUpdateRooms}
										childRateSameAsAdult={values.child_meal_rate_same_as_adult}
										onUpdateMealRate={handleUpdateMealRate}
										parentIsLowerOccupancyRateConfigured={
											values.is_lower_occupancy_rate_configured
										}
										onUpdateRatesAndPaxTypes={handleUpdateRatesAndPaxTypes}
									/>
								)
							}}
						</Form>
					</TabContent>
				) : null}
			</Tabs>
		</Stack>
	)
}

const HotelRateCard = memo(function HotelRateCard({
	hotel,
	seasonKey,
	seasonIndex,
	otherSeasons,
	onCopyRates,
	copyRatesTick,
	hotelErrors,
	onFlush,
	registerFlush,
	getFormValues,
	policyOptions,
	blockPolicies,
	parentMealPlans,
	parentMealRates,
	parentRates,
	parentPaxTypes,
	parentRooms,
	parentRoundingPoint,
	parentIsLowerOccupancyRateConfigured,
	initialBlockRates,
	initialBlockMealRates,
	initialBlockPaxTypes,
	initialBlockRooms,
	childRateSameAsAdult,
	onUpdateMealPlans,
	onUpdateRooms,
	onUpdateMealRate,
	onUpdateRatesAndPaxTypes,
}: {
	hotel: IHotel
	seasonKey: string | number
	seasonIndex: number
	otherSeasons: { id: number; name: string }[]
	onCopyRates: (fromIdx: number) => void
	copyRatesTick: number
	hotelErrors?: THotelRateErrors
	onFlush: (
		seasonKey: string | number,
		hotelId: number,
		rates: IRateBlockSchema
	) => void
	registerFlush: (key: string, flushFn: () => void) => () => void
	getFormValues: () => IRateBlockSchema
	policyOptions: TSheetPolicyOptions
	blockPolicies: TBlockPolicyContext
	parentMealPlans: IRateBlockSchema["meal_plans"]
	parentMealRates: IRateBlockSchema["meal_rates"]
	parentRates: IRateBlockSchema["rates"]
	parentPaxTypes: IRateBlockSchema["pax_types"]
	parentRooms: IRateBlockSchema["rooms"]
	parentRoundingPoint?: IRateBlockSchema["rounding_point"]
	parentIsLowerOccupancyRateConfigured?: boolean
	initialBlockRates: IRateBlockSchema["rates"]
	initialBlockMealRates?: IRateBlockSchema["meal_rates"]
	initialBlockPaxTypes?: IRateBlockSchema["pax_types"]
	initialBlockRooms?: IRateBlockSchema["rooms"]
	childRateSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	onUpdateMealPlans: (updated: IRateBlockSchema["meal_plans"]) => void
	onUpdateRooms: (rooms: IRateBlockSchema["rooms"]) => void
	onUpdateMealRate: (
		mealRates: IRateBlockSchema["meal_rates"],
		childSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	) => void
	onUpdateRatesAndPaxTypes: (
		rates: IRateBlockSchema["rates"],
		paxTypes: IRateBlockSchema["pax_types"],
		isLowerOccupancyConfigured?: boolean
	) => void
}) {
	const hotelId = Number(hotel.id)

	const getValuesRef = useRef(getFormValues)
	useEffect(() => {
		getValuesRef.current = getFormValues
	}, [getFormValues])

	useEffect(() => {
		const key = rateKey(seasonKey, hotelId)
		const unregister = registerFlush(key, () => {
			onFlush(seasonKey, hotelId, getValuesRef.current())
		})
		// A card's initial values can already differ from what is in
		// `hotelRatesRef` — the hotel's OTA rooms or meal plans may have changed
		// since this sheet was saved, seeding rows the ref has never seen. Without
		// this, the errors those rows carry render in the grid immediately (it
		// reads live form values) but stay invisible in the header summary until
		// the user blurs a field or hits Next, whichever flushes first.
		onFlush(seasonKey, hotelId, getValuesRef.current())
		return unregister
	}, [seasonKey, hotelId, onFlush, registerFlush])

	const debouncedFlush = useDebounce(() => {
		onFlush(seasonKey, hotelId, getValuesRef.current())
	}, 100)

	return (
		<form noValidate>
			<HotelRateForm
				hotel={hotel}
				otherSeasons={otherSeasons}
				onCopyRates={onCopyRates}
				copyRatesTick={copyRatesTick}
				hotelErrors={hotelErrors}
				onFieldBlur={debouncedFlush}
				policyOptions={policyOptions}
				blockPolicies={blockPolicies}
				parentMealPlans={parentMealPlans}
				parentMealRates={parentMealRates}
				parentRates={parentRates}
				parentPaxTypes={parentPaxTypes}
				parentRooms={parentRooms}
				parentRoundingPoint={parentRoundingPoint}
				parentIsLowerOccupancyRateConfigured={
					parentIsLowerOccupancyRateConfigured
				}
				initialBlockRates={initialBlockRates}
				initialBlockMealRates={initialBlockMealRates}
				initialBlockPaxTypes={initialBlockPaxTypes}
				initialBlockRooms={initialBlockRooms}
				childRateSameAsAdult={childRateSameAsAdult}
				onUpdateMealPlans={onUpdateMealPlans}
				onUpdateRooms={onUpdateRooms}
				onUpdateMealRate={onUpdateMealRate}
				onUpdateRatesAndPaxTypes={onUpdateRatesAndPaxTypes}
			/>
		</form>
	)
})

// The block's policies. Collapsed it reads them; expanded it lets the block
// override what step 1 picked for the hotel. Three states can show at once — an
// override, a value changed since the last save, and a pinned version the head
// has moved past — and each has its own control.
function RateBlockPoliciesSection({
	hotel,
	policyOptions,
	blockPolicies,
	onFieldBlur,
	isMobile,
}: {
	hotel: IHotel
	policyOptions: TSheetPolicyOptions
	blockPolicies: TBlockPolicyContext
	onFieldBlur: () => void
	isMobile: boolean
}) {
	const { policyHeads, cancellationOptions, paymentOptions } = policyOptions
	const { hotelPolicy, hotelPaymentPolicy, baseline } = blockPolicies

	const { value: cancellationPolicy, onChange: setCancellationPolicy } =
		useFieldValue<TCancellationPolicy | null>("cancellation_policy")
	const { value: cancellationIsManual, onChange: setCancellationIsManual } =
		useFieldValue<boolean>("cancellation_policy_is_manual")
	const { value: paymentPolicy, onChange: setPaymentPolicy } =
		useFieldValue<TPaymentPolicy | null>("payment_policy")
	const { value: paymentIsManual, onChange: setPaymentIsManual } =
		useFieldValue<boolean>("payment_policy_is_manual")

	// Picking a policy here is an override of whatever the hotel sells under
	function changeCancellation(policy: TCancellationPolicy | null) {
		setCancellationPolicy(policy)
		setCancellationIsManual(true)
		onFieldBlur()
	}
	function changePayment(policy: TPaymentPolicy | null) {
		setPaymentPolicy(policy)
		setPaymentIsManual(true)
		onFieldBlur()
	}
	// ✕ — drop the override and go back to the hotel's policy
	function clearCancellationOverride() {
		setCancellationPolicy(hotelPolicy)
		setCancellationIsManual(false)
		onFieldBlur()
	}
	function clearPaymentOverride() {
		setPaymentPolicy(hotelPaymentPolicy)
		setPaymentIsManual(false)
		onFieldBlur()
	}
	// ↺ — put back what this block was last saved with, this block only
	function revertCancellation() {
		setCancellationPolicy(baseline?.cancellation_policy || null)
		setCancellationIsManual(Boolean(baseline?.cancellation_policy_is_manual))
		onFieldBlur()
	}
	function revertPayment() {
		setPaymentPolicy(baseline?.payment_policy || null)
		setPaymentIsManual(Boolean(baseline?.payment_policy_is_manual))
		onFieldBlur()
	}

	// Only worth offering where there is something to go back to
	const cancellationChangedSinceSave =
		Boolean(baseline) &&
		(baseline?.cancellation_policy?.id ?? null) !==
			(cancellationPolicy?.id ?? null)
	const paymentChangedSinceSave =
		Boolean(baseline) &&
		(baseline?.payment_policy?.id ?? null) !== (paymentPolicy?.id ?? null)

	return (
		<Component initialState={false}>
			{({ state, setState }) => (
				<Inline
					gap={isMobile ? "4" : "16"}
					justifyContent="between"
					alignItems={state ? "start" : "center"}
					flexWrap="wrap"
				>
					<Inline
						alignItems={state ? "start" : "center"}
						gap={isMobile ? "2" : "12"}
						flex="1"
						flexWrap="wrap"
					>
						<Text fontWeight="semibold" color="primary" fontSize="lg">
							Policies
						</Text>
						<Inline
							gap={isMobile ? "4" : "16"}
							alignItems="start"
							flexWrap="wrap"
						>
							{state ? (
								<>
									<Stack gap="1">
										<PolicySelect
											kind="cancellation"
											selectKey={hotel.id}
											maxWidth="sm"
											policy={cancellationPolicy}
											options={cancellationOptions}
											heads={policyHeads.cancellation}
											isOverridden={Boolean(cancellationIsManual)}
											onChange={(next) =>
												changeCancellation(
													(next as TCancellationPolicy) || null
												)
											}
											onClear={
												cancellationIsManual
													? clearCancellationOverride
													: undefined
											}
											onRevert={
												cancellationChangedSinceSave
													? revertCancellation
													: undefined
											}
										/>
										<UpdateToLatestLink
											policy={cancellationPolicy}
											heads={policyHeads.cancellation}
											onUpdate={(latest) =>
												changeCancellation(latest as TCancellationPolicy)
											}
										/>
									</Stack>
									<Stack gap="1">
										<PolicySelect
											kind="payment"
											selectKey={hotel.id}
											maxWidth="sm"
											policy={paymentPolicy}
											options={paymentOptions}
											heads={policyHeads.payment}
											isOverridden={Boolean(paymentIsManual)}
											onChange={(next) =>
												changePayment((next as TPaymentPolicy) || null)
											}
											onClear={
												paymentIsManual ? clearPaymentOverride : undefined
											}
											onRevert={
												paymentChangedSinceSave ? revertPayment : undefined
											}
										/>
										<UpdateToLatestLink
											policy={paymentPolicy}
											heads={policyHeads.payment}
											onUpdate={(latest) =>
												changePayment(latest as TPaymentPolicy)
											}
										/>
									</Stack>
								</>
							) : (
								<>
									<Stack gap="1">
										<PolicyChip
											kind="cancellation"
											policy={cancellationPolicy}
											isStale={isPolicyStale(
												cancellationPolicy,
												policyHeads.cancellation
											)}
										/>
										<UpdateToLatestLink
											policy={cancellationPolicy}
											heads={policyHeads.cancellation}
											onUpdate={(latest) =>
												changeCancellation(latest as TCancellationPolicy)
											}
										/>
									</Stack>
									<Stack gap="1">
										<PolicyChip
											kind="payment"
											policy={paymentPolicy}
											isStale={isPolicyStale(
												paymentPolicy,
												policyHeads.payment
											)}
										/>
										<UpdateToLatestLink
											policy={paymentPolicy}
											heads={policyHeads.payment}
											onUpdate={(latest) =>
												changePayment(latest as TPaymentPolicy)
											}
										/>
									</Stack>
								</>
							)}
						</Inline>
					</Inline>
					<Button onClick={() => setState(!state)} level="secondary" size="sm">
						{state ? "Simple View" : "Override Policies"}
					</Button>
				</Inline>
			)}
		</Component>
	)
}

function HotelRateForm({
	hotel,
	otherSeasons,
	onCopyRates,
	copyRatesTick,
	hotelErrors,
	onFieldBlur,
	policyOptions,
	blockPolicies,
	parentMealPlans,
	parentMealRates,
	parentRates,
	parentPaxTypes,
	parentRooms,
	parentRoundingPoint,
	parentIsLowerOccupancyRateConfigured,
	initialBlockRates,
	initialBlockMealRates,
	initialBlockPaxTypes,
	initialBlockRooms,
	childRateSameAsAdult,
	onUpdateMealPlans,
	onUpdateRooms,
	onUpdateMealRate,
	onUpdateRatesAndPaxTypes,
}: {
	hotel: IHotel
	otherSeasons: { id: number; name: string }[]
	onCopyRates: (fromIdx: number) => void
	copyRatesTick: number
	hotelErrors?: THotelRateErrors
	onFieldBlur: () => void
	policyOptions: TSheetPolicyOptions
	blockPolicies: TBlockPolicyContext
	parentMealPlans: IRateBlockSchema["meal_plans"]
	parentMealRates: IRateBlockSchema["meal_rates"]
	parentRates: IRateBlockSchema["rates"]
	parentPaxTypes: IRateBlockSchema["pax_types"]
	parentRooms: IRateBlockSchema["rooms"]
	parentRoundingPoint?: IRateBlockSchema["rounding_point"]
	parentIsLowerOccupancyRateConfigured?: boolean
	initialBlockRates: IRateBlockSchema["rates"]
	initialBlockMealRates?: IRateBlockSchema["meal_rates"]
	initialBlockPaxTypes?: IRateBlockSchema["pax_types"]
	initialBlockRooms?: IRateBlockSchema["rooms"]
	childRateSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	onUpdateMealPlans: (updated: IRateBlockSchema["meal_plans"]) => void
	onUpdateRooms: (rooms: IRateBlockSchema["rooms"]) => void
	onUpdateMealRate: (
		mealRates: IRateBlockSchema["meal_rates"],
		childSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	) => void
	onUpdateRatesAndPaxTypes: (
		rates: IRateBlockSchema["rates"],
		paxTypes: IRateBlockSchema["pax_types"],
		isLowerOccupancyConfigured?: boolean
	) => void
}) {
	const mealPlansRef = useScrollOnFocus<HTMLDivElement>("mealPlans")
	const isMobile = useIsMobile()

	const [selectedSeason, setSelectedSeason] = useState<number | null>(null)
	const [isCopyRatesOpen, setIsCopyRatesOpen] = useState(false)

	const {
		hotel: freshHotel,
		fetchHotel: refetchHotel,
		isFetching,
		isRefreshing,
	} = useHotel(hotel.id)
	const activeHotel = freshHotel || hotel

	const roomTypes = useMemo(
		() => activeHotel.ota_room_types || [],
		[activeHotel.ota_room_types]
	)
	const mealPlans = useMemo(
		() => activeHotel.ota_meal_plans || [],
		[activeHotel.ota_meal_plans]
	)
	const roomCols = useMemo(() => buildRoomCols(activeHotel), [activeHotel])

	// Whether this block has been started at all. The first room type's base rate
	// is the first thing anyone fills, so pricing it is what turns the copy rates
	// banner from a helpful offer into a bar across a grid the user is working in.
	const hasStartedPricing = useMemo(() => {
		const firstRoomType = roomTypes[0]
		if (!firstRoomType) return false
		const baseMealPlanId = splitMealPlans(parentMealPlans, activeHotel)
			.baseMealPlan?.id
		return (parentRates || []).some(
			(rate) =>
				Number(rate.ota_room_type) === Number(firstRoomType.id) &&
				(rate.occupancy || "").toLowerCase() === "base" &&
				(baseMealPlanId == null ||
					Number(rate.ota_meal_plan) === Number(baseMealPlanId)) &&
				isNumeric(rate.rate_before_tax)
		)
	}, [roomTypes, parentRates, parentMealPlans, activeHotel])

	const canCopyRates = otherSeasons.length > 0
	const openCopyRates = useCallback(() => {
		setSelectedSeason(null)
		setIsCopyRatesOpen(true)
	}, [])

	// Each room's occupancy config is not user editable — it mirrors the hotel's
	// OTA room types. *Which* rooms are in the list is the user's though: removing
	// a room from the rate grid drops it from here, and that is what makes the
	// removal survive a season or hotel switch. So seed the full list the first
	// time the room types land (the hotel prop reaches this card before
	// `useHotel` resolves), and from then on only refresh the config of the rooms
	// the block still prices — rebuilding from `roomTypes` wholesale, as this
	// used to, silently restores every removed room on the next refetch.
	const lastRoomsRef = useRef<string | null>(null)
	useEffect(() => {
		if (!roomTypes.length) return
		const fresh = buildRooms(roomTypes)
		const next = parentRooms?.length
			? fresh.filter((room) =>
					parentRooms.some(
						(existing) =>
							Number(existing.ota_room_type) === Number(room.ota_room_type)
					)
				)
			: fresh
		const serialized = JSON.stringify(next)
		if (serialized === lastRoomsRef.current) return
		lastRoomsRef.current = serialized
		onUpdateRooms(next)
	}, [roomTypes, parentRooms, onUpdateRooms])

	// Only against the fetched hotel: the `hotel` prop is the list record, which
	// carries no meal plans, so pruning against it would drop every non-base plan.
	useEffect(() => {
		if (!freshHotel?.ota_meal_plans || isFetching) return
		const pruned = pruneDeletedMealPlanEntries(
			parentMealPlans,
			freshHotel.ota_meal_plans
		)
		if (pruned !== parentMealPlans) onUpdateMealPlans(pruned)
	}, [freshHotel, isFetching, parentMealPlans, onUpdateMealPlans])

	useEffect(() => {
		if (!mealPlans || mealPlans.length === 0) return

		const hasBase = (parentMealPlans || [])?.some((mt) => isTruthy(mt?.is_base))

		if (!hasBase) {
			let defaultMealPlan: THotelOtaMealPlan | undefined

			for (const cat of DEFAULT_BASE_MEAL_PLAN_ORDER) {
				const found = mealPlans.find(
					(mp) =>
						(mp.meal_category || "").trim().toUpperCase() === cat ||
						(mp.name || "").trim().toUpperCase() === cat
				)
				if (found) {
					defaultMealPlan = found
					break
				}
			}

			if (!defaultMealPlan && mealPlans.length > 0) {
				defaultMealPlan = mealPlans[0]
			}

			if (defaultMealPlan) {
				const newItem: IRateBlockSchema["meal_plans"][number] = {
					ota_meal_plan: defaultMealPlan,
					category: defaultMealPlan.meal_category || undefined,
					is_base: true,
					includes_all_room_types: true,
					ota_room_types: [],
				}
				onUpdateMealPlans([...(parentMealPlans || []), newItem])
			}
		}
	}, [mealPlans, parentMealPlans, onUpdateMealPlans])

	return (
		<Stack gap="8" padding="6">
			<RateBlockPoliciesSection
				hotel={activeHotel}
				policyOptions={policyOptions}
				blockPolicies={blockPolicies}
				onFieldBlur={onFieldBlur}
				isMobile={isMobile}
			/>
			<Stack gap="12">
				<Stack gap="4">
					<Inline justifyContent="between">
						<Text fontWeight="semibold" fontSize="lg" color="primary">
							Rates
						</Text>
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary" size="sm">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{canCopyRates && hasStartedPricing ? (
									<Dropdown.MenuItem
										onClick={(e) => {
											// The item is an `<a href="#">`: left alone, the click
											// navigates to `#`, which the router sees as a POP with no
											// location state and the wizard form reinitialises.
											e.preventDefault()
											openCopyRates()
										}}
									>
										<Icons.ClipboardCopy /> Copy Rates from
									</Dropdown.MenuItem>
								) : null}
								<Dropdown.MenuItem
									onClick={(e) => {
										e.preventDefault()
										refetchHotel()
									}}
								>
									<Icons.Refresh
										animation={isFetching || isRefreshing ? "spin" : undefined}
									/>{" "}
									Refresh Rooms/Meals
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
					{canCopyRates && !hasStartedPricing ? (
						<Box
							paddingY="2"
							paddingX="4"
							borderWidth="1"
							rounded="md"
							bgColor="primary"
							borderColor="primary"
						>
							<Inline justifyContent="between" alignItems="center">
								<Inline gap="2" alignItems="center">
									<Box>
										<Icons.ClipboardCopy color="primary" size="6" />
									</Box>
									<Text color="primary">
										You can copy rates here from another season
									</Text>
								</Inline>
								<Button level="primary" onClick={openCopyRates}>
									Copy Rates from
								</Button>
							</Inline>
						</Box>
					) : null}
					{canCopyRates ? (
						<Dialog
							open={isCopyRatesOpen}
							onClose={() => setIsCopyRatesOpen(false)}
							title="Copy Rates from"
							sm
						>
							<DialogBody>
								<Stack gap="1">
									<Text fontWeight="semibold">Copy Rates from</Text>
									<Select
										value={
											otherSeasons.find((s) => s.id === selectedSeason) || null
										}
										onChange={(val) => setSelectedSeason(val?.id ?? null)}
										options={otherSeasons}
										placeholder="Select"
									/>
								</Stack>
							</DialogBody>
							<DialogFooter>
								<Inline justifyContent="end">
									<Button
										level="primary"
										disabled={selectedSeason === null}
										onClick={() => {
											if (selectedSeason !== null) {
												onCopyRates(selectedSeason)
												setIsCopyRatesOpen(false)
											}
										}}
										style={{ opacity: selectedSeason === null ? 0.5 : 1 }}
									>
										Update
									</Button>
								</Inline>
							</DialogFooter>
						</Dialog>
					) : null}
					<Inline>
						<SelectField
							select={Select}
							label="Round up to"
							name="rounding_point"
							placeholder="Select rounding"
							onBlur={onFieldBlur}
							maxWidth="xs"
							options={roudingOptions}
						/>
					</Inline>
				</Stack>
				{!isFetching ? (
					<>
						<Inline gap="6" flexWrap="wrap">
							{mealPlans.length > 0 ? (
								<Stack
									gap="3"
									flex="1"
									maxWidth="lg"
									ref={mealPlansRef}
									style={SCROLL_TARGET_STYLE}
								>
									<Box paddingX="4">
										<Text fontWeight="semibold" fontSize="md">
											Applicable Meal Plans
										</Text>
									</Box>
									<Stack
										borderWidth="1"
										borderColor="emphasis"
										rounded="lg"
										bgColor="white"
									>
										<MealPlanForm
											hotelId={hotel.id}
											mealPlans={mealPlans}
											parentMealPlans={parentMealPlans || []}
											onUpdateMealPlans={onUpdateMealPlans}
										/>
									</Stack>
								</Stack>
							) : (
								<Text color="muted" fontSize="md" fontWeight="semibold">
									No meal plans available for this hotel
								</Text>
							)}
							<MealRatesSection
								currency={hotel.currency}
								parentMealRates={parentMealRates}
								selectedMealPlans={parentMealPlans}
								initialBlockMealRates={initialBlockMealRates}
								childRateSameAsAdult={childRateSameAsAdult}
								onUpdateMealRate={onUpdateMealRate}
								onFieldBlur={onFieldBlur}
							/>
						</Inline>
						{roomTypes.length > 0 ? (
							<RoomRatesSection
								key={`${hotel.id}_${copyRatesTick}`}
								hotel={activeHotel}
								roomCols={roomCols}
								roomTypes={roomTypes}
								hotelErrors={hotelErrors}
								parentMealPlans={parentMealPlans}
								parentMealRates={parentMealRates}
								parentRates={parentRates}
								parentPaxTypes={parentPaxTypes}
								parentRooms={parentRooms}
								parentRoundingPoint={parentRoundingPoint}
								parentIsLowerOccupancyRateConfigured={
									parentIsLowerOccupancyRateConfigured
								}
								initialBlockRates={initialBlockRates}
								initialBlockPaxTypes={initialBlockPaxTypes}
								initialBlockRooms={initialBlockRooms}
								childRateSameAsAdult={childRateSameAsAdult}
								onUpdateRooms={onUpdateRooms}
								onUpdateRatesAndPaxTypes={onUpdateRatesAndPaxTypes}
								onFieldBlur={onFieldBlur}
							/>
						) : (
							<Text color="muted" fontSize="md" fontWeight="semibold">
								No Room Types available for this hotel
							</Text>
						)}
					</>
				) : (
					<Spinner />
				)}
			</Stack>
		</Stack>
	)
}

export function MealPlanForm({
	hotelId,
	mealPlans,
	parentMealPlans,
	onUpdateMealPlans,
}: {
	hotelId: number
	mealPlans: THotelOtaMealPlan[]
	parentMealPlans: IRateBlockSchema["meal_plans"]
	onUpdateMealPlans: (updated: IRateBlockSchema["meal_plans"]) => void
}) {
	const [localMealPlans, setLocalMealPlans] =
		useState<IRateBlockSchema["meal_plans"]>(parentMealPlans)

	const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const onUpdateMealPlansRef = useRef(onUpdateMealPlans)
	onUpdateMealPlansRef.current = onUpdateMealPlans

	const isInternalUpdateRef = useRef(false)
	useEffect(() => {
		if (isInternalUpdateRef.current) {
			isInternalUpdateRef.current = false
			return
		}
		setLocalMealPlans(parentMealPlans)
	}, [parentMealPlans])

	useEffect(() => {
		return () => {
			if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current)
		}
	}, [])

	function flush(updated: IRateBlockSchema["meal_plans"]) {
		isInternalUpdateRef.current = true
		setLocalMealPlans(updated)
		if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current)
		flushTimerRef.current = setTimeout(() => {
			flushTimerRef.current = null
			onUpdateMealPlansRef.current(updated)
		}, 0)
	}

	const listedMealPlans = mealPlansWithSavedBase(mealPlans, localMealPlans)
	const mealPlansCount = listedMealPlans.length

	return listedMealPlans.map((mealPlan, idx) => {
		const foundIndex = localMealPlans.findIndex(
			(item) =>
				Number(item?.ota_meal_plan?.id ?? item?.ota_meal_plan) ===
				Number(mealPlan.id)
		)
		const isActive = foundIndex !== -1
		const activeItem = isActive ? localMealPlans[foundIndex] : null
		const isBase = activeItem?.is_base === true
		return (
			<Stack
				key={mealPlan.id}
				borderBottomWidth={mealPlansCount - 1 === idx ? "0" : "1"}
				borderBottomColor="muted"
				bgColor={isBase ? "success" : undefined}
				roundedTop={idx === 0 ? "lg" : undefined}
				roundedBottom={mealPlansCount - 1 === idx ? "lg" : undefined}
			>
				<Inline
					gap="4"
					justifyContent="between"
					alignItems="start"
					paddingX="4"
					paddingY="3"
				>
					<Stack gap="2">
						<Inline gap="3" as="label" alignItems="start">
							<Box>
								<SwitchInput
									id={`${hotelId}_mp_${mealPlan.id}`}
									checked={isActive}
									style={
										isBase
											? {
													accentColor: themeContractVars.bg.success_emphasis,
													pointerEvents: "auto",
													cursor: "not-allowed",
												}
											: undefined
									}
									onChange={(checked) => {
										if (isBase) return

										if (checked) {
											const newItem = {
												ota_meal_plan: mealPlan,
												category: mealPlan.meal_category || undefined,
												is_base: false,
												includes_all_room_types: true,
												ota_room_types: [],
											}
											flush([...localMealPlans, newItem])
										} else {
											const updated = localMealPlans.filter(
												(item) =>
													Number(
														item?.ota_meal_plan?.id ?? item?.ota_meal_plan
													) !== Number(mealPlan.id)
											)
											flush(updated)
										}
									}}
								/>
							</Box>
							<Stack gap="1">
								<Inline alignItems="center" gap="3" title={mealPlan.name}>
									<Text
										textOverflow="truncate"
										maxWidth="xs"
										fontWeight="semibold"
									>
										{mealPlan.name}
									</Text>
									{mealPlan.meal_category ? (
										<Badge>{mealPlan.meal_category}</Badge>
									) : null}
								</Inline>
								{isActive && !isBase ? (
									<Box fontSize="sm" fontWeight="semibold">
										<RoomsMultiSelect
											selectedRooms={activeItem?.ota_room_types || []}
											roomTypes={mealPlan.ota_room_types}
											onChange={(newRooms, allSelected) => {
												const updated = localMealPlans.map((item, index) => {
													if (index === foundIndex) {
														return {
															...item,
															ota_room_types: newRooms,
															includes_all_room_types: allSelected,
														}
													}
													return item
												})
												flush(updated)
											}}
										/>
									</Box>
								) : null}
							</Stack>
						</Inline>
						{isBase ? (
							<Text color="success" fontSize="sm" fontWeight="normal">
								Enter this plan's price in the main room row, other plans derive
								from it.
							</Text>
						) : null}
					</Stack>

					{isActive ? (
						isBase ? (
							<Badge success>★ Base</Badge>
						) : (
							<Inline gap="4" alignItems="center">
								<Box
									fontSize="sm"
									fontWeight="semibold"
									color="muted"
									cursor="pointer"
									whiteSpace="nowrap"
									onClick={() => {
										let found = false

										const updated = localMealPlans.map((item) => {
											const isThisBase =
												Number(
													item?.ota_meal_plan?.id ?? item?.ota_meal_plan
												) === Number(mealPlan.id)
											if (isThisBase) found = true
											return {
												...item,
												is_base: isThisBase,
												includes_all_room_types: isThisBase
													? true
													: item.includes_all_room_types,
												ota_room_types: isThisBase ? [] : item.ota_room_types,
											}
										})

										if (!found) {
											updated.push({
												ota_meal_plan: mealPlan,
												category: mealPlan.meal_category || undefined,
												is_base: true,
												includes_all_room_types: true,
												ota_room_types: [],
											})
										}

										flush(updated)
									}}
								>
									Set as base
								</Box>
							</Inline>
						)
					) : null}
				</Inline>
			</Stack>
		)
	})
}

// Syncing child meal rate with adult meal rate.
function SyncChildRate({
	prefix,
	same,
	adultRate,
}: {
	prefix: string
	same: boolean
	adultRate: number | null
}) {
	const { onChange: setChildRate } = useFieldValue<number | null>(
		`${prefix}.per_child_rate_before_tax`
	)
	useEffect(() => {
		if (same) {
			setChildRate(adultRate)
		}
	}, [same, adultRate, setChildRate])
	return null
}

function SyncMealRatesFormValues({
	parentMealRates,
}: {
	parentMealRates: IRateBlockSchema["meal_rates"]
}) {
	const form = useForm()
	const prevRef = useRef(parentMealRates)

	useEffect(() => {
		const currentStr = JSON.stringify(parentMealRates)
		const prevStr = JSON.stringify(prevRef.current)
		if (currentStr !== prevStr) {
			prevRef.current = parentMealRates
			form.change("meal_rates", parentMealRates)
		}
	}, [parentMealRates, form])

	return null
}

function SyncChildMealRateSameAsAdult({
	sameAsAdult,
	values,
	onUpdateMealRate,
	onFieldBlur,
}: {
	sameAsAdult: boolean
	values: {
		meal_rates: IRateBlockSchema["meal_rates"]
		child_meal_rate_same_as_adult: boolean
	}
	onUpdateMealRate: (
		mealRates: IRateBlockSchema["meal_rates"],
		childSameAsAdult: boolean
	) => void
	onFieldBlur?: () => void
}) {
	const lastSameRef = useRef(sameAsAdult)
	const isFirstRef = useRef(true)

	useEffect(() => {
		if (isFirstRef.current) {
			isFirstRef.current = false
			return
		}
		if (lastSameRef.current !== sameAsAdult) {
			lastSameRef.current = sameAsAdult
			const updatedRates = sameAsAdult
				? (values.meal_rates || []).map((mr) => ({
						...mr,
						per_child_rate_before_tax: mr?.per_adult_rate_before_tax ?? null,
					}))
				: values.meal_rates
			onUpdateMealRate(updatedRates, sameAsAdult)
			if (onFieldBlur) onFieldBlur()
		}
	}, [sameAsAdult, values.meal_rates, onUpdateMealRate, onFieldBlur])

	return null
}

function MealRateCellChangeDot({
	name,
	initialValue,
	hasBaseline,
}: {
	name: string
	initialValue?: number | string | null
	// False while creating a sheet, where there is no saved version to diff
	// against and every rate typed in would otherwise read as a change
	hasBaseline: boolean
}) {
	const { value: currentVal } = useFieldValue<number | string | null>(name)

	const initNum =
		initialValue !== null &&
		initialValue !== undefined &&
		(initialValue as unknown) !== ""
			? Number(initialValue)
			: null
	const currNum =
		currentVal !== null &&
		currentVal !== undefined &&
		(currentVal as unknown) !== ""
			? Number(currentVal)
			: null

	const isChanged =
		(initNum === null && currNum !== null) ||
		(initNum !== null && currNum === null) ||
		(initNum !== null && currNum !== null && initNum !== currNum)

	if (!hasBaseline || !isChanged) return null

	return (
		<Box
			style={{
				position: "absolute",
				top: "-8px",
				right: "0px",
				width: "8px",
				height: "8px",
				borderRadius: "50%",
				backgroundColor: "#0075ff",
				pointerEvents: "none",
				zIndex: 0,
			}}
		/>
	)
}

export function MealRatesSection({
	currency,
	parentMealRates,
	selectedMealPlans,
	initialBlockMealRates,
	childRateSameAsAdult,
	onUpdateMealRate,
	onFieldBlur,
}: {
	currency: string
	parentMealRates: IRateBlockSchema["meal_rates"]
	selectedMealPlans: IRateBlockSchema["meal_plans"]
	initialBlockMealRates?: IRateBlockSchema["meal_rates"]
	childRateSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	onUpdateMealRate: (
		mealRates: IRateBlockSchema["meal_rates"],
		childSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	) => void
	onFieldBlur?: () => void
}) {
	const sectionRef = useScrollOnFocus<HTMLDivElement>("mealRates")
	const mealCourses = useMemo(() => {
		return getMealCourses(selectedMealPlans)
	}, [selectedMealPlans])

	const syncedParentMealRates = useMemo(() => {
		return syncBlockMealRates(selectedMealPlans, parentMealRates)
	}, [selectedMealPlans, parentMealRates])

	// Reordered onto the current course list so index N here lines up with the
	// row at index N. Its entries stay present but null when there is no
	// baseline — handing `undefined` to RateInputField instead would switch it
	// over to `meta.initial`, which this form re-seeds from live values on every
	// flush, and the revert button would start pointing at whatever was typed
	// last rather than at the saved rate.
	const initialSyncedMealRates = useMemo(
		() => syncBlockMealRates(selectedMealPlans, initialBlockMealRates),
		[selectedMealPlans, initialBlockMealRates]
	)

	// Nothing to diff against on a sheet being created from scratch
	const hasMealRateBaseline = useMemo(
		() =>
			initialSyncedMealRates.some(
				(mr) =>
					mr?.per_adult_rate_before_tax != null ||
					mr?.per_child_rate_before_tax != null
			),
		[initialSyncedMealRates]
	)

	// Meal rates price the gap between the base plan and the others. With only
	// the base plan selected nothing derives from them, so there is nothing to
	// ask for — see `blockNeedsMealRates`.
	if (!blockNeedsMealRates(selectedMealPlans) || mealCourses.length === 0) {
		return null
	}

	return (
		<Form<{
			meal_rates: IRateBlockSchema["meal_rates"]
			child_meal_rate_same_as_adult: boolean
		}>
			initialValues={{
				meal_rates: syncedParentMealRates,
				child_meal_rate_same_as_adult: childRateSameAsAdult ?? true,
			}}
			onSubmit={() => undefined}
			validate={(values) => {
				const errors: Record<string, unknown> = {}
				const mealRatesErrors: Record<string, string>[] = []
				let hasMealError = false

				if (values.meal_rates) {
					values.meal_rates.forEach((mr, idx) => {
						if (idx >= mealCourses.length) return
						const errs: Record<string, string> = {}
						if (!isNumeric(mr?.per_adult_rate_before_tax)) {
							errs.per_adult_rate_before_tax = "Required"
							hasMealError = true
						} else if (Number(mr?.per_adult_rate_before_tax) < 0) {
							errs.per_adult_rate_before_tax = "Must be a positive value"
							hasMealError = true
						}
						if (!values.child_meal_rate_same_as_adult) {
							if (!isNumeric(mr?.per_child_rate_before_tax)) {
								errs.per_child_rate_before_tax = "Required"
								hasMealError = true
							} else if (Number(mr?.per_child_rate_before_tax) < 0) {
								errs.per_child_rate_before_tax = "Must be a positive value"
								hasMealError = true
							}
						}
						if (Object.keys(errs).length > 0) {
							mealRatesErrors[idx] = errs
						}
					})
				}

				if (hasMealError) {
					errors.meal_rates = mealRatesErrors
				}
				return errors
			}}
		>
			{({ values, errors, form }) => {
				const sameAsAdult = !!values.child_meal_rate_same_as_adult
				const mealRatesErrors = errors?.meal_rates as
					| Array<Record<string, string> | undefined>
					| undefined
				const missingCount = (mealRatesErrors || []).filter(
					(err) => err && Object.keys(err).length > 0
				).length

				// Reverting a rate writes to this form from a click on the revert
				// icon, not from the input, so the form's `onBlur` either never fires
				// or fires ahead of the revert and carries the value being reverted
				// away. Pushed on the next tick so `SyncChildRate` has settled the
				// child rate that follows the adult one, and read off the live form
				// rather than this render's `values`, which predate the revert.
				const pushMealRates = () => {
					setTimeout(() => {
						const latest = form.getState().values
						onUpdateMealRate(
							latest.meal_rates,
							latest.child_meal_rate_same_as_adult
						)
						if (onFieldBlur) onFieldBlur()
					}, 0)
				}

				const adultWarnings = getHierarchyWarnings(
					mealCourses,
					values.meal_rates,
					(r) => r?.per_adult_rate_before_tax,
					(r) => r?.meal_type
				)
				const childHierarchyWarnings = sameAsAdult
					? {}
					: getHierarchyWarnings(
							mealCourses,
							values.meal_rates,
							(r) => r?.per_child_rate_before_tax,
							(r) => r?.meal_type
						)

				const childWarnings: Record<number, string> = {
					...childHierarchyWarnings,
				}

				if (!sameAsAdult && values.meal_rates) {
					mealCourses.forEach((_course, idx) => {
						const mr = values.meal_rates[idx]
						// `toNum` already folds null/undefined/"" into null, which is what
						// the empty-string comparisons were reaching for — they only
						// needed a cast because the field type does not admit "".
						const adultNum = toNum(mr?.per_adult_rate_before_tax)
						const childNum = toNum(mr?.per_child_rate_before_tax)
						if (adultNum != null && childNum != null && childNum > adultNum) {
							childWarnings[idx] = "Child rate is more than adult rate"
						}
					})
				}

				return (
					<form
						noValidate
						onBlur={() => {
							onUpdateMealRate(
								values.meal_rates,
								values.child_meal_rate_same_as_adult
							)
							if (onFieldBlur) onFieldBlur()
						}}
					>
						<SyncMealRatesFormValues parentMealRates={syncedParentMealRates} />
						<SyncChildMealRateSameAsAdult
							sameAsAdult={sameAsAdult}
							values={values}
							onUpdateMealRate={onUpdateMealRate}
							onFieldBlur={onFieldBlur}
						/>
						<Stack gap="3" ref={sectionRef} style={SCROLL_TARGET_STYLE}>
							<Inline justifyContent="between">
								<Inline paddingX="4" gap="1">
									<Text fontWeight="semibold" fontSize="md">
										Meal Rates
									</Text>
									<MiniCurrency currency={currency} />
									<Text color="muted">(before taxes)</Text>
								</Inline>
								{missingCount > 0 ? (
									<Inline gap="1" alignItems="center">
										<Icons.CircleAlert color="danger" />
										<Text color="danger" fontSize="sm" fontWeight="semibold">
											{missingCount} Meal Rates are missing
										</Text>
									</Inline>
								) : null}
							</Inline>
							<Box
								rounded="lg"
								borderWidth="1"
								borderColor="emphasis"
								overflow="hidden"
							>
								<Table
									headerVerticalAlign="top"
									headerBgColor="subtle"
									responsive={true}
									headers={[
										"Meal",
										<Box
											paddingX="4"
											style={{ minWidth: "4rem" }}
											textAlign="right"
										>
											Per Adult
										</Box>,
										<Stack style={{ minWidth: "4rem" }}>
											<Box paddingLeft="4">Per Child</Box>
											<SwitchInputField
												name="child_meal_rate_same_as_adult"
												alignLabelCenter
												label={
													<Text
														fontSize="xs"
														fontWeight="normal"
														whiteSpace="nowrap"
													>
														Same as Adult
													</Text>
												}
											/>
										</Stack>,
									]}
									rows={mealCourses.map((course, idx) => [
										<Inline
											style={{ minWidth: "150px" }}
											key={mealCourseKey(course)}
										>
											<Text>
												{course.name.charAt(0).toUpperCase() +
													course.name.slice(1)}
											</Text>
											{course.ota_meal_plan == null ? (
												<Box display="none">
													<TextInputField
														name={`meal_rates[${idx}].meal_type`}
														defaultValue={course.name}
													/>
												</Box>
											) : null}
										</Inline>,
										<Box
											key={`adult_${mealCourseKey(course)}`}
											position="relative"
										>
											<MealRateCellChangeDot
												name={`meal_rates[${idx}].per_adult_rate_before_tax`}
												initialValue={
													initialSyncedMealRates[idx]?.per_adult_rate_before_tax
												}
												hasBaseline={hasMealRateBaseline}
											/>
											<RateInputField
												type="meal"
												name={`meal_rates[${idx}].per_adult_rate_before_tax`}
												initialValue={
													initialSyncedMealRates[idx]?.per_adult_rate_before_tax
												}
												warning={adultWarnings[idx]}
												onRevert={pushMealRates}
											/>
										</Box>,
										<Box
											key={`child_${mealCourseKey(course)}`}
											position="relative"
										>
											<MealRateCellChangeDot
												name={`meal_rates[${idx}].per_child_rate_before_tax`}
												initialValue={
													initialSyncedMealRates[idx]?.per_child_rate_before_tax
												}
												hasBaseline={hasMealRateBaseline}
											/>
											<RateInputField
												type="meal"
												name={`meal_rates[${idx}].per_child_rate_before_tax`}
												initialValue={
													initialSyncedMealRates[idx]?.per_child_rate_before_tax
												}
												onRevert={pushMealRates}
												disabled={sameAsAdult}
												warning={childWarnings[idx]}
											/>
										</Box>,
									])}
								/>
							</Box>

							{mealCourses.map((course, idx) => (
								<GetFieldValue<number | null>
									name={`meal_rates[${idx}].per_adult_rate_before_tax`}
									key={`sync_${mealCourseKey(course)}`}
								>
									{({ value: adultRate }) => (
										<SyncChildRate
											prefix={`meal_rates[${idx}]`}
											same={sameAsAdult}
											adultRate={adultRate}
										/>
									)}
								</GetFieldValue>
							))}
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

// A rate input for both the meal rates table and the room rates grid. It
// subscribes to its own field only — anything that depends on other cells
// (warnings, the manual flag) is handed in, since every extra subscription per
// cell is paid for across the whole grid when final-form registers fields.
function RateInputField({
	name,
	type = "room",
	disabled,
	warning,
	isDerived = false,
	isManual = false,
	showChangeDot = false,
	initialValue,
	onChange,
	onManualEdit,
	onResetManual,
	onRevert,
}: {
	name: string
	type?: "meal" | "room"
	disabled?: boolean
	warning?: string
	isDerived?: boolean
	isManual?: boolean
	// Marks the cell once its value moves off `initialValue`. Off while creating
	// a sheet, where there is no saved version to diff against.
	showChangeDot?: boolean
	initialValue?: number | string | null
	onChange?: (e: unknown) => void
	// Called only when the user types into the input — not on a revert
	onManualEdit?: () => void
	onResetManual?: () => void
	onRevert?: () => void
}) {
	const { input, meta } = useField(name, {
		subscription: { value: true, error: true, touched: true, initial: true },
	})
	const initialVal = initialValue !== undefined ? initialValue : meta.initial
	const hasInitialValue =
		initialVal !== null &&
		initialVal !== undefined &&
		(initialVal as unknown) !== ""

	const isDifferentFromInitial = (() => {
		if (!hasInitialValue) return false
		const initNum = Number(initialVal)
		if (isNaN(initNum)) return false

		const currVal = input.value
		const hasCurrValue =
			currVal !== null && currVal !== undefined && (currVal as unknown) !== ""
		if (!hasCurrValue) return true

		const currNum = Number(currVal)
		if (isNaN(currNum)) return true

		return initNum !== currNum
	})()

	const isChangedFromBaseline = (() => {
		if (!showChangeDot) return false
		const initNum =
			initialValue !== null && initialValue !== undefined
				? Number(initialValue)
				: null
		const currNum =
			input.value !== null &&
			input.value !== undefined &&
			(input.value as unknown) !== ""
				? Number(input.value)
				: null
		return (
			(initNum === null && currNum !== null) ||
			(initNum !== null && currNum === null) ||
			(initNum !== null && currNum !== null && initNum !== currNum)
		)
	})()

	const isDerivedValue = isDerived && !isManual
	const hasError = meta.error

	const borderColor = hasError
		? "danger_emphasis"
		: disabled
			? { default: "transparent" }
			: isManual
				? "warning_emphasis"
				: warning && !isDerived
					? "warning_emphasis"
					: isDerivedValue
						? {
								default: "transparent",
								hover: "muted",
								focus: "primary_emphasis",
							}
						: undefined

	const bgColor = hasError
		? "danger"
		: warning && !isDerived
			? "warning"
			: isManual
				? "warning"
				: "transparent"

	return (
		<Inline alignItems="center" gap="1">
			{isChangedFromBaseline ? (
				<Box
					style={{
						position: "absolute",
						top: "8px",
						right: "8px",
						width: "8px",
						height: "8px",
						borderRadius: "50%",
						backgroundColor: "#0075ff",
						pointerEvents: "none",
						zIndex: 0,
					}}
				/>
			) : null}
			<TextInputField
				type="number"
				name={name}
				disabled={disabled}
				hasError={Boolean(hasError)}
				bgColor={bgColor}
				borderWidth="1"
				borderColor={borderColor as BoxOwnProps["borderColor"]}
				color={disabled ? "muted" : undefined}
				textAlign="right"
				className={noSpinnersClassName}
				style={{
					maxWidth: "7rem",
					maxHeight: type === "meal" ? "1.75rem" : "1.5rem",
				}}
				hideErrors
				onChange={(e: unknown) => {
					input.onChange(e)
					if (onManualEdit) onManualEdit()
					if (onChange) onChange(e)
				}}
				fontSize={type === "meal" ? undefined : "sm"}
			/>
			{isManual && isDerived ? (
				<Box
					cursor="pointer"
					title="Reset to derived value"
					onClick={onResetManual}
					style={{ display: "inline-flex", alignItems: "center" }}
				>
					<Icons.Cancel size="4" color="muted" />
				</Box>
			) : null}
			{hasInitialValue && isDifferentFromInitial ? (
				<Box
					cursor="pointer"
					title="Revert to initial value"
					onClick={() => {
						input.onChange(initialVal)
						if (onRevert) {
							onRevert()
						}
						if (onChange) {
							onChange(initialVal)
						}
					}}
					style={{
						display: "inline-flex",
						alignItems: "center",
						color: "#00749E",
					}}
				>
					<Icons.Rotate size="3" />
				</Box>
			) : null}
			{warning ? (
				<Tooltip
					content={
						<Text fontSize="sm">
							<Icons.Attention color="warning_light" /> {warning}
						</Text>
					}
				>
					<Box paddingLeft="1">
						<Icons.Attention color="warning_light" size="4" />
					</Box>
				</Tooltip>
			) : null}
		</Inline>
	)
}

const FORMULA_INPUT_STYLE: React.CSSProperties = {
	minWidth: "1.5ch",
	fontSize: "0.75rem",
	fontWeight: 500,
	textAlign: "center",
	padding: "1px 2px",
	borderRadius: "0.25rem",
	borderWidth: "1px",
	borderStyle: "solid",
	outline: "none",
	transition:
		"border-color 120ms ease, background-color 120ms ease, color 120ms ease",
}

function FormulaNumberInput({
	value,
	active,
	title,
	onChange,
	onFocusChange,
}: {
	value: number
	// Whether the formula row is being hovered or edited — the row owns this so
	// both of its inputs reveal their box together.
	active: boolean
	title: string
	onChange: (val: number) => void
	onFocusChange: (focused: boolean) => void
}) {
	const [isFocused, setIsFocused] = useState(false)
	// A partly typed number — "", "-", "1." — is not one the formula can hold, so
	// the field keeps the raw text while it has focus and only commits the value
	// once it parses. Without this, deleting the last digit snaps the field back.
	const [draft, setDraft] = useState<string | null>(null)
	const text = draft ?? String(value)

	return (
		<input
			type="number"
			step="any"
			title={title}
			aria-label={title}
			className={noSpinnersClassName}
			value={text}
			onChange={(e) => {
				const raw = e.target.value
				setDraft(raw)
				const parsed = parseFloat(raw)
				if (!isNaN(parsed) && parsed !== value) {
					onChange(parsed)
				}
			}}
			onFocus={() => {
				setIsFocused(true)
				onFocusChange(true)
			}}
			onBlur={() => {
				setDraft(null)
				setIsFocused(false)
				onFocusChange(false)
			}}
			style={{
				...FORMULA_INPUT_STYLE,
				width: `${text.length + 1}ch`,
				borderColor: isFocused ? "#94A3B8" : active ? "#CBD5E1" : "transparent",
				backgroundColor: active ? "#FFFFFF" : "transparent",
				color: active ? "#334155" : "#60646C",
			}}
		/>
	)
}

// The formula behind a derived column. At rest it reads as one line — "× 1 - 250"
// — with the constant's sign folded into the operator; hovering or focusing
// anywhere in the row turns both numbers into inputs holding their raw, signed
// values, which is what the user actually edits.
function DerivationFormula({
	multiplier,
	constant,
	onChange,
}: {
	multiplier: number
	constant: number
	onChange: (patch: { multiplier?: number; constant?: number }) => void
}) {
	const [isHovered, setIsHovered] = useState(false)
	const [isFocused, setIsFocused] = useState(false)
	const isActive = isHovered || isFocused
	const isNegative = constant < 0

	return (
		<Inline
			gap="1"
			alignItems="center"
			justifyContent="center"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Text fontSize="sm" color={isActive ? "muted" : "subtle"}>
				×
			</Text>
			<FormulaNumberInput
				value={multiplier}
				active={isActive}
				title="Multiplied by"
				onChange={(value) => onChange({ multiplier: value })}
				onFocusChange={setIsFocused}
			/>
			<Text fontSize="sm" color={isActive ? "muted" : "subtle"}>
				{!isActive && isNegative ? "-" : "+"}
			</Text>
			<FormulaNumberInput
				value={!isActive && isNegative ? Math.abs(constant) : constant}
				active={isActive}
				title="Added to the source rate"
				onChange={(value) => onChange({ constant: value })}
				onFocusChange={setIsFocused}
			/>
		</Inline>
	)
}

function isCircularDerivation(
	candidateSourceKey: string,
	targetColKey: string,
	columnDerivations: Record<
		string,
		{ mode: "manual" | "derived"; sourceColKey?: string }
	>
): boolean {
	if (candidateSourceKey === targetColKey) return true

	const visited = new Set<string>()
	let curr: string | undefined = candidateSourceKey

	while (curr && !visited.has(curr)) {
		if (curr === targetColKey) {
			return true
		}
		visited.add(curr)
		const deriv:
			| { mode: "manual" | "derived"; sourceColKey?: string }
			| undefined = columnDerivations[curr]
		if (deriv && deriv.mode === "derived" && deriv.sourceColKey) {
			curr = deriv.sourceColKey
		} else {
			break
		}
	}

	return false
}

// The room rates grid's own form. `rates` is always seeded, by `buildGridRates`.
type TRoomRatesGridFormSchema = {
	rates: TGridRate[]
	pax_types: IRateBlockSchema["pax_types"]
	is_lower_occupancy_rate_configured: boolean
}

export function RoomRatesSection({
	hotel,
	roomCols,
	roomTypes,
	hotelErrors,
	parentMealPlans: mealPlans,
	parentMealRates: mealRates,
	parentRates,
	parentPaxTypes,
	parentRooms,
	parentRoundingPoint,
	parentIsLowerOccupancyRateConfigured,
	initialBlockRates,
	initialBlockPaxTypes,
	initialBlockRooms,
	childRateSameAsAdult,
	onUpdateRooms,
	onUpdateRatesAndPaxTypes,
	onFieldBlur,
}: {
	hotel: IHotel
	roomCols: TRoomCol[]
	roomTypes: THotelOtaRoomType[]
	hotelErrors?: THotelRateErrors
	parentMealPlans: IRateBlockSchema["meal_plans"]
	parentMealRates: IRateBlockSchema["meal_rates"]
	parentRates: IRateBlockSchema["rates"]
	parentPaxTypes: IRateBlockSchema["pax_types"]
	parentRooms: IRateBlockSchema["rooms"]
	parentRoundingPoint?: IRateBlockSchema["rounding_point"]
	parentIsLowerOccupancyRateConfigured?: boolean
	initialBlockRates: IRateBlockSchema["rates"]
	initialBlockPaxTypes?: IRateBlockSchema["pax_types"]
	initialBlockRooms?: IRateBlockSchema["rooms"]
	childRateSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	onUpdateRooms: (rooms: IRateBlockSchema["rooms"]) => void
	onUpdateRatesAndPaxTypes: (
		rates: IRateBlockSchema["rates"],
		paxTypes: IRateBlockSchema["pax_types"],
		isLowerOccupancyConfigured?: boolean
	) => void
	onFieldBlur?: () => void
}) {
	const initialConfigureLowerOccupancy = Boolean(
		parentIsLowerOccupancyRateConfigured
	)

	// The derivation each column was saved with — what a reset button restores.
	// Columns the block never saved a derivation for (a new sheet, or a hotel
	// column added since the sheet was last saved) fall back to the same default
	// a fresh block gets, so a reset on those has nothing to differ from.
	const savedColumnKeys = useMemo(
		() => occupancyKeysOf(initialBlockPaxTypes),
		[initialBlockPaxTypes]
	)
	const seedColumnDerivations = useCallback(
		(cols: Pick<TRoomCol, "key" | "paxType">[]) => {
			const defaults = getInitialColumnDerivations(cols)
			const saved = columnDerivationsFromPaxTypes(initialBlockPaxTypes, cols)
			const seeded: Record<string, TColumnDerivation> = {}
			cols.forEach((col) => {
				seeded[col.key] = savedColumnKeys.has(col.key)
					? saved[col.key]
					: defaults[col.key]
			})
			return seeded
		},
		[initialBlockPaxTypes, savedColumnKeys]
	)
	// The baseline itself, kept for the reset comparison — never mutated by the
	// user, unlike `columnDerivations` below.
	const initialColumnDerivations = useMemo(
		() => seedColumnDerivations(roomCols),
		[seedColumnDerivations, roomCols]
	)

	// The derivations the block *currently* holds, which is what the live state
	// has to come back to. Stepping away and back, or switching hotel/season,
	// remounts this grid; seeding only from `initialBlockPaxTypes` — the sheet as
	// it was loaded from the server — would drop every derivation edited since,
	// and the sync effect further down would then write that reset straight back
	// into the block. Snapshotted at mount because that same effect rewrites
	// `parentPaxTypes` from this state, so reading it live would feed the state
	// into its own seed.
	const [mountedPaxTypes] = useState(() => parentPaxTypes)
	const liveColumnKeys = useMemo(
		() => occupancyKeysOf(mountedPaxTypes),
		[mountedPaxTypes]
	)
	const seedLiveColumnDerivations = useCallback(
		(cols: Pick<TRoomCol, "key" | "paxType">[]) => {
			const fallback = seedColumnDerivations(cols)
			const live = columnDerivationsFromPaxTypes(mountedPaxTypes, cols)
			const seeded: Record<string, TColumnDerivation> = {}
			cols.forEach((col) => {
				seeded[col.key] = liveColumnKeys.has(col.key)
					? live[col.key]
					: fallback[col.key]
			})
			return seeded
		},
		[seedColumnDerivations, liveColumnKeys, mountedPaxTypes]
	)

	const [columnDerivations, setColumnDerivations] = useState<
		Record<string, TColumnDerivation>
	>(() => seedLiveColumnDerivations(roomCols))

	useEffect(() => {
		setColumnDerivations((prev) => {
			const defaults = seedLiveColumnDerivations(roomCols)
			let changed = false
			const next = { ...prev }
			Object.keys(defaults).forEach((key) => {
				if (!next[key]) {
					next[key] = defaults[key]
					changed = true
				}
			})
			return changed ? next : prev
		})
	}, [roomCols, seedLiveColumnDerivations])

	// The block's `rooms` is the record of which room types it prices, so a
	// removed room is simply one that is not in it. Deriving the split from the
	// block rather than from component state is what makes a removal outlive the
	// grid — every season and hotel switch remounts this form, and local state
	// would come back empty with every room restored.
	//
	// An empty `rooms` means the block has not been seeded yet (the hotel's OTA
	// data is still landing), not that every room was removed — HotelRateForm
	// seeds it as soon as the room types arrive.
	const pricedRoomTypeIds = useMemo(
		() =>
			parentRooms?.length
				? new Set(parentRooms.map((room) => Number(room.ota_room_type)))
				: null,
		[parentRooms]
	)

	const activeRoomTypes = useMemo(
		() =>
			pricedRoomTypeIds
				? roomTypes.filter((rt) => pricedRoomTypeIds.has(Number(rt.id)))
				: roomTypes,
		[roomTypes, pricedRoomTypeIds]
	)

	const removedRoomTypes = useMemo(
		() =>
			pricedRoomTypeIds
				? roomTypes.filter((rt) => !pricedRoomTypeIds.has(Number(rt.id)))
				: [],
		[roomTypes, pricedRoomTypeIds]
	)

	const removeRoomType = useCallback(
		(roomTypeId: number) => {
			onUpdateRooms(
				buildRooms(
					activeRoomTypes.filter((rt) => Number(rt.id) !== Number(roomTypeId))
				)
			)
		},
		[activeRoomTypes, onUpdateRooms]
	)

	const restoreRoomType = useCallback(
		(roomTypeId: number) => {
			// Rebuilt by walking the hotel's own room types, so a restored room lands
			// back in its original position rather than at the end of the table.
			const priced = new Set(activeRoomTypes.map((rt) => Number(rt.id)))
			priced.add(Number(roomTypeId))
			onUpdateRooms(
				buildRooms(roomTypes.filter((rt) => priced.has(Number(rt.id))))
			)
		},
		[activeRoomTypes, roomTypes, onUpdateRooms]
	)

	// Rows and columns of the grid. Derived by the same helper the headless
	// summary uses, so the warnings shown here and the ones counted in the Step 2
	// header for unmounted blocks are computed against identical geometry.
	const {
		lowerCols,
		numLowerCols,
		visibleRows,
		baseMealPlan,
		itemsPerRoom,
		signature: geometrySignature,
	} = useMemo(
		() =>
			buildGridGeometry(hotel, mealPlans, initialConfigureLowerOccupancy, {
				roomTypes: activeRoomTypes,
				roomCols,
			}),
		[
			hotel,
			mealPlans,
			initialConfigureLowerOccupancy,
			activeRoomTypes,
			roomCols,
		]
	)

	// Live grid values, seeded from whatever the block already holds
	const initialRates = useMemo(
		() =>
			buildGridRates(
				visibleRows,
				lowerCols,
				roomCols,
				initialConfigureLowerOccupancy,
				parentRates
			),
		[
			visibleRows,
			initialConfigureLowerOccupancy,
			lowerCols,
			roomCols,
			parentRates,
		]
	)

	// Grid-aligned baseline, used strictly for change-dot comparison. Built by the
	// same helper so it stays index-aligned with `initialRates`.
	const initialGridRates = useMemo(
		() =>
			buildGridRates(
				visibleRows,
				lowerCols,
				roomCols,
				initialConfigureLowerOccupancy,
				initialBlockRates
			),
		[
			visibleRows,
			initialConfigureLowerOccupancy,
			lowerCols,
			roomCols,
			initialBlockRates,
		]
	)

	const initialChildPaxTypes = useMemo(
		() => parentPaxTypes || [],
		[parentPaxTypes]
	)

	return (
		<Form<TRoomRatesGridFormSchema>
			// Keyed on the grid's geometry on purpose. Cells are addressed
			// positionally, so when the rows or columns move, index N stops meaning
			// the same cell. react-final-form would reinitialize from the rebuilt
			// `initialValues` — but it does that from a *parent* effect, which runs
			// after the children's, leaving `syncAllDerivedRates` to run once
			// against the old values under the new geometry and write derived rates
			// into the wrong cells. Remounting is what guarantees every field is
			// built from the new `initialRates` before any effect sees it.
			//
			// The key is the geometry's identity, not its size: a same-sized
			// reshuffle moves every cell just as surely as a resize does, and this
			// grid mounts on one of those — the layout before the block's base meal
			// plan is seeded is one room type x its meal plans, and the layout after
			// is one base row per room type. See `gridGeometrySignature`.
			key={`${hotel.id}_${geometrySignature}`}
			initialValues={{
				rates: initialRates,
				pax_types: initialChildPaxTypes,
				is_lower_occupancy_rate_configured: initialConfigureLowerOccupancy,
			}}
			onSubmit={() => undefined}
			validate={(values) => {
				const errors: Record<string, unknown> = {}
				if (values.rates) {
					const rates = values.rates
					const ratesErrors: Record<string, string>[] = []
					let hasRateError = false
					// Derived cells hold no input of their own — see `isDerivedRate`
					const derivation = {
						derivedOccupancies:
							derivedOccupanciesFromColumns(columnDerivations),
						baseMealPlanId: baseMealPlan ? Number(baseMealPlan.id) : null,
					}
					// Walked as the grid lays the array out — `buildGridRates` order —
					// rather than over the array itself. What a cell *is*, and so
					// whether it is derived and has to be filled in at all, is a
					// property of the geometry; reading it back off the value sitting
					// at that index takes the array's word for a layout it may no
					// longer be on, and a row that is in fact required then reports
					// nothing at all.
					visibleRows.forEach((row, rowIdx) => {
						const rowBaseOccupancy = getRoomBaseOccupancy(row.rt)
						const cells = [
							...(initialConfigureLowerOccupancy
								? lowerCols.map((col) => ({
										key: col.key,
										// A room cannot be sold at or above its own base
										// occupancy through a lower occupancy column, and
										// `buildGridRates` leaves those cells as placeholders
										enabled: col.occ < rowBaseOccupancy,
									}))
								: []),
							{ key: "base", enabled: true },
							...roomCols.map((col) => ({ key: col.key, enabled: true })),
						]
						cells.forEach((cell, colOffset) => {
							if (!cell.enabled) return
							const idx = rowIdx * itemsPerRoom + colOffset
							const r = rates[idx]
							if (!r) return
							const derived = isDerivedRate(
								{
									occupancy: cell.key,
									ota_meal_plan: row.mp.id,
									is_manual: r.is_manual,
								},
								derivation
							)
							if (!isNumeric(r.rate_before_tax)) {
								// Blank is only a missing entry on a cell the user fills in.
								// A blank derived cell means its source is blank, and that
								// cell reports the error itself.
								if (!derived) {
									ratesErrors[idx] = { rate_before_tax: "Required" }
									hasRateError = true
								}
							} else if (Number(r.rate_before_tax) < 0) {
								// A formula can drive a derived cell below zero, which is
								// just as invalid as typing it by hand.
								ratesErrors[idx] = {
									rate_before_tax: "Must be a positive value",
								}
								hasRateError = true
							} else if (Number(r.rate_before_tax) > MAX_RATE) {
								ratesErrors[idx] = { rate_before_tax: rateExceedsMaxMessage }
								hasRateError = true
							}
						})
					})
					if (hasRateError) {
						errors.rates = ratesErrors
					}
				}
				return errors
			}}
			// Nothing in this render prop reads form state — RateGridSummary
			// subscribes to values and errors itself. Without this the prop would
			// re-render the whole grid on every validation pass.
			subscription={{}}
		>
			{() => (
				<RoomRatesInner
					hotel={hotel}
					roomCols={roomCols}
					hotelErrors={hotelErrors}
					activeRoomTypes={activeRoomTypes}
					removedRoomTypes={removedRoomTypes}
					onRemoveRoomType={removeRoomType}
					onRestoreRoomType={restoreRoomType}
					lowerCols={lowerCols}
					numLowerCols={numLowerCols}
					configureLowerOccupancy={initialConfigureLowerOccupancy}
					columnDerivations={columnDerivations}
					setColumnDerivations={setColumnDerivations}
					initialColumnDerivations={initialColumnDerivations}
					columnsWithSavedDerivation={savedColumnKeys}
					baseMealPlan={baseMealPlan}
					visibleRows={visibleRows}
					mealRates={mealRates}
					childRateSameAsAdult={childRateSameAsAdult}
					onUpdateRatesAndPaxTypes={onUpdateRatesAndPaxTypes}
					onFieldBlur={onFieldBlur}
					initialGridRates={initialGridRates}
					initialBlockRooms={initialBlockRooms}
					parentRooms={parentRooms}
					roundingPoint={parentRoundingPoint}
				/>
			)}
		</Form>
	)
}

type TCellWarningStore = {
	subscribe: (listener: () => void) => () => void
	get: (cellIdx: number) => string | undefined
}

// The warning each grid cell shows, computed once for the whole grid from a
// single subscription to its rates. Kept out of per-cell field subscriptions
// on purpose: final-form notifies every subscriber each time a field
// registers, so mounting cost grows with the square of the subscription
// count. Only a cell whose warning actually changed re-renders.
function createCellWarningStore(
	geometry: TRoomRateWarningGeometry,
	configureLowerOccupancy: boolean,
	initialRates: TGridRate[]
): TCellWarningStore & { recompute: (rates: TGridRate[]) => void } {
	let warnings = new Map<number, string>()
	let lastRates: TGridRate[] | undefined
	const listeners = new Set<() => void>()
	function recompute(rates: TGridRate[]) {
		// The subscription fires on any value change; only the rates matter
		if (rates === lastRates) return
		lastRates = rates
		warnings = collectRoomRateCellWarnings(
			geometry,
			configureLowerOccupancy,
			(idx) => toNum(rates[idx]?.rate_before_tax)
		)
		listeners.forEach((listener) => listener())
	}
	// Seeded up front so the very first render already carries the warnings
	recompute(initialRates)
	return {
		recompute,
		subscribe(listener) {
			listeners.add(listener)
			return () => {
				listeners.delete(listener)
			}
		},
		get: (cellIdx) => warnings.get(cellIdx),
	}
}

function useRoomRateCellWarnings(
	form: FormApi<TRoomRatesGridFormSchema>,
	geometry: TRoomRateWarningGeometry,
	configureLowerOccupancy: boolean
): TCellWarningStore {
	const store = useMemo(
		() =>
			createCellWarningStore(
				geometry,
				configureLowerOccupancy,
				form.getState().values.rates
			),
		[form, geometry, configureLowerOccupancy]
	)

	useEffect(
		() =>
			form.subscribe(({ values }) => store.recompute(values.rates), {
				values: true,
			}),
		[form, store]
	)

	return store
}

// One priced cell of the room rates grid. Kept to the fewest field
// subscriptions it can have — its own value (twice: RateInputField and the
// input inside it) and its manual flag — see `useRoomRateCellWarnings`. Writes
// to the grid go through the callbacks, which RoomRatesInner owns.
function RoomRateCell({
	cellIdx,
	initialValue,
	isDerived,
	hasBaseline,
	warningStore,
	onChange,
	onManualEdit,
	onResetCell,
	onRevertCell,
}: {
	cellIdx: number
	initialValue?: TGridRate["rate_before_tax"]
	isDerived?: boolean
	hasBaseline: boolean
	warningStore: TCellWarningStore
	onChange?: () => void
	onManualEdit: (cellIdx: number) => void
	onResetCell: (cellIdx: number) => void
	onRevertCell: (cellIdx: number) => void
}) {
	const { value: isManual } = useFieldValue<boolean>(
		`rates[${cellIdx}].is_manual`
	)
	const warning = useSyncExternalStore(warningStore.subscribe, () =>
		warningStore.get(cellIdx)
	)

	return (
		<RateInputField
			name={`rates[${cellIdx}].rate_before_tax`}
			initialValue={initialValue}
			isDerived={isDerived}
			isManual={Boolean(isManual)}
			showChangeDot={hasBaseline}
			warning={warning}
			onManualEdit={() => onManualEdit(cellIdx)}
			onResetManual={() => onResetCell(cellIdx)}
			onRevert={() => onRevertCell(cellIdx)}
			onChange={onChange}
		/>
	)
}

// Aggregate change / warning / error summary for the rate grid.
//
// This owns the only subscription to the grid form's values. Keeping it out of
// RoomRatesInner is what stops a keystroke in one cell from re-rendering all
// ~130 rate inputs: typing now re-renders the edited field and this chip row.
function RateGridSummary({
	hotel,
	hotelErrors,
	activeRoomTypes,
	visibleRows,
	aboveRowIndicesMap,
	configureLowerOccupancy,
	lowerCols,
	numLowerCols,
	roomCols,
	itemsPerRoom,
	initialGridRates,
	initialBlockRooms,
	parentRooms,
}: {
	hotel: IHotel
	hotelErrors?: THotelRateErrors
	activeRoomTypes: THotelOtaRoomType[]
	visibleRows: {
		rt: THotelOtaRoomType
		mp: TOtaMealPlan
		isBase: boolean
	}[]
	aboveRowIndicesMap: Record<number, number>
	configureLowerOccupancy: boolean
	lowerCols: TLowerCol[]
	numLowerCols: number
	roomCols: TRoomCol[]
	itemsPerRoom: number
	initialGridRates: TGridRate[]
	initialBlockRooms?: IRateBlockSchema["rooms"]
	parentRooms?: IRateBlockSchema["rooms"]
}) {
	const formState = useFormState<TRoomRatesGridFormSchema>({
		subscription: { values: true, errors: true },
	})
	const rates = formState.values.rates
	const errors = formState.errors

	// A sheet being created from scratch has no baseline to diff against, so
	// every rate typed in would otherwise read as a change here while the step 2
	// header — which knows there is no baseline — correctly reports none.
	const hasBaseline = useMemo(
		() => initialGridRates.some((r) => r.rate_before_tax),
		[initialGridRates]
	)

	// Shared with the headless summary, so the chips here and the Step 2 counts
	// cannot report a different set of warnings for the same grid.
	const warningMessages = useMemo(() => {
		if (!rates || !Array.isArray(rates)) return []
		return collectRoomRateWarnings(
			{
				lowerCols,
				roomCols,
				visibleRows,
				itemsPerRoom,
				aboveRowIndicesMap,
			},
			configureLowerOccupancy,
			(idx) => {
				const val = rates[idx]?.rate_before_tax
				return val !== null && val !== undefined && (val as unknown) !== ""
					? Number(val)
					: null
			}
		)
	}, [
		rates,
		visibleRows,
		aboveRowIndicesMap,
		configureLowerOccupancy,
		lowerCols,
		roomCols,
		itemsPerRoom,
	])

	const changedMessages = useMemo(() => {
		const changes: string[] = []
		// A sheet created from scratch has no baseline, so nothing here is a change
		if (!rates || !Array.isArray(rates) || !hasBaseline) return changes

		const roomTypeName = (id: number) =>
			hotel.ota_room_types?.find((rt) => Number(rt.id) === id)?.name ||
			`Room ${id}`

		const currentRoomIds = new Set(
			(parentRooms || []).map((r) => Number(r.ota_room_type))
		)
		const baselineRoomIds = new Set(
			(initialBlockRooms || []).map((r) => Number(r.ota_room_type))
		)

		if (initialBlockRooms && initialBlockRooms.length > 0) {
			currentRoomIds.forEach((id) => {
				if (!baselineRoomIds.has(id)) {
					changes.push(`Room ${roomTypeName(id)} added`)
				}
			})
			baselineRoomIds.forEach((id) => {
				if (!currentRoomIds.has(id)) {
					changes.push(`Room ${roomTypeName(id)} removed`)
				}
			})
		}

		const baseColOffset = configureLowerOccupancy ? numLowerCols : 0

		const numAt = (source: TGridRate[], idx: number) => {
			const val = source[idx]?.rate_before_tax
			return val !== null && val !== undefined && (val as unknown) !== ""
				? Number(val)
				: null
		}

		visibleRows.forEach((row, rIdx) => {
			const { rt, mp } = row
			const rtBaseOcc = getRoomBaseOccupancy(rt)

			const checkChange = (cellIdx: number, colLabel: string) => {
				const initNum = numAt(initialGridRates, cellIdx)
				const currNum = numAt(rates, cellIdx)
				if (
					(initNum === null && currNum !== null) ||
					(initNum !== null && currNum === null) ||
					(initNum !== null && currNum !== null && initNum !== currNum)
				) {
					changes.push(
						`${rt.name} - ${mp.name} - ${colLabel} Rate is updated ${initNum ?? 0} → ${currNum ?? 0}`
					)
				}
			}

			if (configureLowerOccupancy) {
				lowerCols.forEach((col, colIdx) => {
					if (col.occ >= rtBaseOcc) return
					checkChange(rIdx * itemsPerRoom + colIdx, col.label)
				})
			}

			checkChange(rIdx * itemsPerRoom + baseColOffset, "BASE")

			roomCols.forEach((col, colIndex) => {
				checkChange(
					rIdx * itemsPerRoom + baseColOffset + 1 + colIndex,
					col.label || col.key.toUpperCase()
				)
			})
		})

		return changes
	}, [
		rates,
		initialGridRates,
		hasBaseline,
		parentRooms,
		initialBlockRooms,
		hotel.ota_room_types,
		visibleRows,
		configureLowerOccupancy,
		lowerCols,
		numLowerCols,
		roomCols,
		itemsPerRoom,
	])

	const errorMessages = useMemo(() => {
		const msgs: string[] = []

		const ratesErrors = errors?.rates as Record<string, string>[] | undefined
		if (ratesErrors && Array.isArray(ratesErrors)) {
			ratesErrors.forEach((errObj, idx) => {
				if (errObj && typeof errObj === "object") {
					const rateItem = rates[idx]
					const errText = Object.values(errObj).filter(Boolean).join(", ")
					if (rateItem && errText) {
						const roomType = activeRoomTypes.find(
							(rt) => Number(rt.id) === Number(rateItem.ota_room_type)
						)
						const mealPlan = hotel.ota_meal_plans?.find(
							(mp) => Number(mp.id) === Number(rateItem.ota_meal_plan)
						)

						const roomName = roomType?.name || "Room"
						const mpName = mealPlan?.name || ""

						let occLabel = rateItem.occupancy || ""
						if (occLabel === "base") {
							const baseOcc = roomType ? getRoomBaseOccupancy(roomType) : 2
							occLabel = getOccupancyInfo(baseOcc).label
						} else {
							const foundCol =
								roomCols.find((c) => c.key === occLabel) ||
								lowerCols.find((c) => c.key === occLabel)
							if (foundCol) {
								occLabel = foundCol.label
							}
						}

						const label = mpName
							? `${roomName} - ${mpName} (${occLabel})`
							: `${roomName} (${occLabel})`

						msgs.push(`${label}: ${errText}`)
					} else if (errText) {
						msgs.push(errText)
					}
				}
			})
		}

		// The block level message restates whatever the per cell errors already say,
		// so it only earns its place when there are none — which happens when the
		// grid renders no rows at all and would otherwise report nothing.
		if (msgs.length === 0 && hotelErrors?.roomRates) {
			msgs.push(hotelErrors.roomRates)
		}

		return msgs
	}, [
		errors,
		hotelErrors,
		rates,
		activeRoomTypes,
		hotel.ota_meal_plans,
		roomCols,
		lowerCols,
	])

	return (
		<Component<{
			isOpen: boolean
			status: "changes" | "warnings" | "errors" | null
			messages: string[]
		}>
			initialState={{ isOpen: false, status: null, messages: [] }}
		>
			{({ state, setState }) => (
				<>
					<Inline gap="2" alignItems="center">
						{changedMessages.length > 0 ? (
							<Box
								paddingX="2"
								paddingY="1"
								style={{
									color: "#0284c7",
								}}
								cursor="pointer"
								onClick={() =>
									setState({
										isOpen: true,
										status: "changes",
										messages: changedMessages,
									})
								}
							>
								<Text fontSize="sm" fontWeight="semibold">
									● {changedMessages.length}{" "}
									{changedMessages.length === 1 ? "Change" : "Changes"}
								</Text>
							</Box>
						) : null}
						{warningMessages.length > 0 ? (
							<Inline
								gap="1"
								alignItems="center"
								style={{
									color: "#E17100",
								}}
								cursor="pointer"
								onClick={() =>
									setState({
										isOpen: true,
										status: "warnings",
										messages: warningMessages,
									})
								}
							>
								<Icons.Attention size="4" />
								<Text fontSize="sm" fontWeight="semibold">
									{warningMessages.length}{" "}
									{warningMessages.length === 1 ? "Warning" : "Warnings"}
								</Text>
							</Inline>
						) : null}
						{errorMessages.length > 0 ? (
							<Inline
								gap="1"
								alignItems="center"
								color="danger"
								cursor="pointer"
								onClick={() =>
									setState({
										isOpen: true,
										status: "errors",
										messages: errorMessages,
									})
								}
							>
								<Icons.CircleAlert size="4" />
								<Text fontSize="sm" fontWeight="semibold">
									{errorMessages.length}{" "}
									{errorMessages.length === 1 ? "Error" : "Errors"}
								</Text>
							</Inline>
						) : null}
					</Inline>
					<Dialog
						title={
							<Inline gap="2" alignItems="center">
								<Text fontWeight="semibold">
									{" "}
									{state.status === "errors"
										? "Errors"
										: state.status === "warnings"
											? "Warnings"
											: "Changes"}
								</Text>
								<Inline gap="1" alignItems="center">
									{state.status === "errors" ? (
										<Icons.CircleAlert size="4" color="danger" />
									) : state.status === "warnings" ? (
										<Icons.Attention size="4" style={{ color: "#E17100" }} />
									) : (
										<Text style={{ color: "#0081FF" }} fontSize="sm">
											●
										</Text>
									)}
									<Text
										fontSize="sm"
										fontWeight="semibold"
										color={state.status === "errors" ? "danger" : undefined}
										style={
											state.status === "warnings"
												? { color: "#E17100" }
												: state.status === "changes"
													? { color: "#0081FF" }
													: undefined
										}
									>
										{state.messages.length}{" "}
										{state.status === "errors"
											? state.messages.length === 1
												? "Error"
												: "Errors"
											: state.status === "warnings"
												? state.messages.length === 1
													? "Warning"
													: "Warnings"
												: state.messages.length === 1
													? "Change"
													: "Changes"}{" "}
										Found
									</Text>
								</Inline>
							</Inline>
						}
						open={state.isOpen}
						onClose={() =>
							setState({ isOpen: false, status: null, messages: [] })
						}
						sm
						centered
					>
						<DialogBody>
							<Stack gap="2" style={{ maxHeight: "75dvh", overflowY: "auto" }}>
								{state.messages.map((message) => (
									<Inline gap="1" alignItems="center">
										<Box>
											{state.status === "errors" ? (
												<Icons.CircleAlert size="4" color="danger" />
											) : state.status === "warnings" ? (
												<Icons.Attention size="4" color="warning_light" />
											) : (
												<Text style={{ color: "#0081FF" }} fontSize="sm">
													●
												</Text>
											)}
										</Box>
										<Text fontSize="sm" whiteSpace="preserveAndWrap">
											{message}
										</Text>
									</Inline>
								))}
							</Stack>
						</DialogBody>
					</Dialog>
				</>
			)}
		</Component>
	)
}

function RoomRatesInner({
	hotel,
	roomCols,
	hotelErrors,
	activeRoomTypes,
	removedRoomTypes,
	onRemoveRoomType,
	onRestoreRoomType,
	lowerCols,
	numLowerCols,
	configureLowerOccupancy,
	columnDerivations,
	setColumnDerivations,
	initialColumnDerivations,
	columnsWithSavedDerivation,
	baseMealPlan,
	visibleRows,
	mealRates,
	childRateSameAsAdult,
	onUpdateRatesAndPaxTypes,
	onFieldBlur,
	initialGridRates,
	initialBlockRooms,
	parentRooms,
	roundingPoint,
}: {
	hotel: IHotel
	roomCols: TRoomCol[]
	hotelErrors?: THotelRateErrors
	activeRoomTypes: THotelOtaRoomType[]
	removedRoomTypes: THotelOtaRoomType[]
	onRemoveRoomType: (roomTypeId: number) => void
	onRestoreRoomType: (roomTypeId: number) => void
	lowerCols: TLowerCol[]
	numLowerCols: number
	// The geometry the grid is actually mounted on — see the note beside
	// `isLowerOccupancyConfigured` below for why this is not the live field.
	configureLowerOccupancy: boolean
	columnDerivations: Record<string, TColumnDerivation>
	setColumnDerivations: React.Dispatch<
		React.SetStateAction<Record<string, TColumnDerivation>>
	>
	// The derivation each column was saved with, for the reset button. Empty for
	// a block with no baseline to reset to.
	initialColumnDerivations: Record<string, TColumnDerivation>
	// Which columns actually have a saved derivation to reset to — a column
	// absent here is new since the block was last saved, and gets no reset icon
	// even if it drifts from the shared default.
	columnsWithSavedDerivation: Set<string>
	baseMealPlan: TOtaMealPlan | null
	visibleRows: {
		rt: THotelOtaRoomType
		mp: TOtaMealPlan
		isBase: boolean
	}[]
	mealRates: IRateBlockSchema["meal_rates"]
	childRateSameAsAdult: IRateBlockSchema["child_meal_rate_same_as_adult"]
	onUpdateRatesAndPaxTypes: (
		rates: IRateBlockSchema["rates"],
		paxTypes: IRateBlockSchema["pax_types"],
		isLowerOccupancyConfigured?: boolean
	) => void
	onFieldBlur?: () => void
	initialGridRates: TGridRate[]
	initialBlockRooms?: IRateBlockSchema["rooms"]
	parentRooms?: IRateBlockSchema["rooms"]
	roundingPoint?: IRateBlockSchema["rounding_point"]
}) {
	const sectionRef = useScrollOnFocus<HTMLDivElement>("roomRates")
	const form = useForm<TRoomRatesGridFormSchema>()

	// What the switch currently reads, which is a render ahead of the grid: the
	// rates array is laid out flat, `itemsPerRoom` cells per row, and it is only
	// rebuilt on the new column count when the toggle reaches the block and
	// remounts this grid on the new geometry. Deriving anything positional from
	// this value in between would address cells the array does not have that
	// shape for — with lower occupancy just switched on, row N's cells would be
	// looked up one stride too far along, and the last room's cells would run off
	// the end of the array entirely. So this drives nothing but the push below
	// that asks for the remount; everything positional uses the geometry the grid
	// was mounted with.
	const { value: isLowerOccupancyConfigured } = useFieldValue<boolean>(
		"is_lower_occupancy_rate_configured"
	)
	const requestedLowerOccupancy = Boolean(isLowerOccupancyConfigured)
	const itemsPerRoom =
		(configureLowerOccupancy ? numLowerCols : 0) + 1 + roomCols.length

	// The pax types priced by this block, one per column of the rate table
	const paxTypes = useMemo(
		() =>
			buildPaxTypes(
				configureLowerOccupancy ? lowerCols : [],
				roomCols,
				columnDerivations
			),
		[configureLowerOccupancy, lowerCols, roomCols, columnDerivations]
	)

	// Keep the block form in sync as columns and their derivations change, so the
	// payload carries pax types even when the rate grid is never focused.
	const lastSyncRef = useRef<string | null>(null)
	useEffect(() => {
		const serialized = JSON.stringify([paxTypes, requestedLowerOccupancy])
		if (serialized === lastSyncRef.current) return
		const isFirstSync = lastSyncRef.current === null
		lastSyncRef.current = serialized
		const values = form.getState().values
		// A saved block mounts with the columns it was priced in, which the hotel
		// may have re-cut since. That is the one first sync worth reporting.
		const isColumnsReconciled =
			isFirstSync && paxColumnsChanged(paxTypes, values.pax_types)
		form.change("pax_types", paxTypes)
		// The rates travel as they are — laid out on the mounted geometry, and
		// re-keyed by room type, meal plan and occupancy on the way in, so the
		// remount this push triggers seeds the new column count from them without
		// losing a cell.
		onUpdateRatesAndPaxTypes(
			toBlockRates(values.rates),
			paxTypes,
			requestedLowerOccupancy
		)
		// Toggling lower occupancy rewrites the columns, so flush to the parent
		// right away instead of waiting for the switch to lose focus. Skipped on
		// mount so errors only surface once the user has touched the block —
		// unless the columns were reconciled, which the sheet summary has to see.
		if (!isFirstSync || isColumnsReconciled) {
			onFieldBlur?.()
		}
	}, [
		paxTypes,
		requestedLowerOccupancy,
		form,
		onUpdateRatesAndPaxTypes,
		onFieldBlur,
	])

	const aboveRowIndicesMap = useMemo(
		() => buildAboveRowIndicesMap(visibleRows),
		[visibleRows]
	)

	const baseOccupancyChangeByRoom = useMemo(
		() => baseOccupancyChanges(initialBlockRooms || [], activeRoomTypes),
		[initialBlockRooms, activeRoomTypes]
	)

	// Nothing to mark as changed on a sheet being created from scratch. Worked
	// out once here rather than in every cell.
	const hasBaseline = useMemo(
		() => initialGridRates.some((r) => r.rate_before_tax),
		[initialGridRates]
	)

	const warningGeometry = useMemo(
		() => ({
			lowerCols,
			roomCols,
			visibleRows,
			itemsPerRoom,
			aboveRowIndicesMap,
		}),
		[lowerCols, roomCols, visibleRows, itemsPerRoom, aboveRowIndicesMap]
	)
	const warningStore = useRoomRateCellWarnings(
		form,
		warningGeometry,
		configureLowerOccupancy
	)

	const syncAllDerivedRates = useCallback(() => {
		const formRates: TGridRate[] = form.getState().values.rates
		const derivedRates = deriveGridRates({
			rates: formRates,
			geometry: {
				activeRoomTypes,
				visibleRows,
				roomCols,
				lowerCols,
				numLowerCols,
				itemsPerRoom,
				baseMealPlan,
			},
			configureLowerOccupancy,
			columnDerivations,
			childRateSameAsAdult,
			mealRates,
			roundingValue: roundingPoint?.id ?? null,
		})
		if (derivedRates === formRates) return

		// One `form.change` per moved cell. Left unbatched each of those revalidates
		// the grid and republishes every registered field, which is what makes
		// toggling a meal plan (and typing a base rate, which schedules this too)
		// stutter.
		bulkChange(form, () => {
			derivedRates.forEach((cell, idx) => {
				const before = formRates[idx]
				if (cell === before) return
				;(
					[
						"rate_before_tax",
						"rate_before_tax_unrounded",
						"ota_meal_plan",
						"ota_room_type",
					] as const
				).forEach((field) => {
					if (cell[field] !== before?.[field]) {
						form.change(`rates[${idx}].${field}` as never, cell[field] as never)
					}
				})
			})
		})
	}, [
		form,
		activeRoomTypes,
		visibleRows,
		roomCols,
		columnDerivations,
		configureLowerOccupancy,
		numLowerCols,
		itemsPerRoom,
		lowerCols,
		baseMealPlan,
		childRateSameAsAdult,
		mealRates,
		roundingPoint,
	])

	// The grid hands its rates to the block form when it loses focus, which
	// covers every edit made inside the grid. A meal rate typed into the table
	// beside it rewrites every derived cell here without the grid ever being
	// focused, so the push has to happen from the sync itself — otherwise the
	// block keeps the pre-edit rates and the preview, the sheet summary and the
	// payload all read them back.
	useEffect(() => {
		const before = JSON.stringify(form.getState().values.rates)
		syncAllDerivedRates()
		const values = form.getState().values
		// Pushing unconditionally would flush the block on every run of this
		// effect, and the flush re-renders the tree that feeds it. Only a sync
		// that actually moved a rate is worth reporting upwards. That includes
		// the mount run: a column added since the sheet was saved is filled in
		// here, and would otherwise stay in the grid alone.
		if (JSON.stringify(values.rates) === before) return
		onUpdateRatesAndPaxTypes(
			toBlockRates(values.rates),
			values.pax_types,
			values.is_lower_occupancy_rate_configured
		)
		onFieldBlur?.()
	}, [
		syncAllDerivedRates,
		columnDerivations,
		mealRates,
		baseMealPlan,
		visibleRows,
		roundingPoint,
		form,
		onUpdateRatesAndPaxTypes,
		onFieldBlur,
	])

	// Both cell controls are clicked rather than typed into, so the grid's own
	// `onBlur` either never fires or fires ahead of the change and carries the
	// pre-click rates. They push for themselves instead.
	const pushGridRates = useCallback(() => {
		const values = form.getState().values
		onUpdateRatesAndPaxTypes(
			toBlockRates(values.rates),
			values.pax_types,
			values.is_lower_occupancy_rate_configured
		)
		onFieldBlur?.()
	}, [form, onUpdateRatesAndPaxTypes, onFieldBlur])

	// A rate typed by hand pins its cell against the derivation formula.
	const markCellManual = useCallback(
		(cellIdx: number) => {
			bulkChange(form, () => {
				form.change(`rates[${cellIdx}].is_manual` as never, true as never)
				// A hand-typed rate has no unrounded counterpart, and the cell may be
				// holding one from back when the formula owned it. Cleared alongside
				// `is_manual`, so the two can never describe the cell differently.
				form.change(
					`rates[${cellIdx}].rate_before_tax_unrounded` as never,
					null as never
				)
			})
		},
		[form]
	)

	const resetCell = useCallback(
		(cellIdx: number) => {
			form.change(`rates[${cellIdx}].is_manual` as never, false as never)
			setTimeout(() => {
				syncAllDerivedRates()
				pushGridRates()
			}, 0)
		},
		[form, syncAllDerivedRates, pushGridRates]
	)

	const revertCell = useCallback(
		(cellIdx: number) => {
			const initialObj = initialGridRates[cellIdx]
			const initialIsManual = initialObj?.is_manual ?? false
			const restored = initialObj?.rate_before_tax ?? null
			const restoredUnrounded = initialObj?.rate_before_tax_unrounded ?? null
			bulkChange(form, () => {
				form.change(
					`rates[${cellIdx}].is_manual` as never,
					initialIsManual as never
				)
				form.change(
					`rates[${cellIdx}].rate_before_tax` as never,
					restored as never
				)
				form.change(
					`rates[${cellIdx}].rate_before_tax_unrounded` as never,
					restoredUnrounded as never
				)
			})
			setTimeout(() => {
				syncAllDerivedRates()
				// A derived cell cannot hold its saved rate on the formula alone once
				// the formula's inputs have moved — the meal rate it subtracts, say —
				// and the sync above has just recomputed the revert away. Pinning it
				// is what makes the revert stick, and is honest about what the cell
				// now is: a value that deviates from its derivation. The reset
				// control beside it hands the cell back to the formula.
				const after =
					form.getState().values.rates[cellIdx]?.rate_before_tax ?? null
				if (toNum(after) !== toNum(restored)) {
					bulkChange(form, () => {
						form.change(`rates[${cellIdx}].is_manual` as never, true as never)
						form.change(
							`rates[${cellIdx}].rate_before_tax` as never,
							restored as never
						)
						// Pinned as manual, so it is the user's value now rather than a
						// rounded one — whatever it was derived from no longer applies.
						form.change(
							`rates[${cellIdx}].rate_before_tax_unrounded` as never,
							null as never
						)
					})
				}
				// A base cell also carries `onChange`, which schedules a sync of its
				// own from this same click — and that one cascades into the sub-rows
				// derived from it. Reporting upwards is deferred behind it so those
				// recomputed sub-rows travel with the revert rather than a tick later.
				setTimeout(pushGridRates, 0)
			}, 0)
		},
		[form, initialGridRates, syncAllDerivedRates, pushGridRates]
	)

	// A column's derivation mode gates its base meal plan cells via `is_manual`
	// (see `syncAllDerivedRates`), so flipping the mode has to flip that flag on
	// those cells too — otherwise the formula keeps skipping cells it should now
	// own (manual → derived), or stale derived numbers linger in cells the user
	// now has to fill in by hand (derived → manual). The other meal plan rows are
	// priced off the base meal plan row whatever the column's mode, so they are
	// left alone: pinning them would detach them from that row and turn every
	// blank one into a missing rate.
	const applyColumnDerivationModeChange = useCallback(
		(colIndex: number, nextMode: "manual" | "derived") => {
			const colOffset =
				(configureLowerOccupancy ? numLowerCols : 0) + 1 + colIndex
			bulkChange(form, () => {
				visibleRows.forEach((row, rowIndex) => {
					if (!row.isBase) return
					const cellIdx = rowIndex * itemsPerRoom + colOffset
					form.change(
						`rates[${cellIdx}].is_manual` as never,
						(nextMode === "manual") as never
					)
					if (nextMode === "manual") {
						form.change(
							`rates[${cellIdx}].rate_before_tax` as never,
							null as never
						)
						form.change(
							`rates[${cellIdx}].rate_before_tax_unrounded` as never,
							null as never
						)
					}
				})
			})
		},
		[form, itemsPerRoom, visibleRows, configureLowerOccupancy, numLowerCols]
	)

	const handleBaseRateChange = useCallback(() => {
		setTimeout(() => syncAllDerivedRates(), 0)
	}, [syncAllDerivedRates])

	return (
		<form
			noValidate
			onBlur={() => {
				const formValues = form.getState().values
				onUpdateRatesAndPaxTypes(
					toBlockRates(formValues.rates),
					formValues.pax_types,
					formValues.is_lower_occupancy_rate_configured
				)
				onFieldBlur?.()
			}}
		>
			<Stack
				gap="3"
				ref={sectionRef}
				style={{
					width: "fit-content",
					maxWidth: "100%",
					...SCROLL_TARGET_STYLE,
				}}
			>
				<Box style={{ minWidth: "100%" }}>
					<Inline gap="6" justifyContent="between" flexWrap="wrap">
						<Inline gap="1" flex="1" paddingLeft="4">
							<Text fontWeight="semibold" fontSize="md">
								Room Rates
							</Text>
							{hotel.currency ? (
								<MiniCurrency currency={hotel.currency} />
							) : null}
							<Text color="muted">(before taxes)</Text>
						</Inline>
						<RateGridSummary
							hotel={hotel}
							hotelErrors={hotelErrors}
							activeRoomTypes={activeRoomTypes}
							visibleRows={visibleRows}
							aboveRowIndicesMap={aboveRowIndicesMap}
							configureLowerOccupancy={configureLowerOccupancy}
							lowerCols={lowerCols}
							numLowerCols={numLowerCols}
							roomCols={roomCols}
							itemsPerRoom={itemsPerRoom}
							initialGridRates={initialGridRates}
							initialBlockRooms={initialBlockRooms}
							parentRooms={parentRooms}
						/>
						<SwitchInputField
							name="is_lower_occupancy_rate_configured"
							label="Configure lower occupancy price"
							labelStyle={{ color: "#60646C" }}
							alignLabelCenter
						/>
					</Inline>
				</Box>

				<Box overflow="auto" rounded="md">
					{/* Table */}
					<Box
						as="table"
						style={{
							borderCollapse: "separate",
							borderSpacing: 0,
							width: "auto",
						}}
						borderColor="emphasis"
						borderWidth="1"
						rounded="lg"
						overflow="hidden"
						bgColor="white"
					>
						{/* Table Head */}
						<Box as="thead">
							<Box as="tr" bgColor="white">
								<Box
									as="th"
									padding="3"
									fontWeight="semibold"
									textAlign="left"
									bgColor="subtle"
									color="muted"
									borderRightWidth="1"
									borderBottomWidth="1"
									style={{
										borderColor: GRID_BORDER_COLOR,
										width: `calc(${ROOM_OCCUPANCY_COL_WIDTH} - 50px)`,
										maxWidth: ROOM_OCCUPANCY_COL_WIDTH,
										position: "sticky",
										left: 0,
										zIndex: 2,
										backgroundColor: "#F8FAFC",
									}}
								>
									<Inline gap="4" flexWrap="wrapReverse">
										<Text whiteSpace="nowrap">
											Room Type <Icons.ArrowLeft rotate="270" />
										</Text>
										<Text whiteSpace="nowrap">
											Occupancy <Icons.ArrowLeft rotate="180" />
										</Text>
									</Inline>
								</Box>
								{configureLowerOccupancy
									? lowerCols.map((col) => (
											<Box
												as="th"
												key={col.key}
												paddingX="2"
												paddingY="2"
												textAlign="center"
												verticalAlign="top"
												borderRightWidth="1"
												borderBottomWidth="1"
												style={{
													borderColor: GRID_BORDER_COLOR,
													minWidth: RATE_COL_MIN_WIDTH,
												}}
											>
												<ColumnHeaderPill
													label={col.label}
													variant="lower"
													paddingX="3"
												/>
											</Box>
										))
									: null}

								<Box
									as="th"
									paddingX="2"
									paddingY="2"
									textAlign="center"
									verticalAlign="top"
									borderRightWidth="1"
									borderBottomWidth="1"
									style={{
										borderColor: GRID_BORDER_COLOR,
										minWidth: RATE_COL_MIN_WIDTH,
									}}
								>
									<ColumnHeaderPill label="BASE" variant="base" />
								</Box>

								{roomCols.map((col, colIndex) => {
									const isAdultCol = col.paxType === "AWEB"
									const derivation = columnDerivations[col.key] || {
										mode: "manual",
									}

									return (
										<Box
											as="th"
											key={col.key}
											paddingX="2"
											paddingY="2"
											textAlign="center"
											verticalAlign="top"
											borderRightWidth="1"
											borderBottomWidth="1"
											style={{
												borderColor: GRID_BORDER_COLOR,
												minWidth: RATE_COL_MIN_WIDTH,
											}}
										>
											<Stack gap="1" alignItems="center">
												{isAdultCol ? (
													<ColumnHeaderPill
														label={col.label || col.key.toUpperCase()}
														variant={columnPillVariant(col)}
													/>
												) : (
													<>
														<Inline gap="1" alignItems="center">
															<ColumnHeaderPill
																label={col.label || col.key.toUpperCase()}
																variant={columnPillVariant(col)}
															/>
															{columnsWithSavedDerivation.has(col.key) &&
															columnDerivationChanged(
																derivation,
																initialColumnDerivations[col.key]
															) ? (
																<Box
																	cursor="pointer"
																	title="Reset to saved configuration"
																	onClick={() =>
																		setColumnDerivations((prev) => ({
																			...prev,
																			[col.key]: initialColumnDerivations[
																				col.key
																			] || { mode: "manual" },
																		}))
																	}
																	style={{
																		display: "inline-flex",
																		alignItems: "center",
																		color: "#00749E",
																	}}
																>
																	<Icons.Rotate size="4" />
																</Box>
															) : null}
														</Inline>
														<Inline gap="1" alignItems="center">
															<Text
																fontSize="xs"
																fontWeight="semibold"
																aria-hidden
																style={{ color: "#60646C" }}
															>
																{derivation.mode === "manual" ? (
																	<Icons.Pencil size="3" />
																) : (
																	<Icons.ArrowDownRight size="4" />
																)}
															</Text>
															<select
																value={
																	derivation.mode === "manual"
																		? "manual"
																		: derivation.sourceColKey
																}
																onChange={(e) => {
																	const val = e.target.value
																	const nextMode: "manual" | "derived" =
																		val === "manual" ? "manual" : "derived"
																	if (nextMode !== derivation.mode) {
																		applyColumnDerivationModeChange(
																			colIndex,
																			nextMode
																		)
																	}
																	if (val === "manual") {
																		setColumnDerivations((prev) => ({
																			...prev,
																			[col.key]: { mode: "manual" },
																		}))
																	} else {
																		setColumnDerivations((prev) => ({
																			...prev,
																			[col.key]: {
																				mode: "derived",
																				sourceColKey: val,
																				multiplier:
																					prev[col.key]?.multiplier ?? 1,
																				constant: prev[col.key]?.constant ?? 0,
																			},
																		}))
																	}
																}}
																style={{
																	fontSize: "0.75rem",
																	fontWeight: "600",
																	padding: "0.125rem 0.25rem 0.125rem 0",
																	borderRadius: "0.25rem",
																	backgroundColor: "transparent",
																	color: "#60646C",
																	cursor: "pointer",
																}}
															>
																<option value="manual">Manual</option>
																<option value="base">BASE</option>
																{roomCols
																	.filter(
																		(c) =>
																			!isCircularDerivation(
																				c.key,
																				col.key,
																				columnDerivations
																			)
																	)
																	.map((c) => (
																		<option key={c.key} value={c.key}>
																			{c.label || c.key.toUpperCase()}
																		</option>
																	))}
															</select>
														</Inline>

														{derivation.mode === "derived" ? (
															<DerivationFormula
																multiplier={derivation.multiplier ?? 1}
																constant={derivation.constant ?? 0}
																onChange={(patch) => {
																	setColumnDerivations((prev) => ({
																		...prev,
																		[col.key]: {
																			...prev[col.key],
																			mode: "derived",
																			...patch,
																		},
																	}))
																}}
															/>
														) : null}
													</>
												)}
											</Stack>
										</Box>
									)
								})}
							</Box>
						</Box>
						{/* Table Body */}
						<Box as="tbody">
							{activeRoomTypes.map((rt) => {
								const rtBaseOcc = getRoomBaseOccupancy(rt)

								const baseRowItem = visibleRows
									.map((r, idx) => ({ ...r, rowIndex: idx }))
									.find((r) => r.rt.id === rt.id && r.isBase)
								const baseRowIndex = baseRowItem ? baseRowItem.rowIndex : -1

								const subRowItems = visibleRows
									.map((r, idx) => ({ ...r, rowIndex: idx }))
									.filter((r) => r.rt.id === rt.id && !r.isBase)

								return (
									<Fragment key={rt.id}>
										{/* Room Header Row */}
										<Box as="tr" bgColor="white">
											{/* Room Info Cell */}
											<RoomInfoCell
												roomType={rt}
												baseOccupancy={rtBaseOcc}
												baseOccupancyChange={baseOccupancyChangeByRoom.get(
													Number(rt.id)
												)}
												baseMealPlan={baseMealPlan}
												// A block has to price at least one room: an empty `rooms` reads as
												// not-yet-seeded, so removing the last one would restore them all.
												actions={
													activeRoomTypes.length > 1 ? (
														<Component initialState={false}>
															{({ state: isOpen, setState: setIsOpen }) => (
																<>
																	<Component initialState={false}>
																		{({
																			state: isHovered,
																			setState: setIsHovered,
																		}) => (
																			<Tooltip
																				content={
																					<Text>
																						<Icons.CircleAlert color="danger" />{" "}
																						Remove room type
																					</Text>
																				}
																			>
																				<Button
																					level="tertiary"
																					size="sm"
																					style={{ border: "none" }}
																					onClick={() => setIsOpen(true)}
																					onMouseEnter={() =>
																						setIsHovered(true)
																					}
																					onMouseLeave={() =>
																						setIsHovered(false)
																					}
																				>
																					<Icons.Cancel
																						size="4"
																						color={
																							isHovered ? "danger" : "muted"
																						}
																					/>
																				</Button>
																			</Tooltip>
																		)}
																	</Component>
																	<Dialog
																		title="Remove Room"
																		open={isOpen}
																		onClose={() => setIsOpen(false)}
																		sm
																		centered
																	>
																		<DialogBody>
																			<Box
																				padding="4"
																				rounded="md"
																				bgColor="danger"
																				borderWidth="1"
																				borderColor="danger"
																			>
																				<Inline gap="2">
																					<Box>
																						<Icons.Attention
																							color="danger"
																							size="5"
																						/>
																					</Box>
																					<Stack gap="3">
																						<Inline
																							gap="3"
																							paddingBottom="2"
																							alignItems="center"
																						>
																							<Text color="danger">
																								Are you sure you want to remove{" "}
																								<b>{rt.name}</b> Room?
																							</Text>
																						</Inline>
																					</Stack>
																				</Inline>
																			</Box>
																		</DialogBody>
																		<DialogFooter>
																			<Inline justifyContent="end">
																				<Button
																					level="primary"
																					status="danger"
																					onClick={() => {
																						onRemoveRoomType(rt.id)
																						setIsOpen(false)
																					}}
																				>
																					I understand, remove it
																				</Button>
																			</Inline>
																		</DialogFooter>
																	</Dialog>
																</>
															)}
														</Component>
													) : null
												}
											/>

											{/* Base Plan Row Rate Inputs */}
											{baseMealPlan && baseRowIndex !== -1 ? (
												<>
													{configureLowerOccupancy
														? lowerCols.map((col, colIdx) => {
																const lowerIdx =
																	baseRowIndex * itemsPerRoom + colIdx
																const isLower = col.occ < rtBaseOcc

																if (!isLower) {
																	return (
																		<Box
																			as="td"
																			key={col.key}
																			padding="2"
																			textAlign="center"
																			borderRightWidth="1"
																			borderBottomWidth="1"
																			style={{
																				borderColor: GRID_BORDER_COLOR,
																				background: NOT_SELLABLE_BACKGROUND,
																			}}
																		/>
																	)
																}

																return (
																	<Box
																		as="td"
																		key={col.key}
																		padding="2"
																		paddingX="6"
																		textAlign="center"
																		verticalAlign="top"
																		borderRightWidth="1"
																		borderBottomWidth="1"
																		style={{
																			borderColor: GRID_BORDER_COLOR,
																			position: "relative",
																		}}
																	>
																		<Stack gap="1">
																			<OccupancyBadgeSlot />
																			<RoomRateCell
																				cellIdx={lowerIdx}
																				initialValue={
																					initialGridRates[lowerIdx]
																						?.rate_before_tax
																				}
																				isDerived={
																					columnDerivations[col.key]?.mode ===
																					"derived"
																				}
																				hasBaseline={hasBaseline}
																				warningStore={warningStore}
																				onManualEdit={markCellManual}
																				onResetCell={resetCell}
																				onRevertCell={revertCell}
																				onChange={handleBaseRateChange}
																			/>
																		</Stack>
																	</Box>
																)
															})
														: null}

													{/* BASE Input Cell */}
													{(() => {
														const baseIndex =
															baseRowIndex * itemsPerRoom +
															(configureLowerOccupancy ? numLowerCols : 0)
														return (
															<Box
																as="td"
																padding="2"
																paddingX="6"
																textAlign="center"
																verticalAlign="top"
																borderRightWidth="1"
																borderBottomWidth="1"
																style={{
																	borderColor: GRID_BORDER_COLOR,
																	position: "relative",
																}}
															>
																<Stack gap="1">
																	<OccupancyBadgeSlot
																		label={getOccupancyInfo(rtBaseOcc).label}
																	/>
																	<RoomRateCell
																		cellIdx={baseIndex}
																		initialValue={
																			initialGridRates[baseIndex]
																				?.rate_before_tax
																		}
																		hasBaseline={hasBaseline}
																		warningStore={warningStore}
																		onManualEdit={markCellManual}
																		onResetCell={resetCell}
																		onRevertCell={revertCell}
																		onChange={handleBaseRateChange}
																	/>
																</Stack>
															</Box>
														)
													})()}

													{/* Extra Columns */}
													{roomCols.map((col, colIndex) => {
														const colOffsetInRoom =
															(configureLowerOccupancy ? numLowerCols : 0) +
															1 +
															colIndex
														const idx =
															baseRowIndex * itemsPerRoom + colOffsetInRoom
														return (
															<Box
																as="td"
																key={col.key}
																padding="2"
																paddingX="6"
																textAlign="center"
																verticalAlign="top"
																borderRightWidth="1"
																borderBottomWidth="1"
																style={{
																	borderColor: GRID_BORDER_COLOR,
																	position: "relative",
																}}
															>
																<Stack gap="1">
																	<OccupancyBadgeSlot />
																	<RoomRateCell
																		cellIdx={idx}
																		initialValue={
																			initialGridRates[idx]?.rate_before_tax
																		}
																		isDerived={
																			col.key.toLowerCase() !== "aweb" &&
																			columnDerivations[col.key]?.mode ===
																				"derived"
																		}
																		hasBaseline={hasBaseline}
																		warningStore={warningStore}
																		onManualEdit={markCellManual}
																		onResetCell={resetCell}
																		onRevertCell={revertCell}
																		onChange={handleBaseRateChange}
																	/>
																</Stack>
															</Box>
														)
													})}
												</>
											) : (
												<Box
													as="td"
													colSpan={
														(configureLowerOccupancy ? lowerCols.length : 0) +
														1 +
														roomCols.length
													}
													bgColor="subtle"
													borderRightWidth="1"
													borderBottomWidth="1"
													style={{ borderColor: GRID_BORDER_COLOR }}
												/>
											)}
										</Box>

										{/* Meal Plan Sub-Rows */}
										{subRowItems.map((subRowItem) => {
											const mp = subRowItem.mp
											const mpRowIndex = subRowItem.rowIndex
											return (
												<Box as="tr" key={`${rt.id}_${mp.id}`}>
													<MealPlanRowCell mealPlan={mp} />

													{configureLowerOccupancy
														? lowerCols.map((col, colIdx) => {
																const lowerIdx =
																	mpRowIndex * itemsPerRoom + colIdx
																const isLower = col.occ < rtBaseOcc

																if (!isLower) {
																	return (
																		<Box
																			as="td"
																			key={col.key}
																			padding="2"
																			textAlign="center"
																			borderRightWidth="1"
																			borderBottomWidth="1"
																			style={{
																				borderColor: GRID_BORDER_COLOR,
																				background: NOT_SELLABLE_BACKGROUND,
																			}}
																		/>
																	)
																}

																return (
																	<Box
																		as="td"
																		key={col.key}
																		padding="2"
																		paddingX="6"
																		textAlign="center"
																		verticalAlign="top"
																		borderRightWidth="1"
																		borderBottomWidth="1"
																		style={{
																			borderColor: GRID_BORDER_COLOR,
																			backgroundColor: RATE_CELL_BACKGROUND,
																			position: "relative",
																		}}
																	>
																		<RoomRateCell
																			cellIdx={lowerIdx}
																			initialValue={
																				initialGridRates[lowerIdx]
																					?.rate_before_tax
																			}
																			isDerived={true}
																			hasBaseline={hasBaseline}
																			warningStore={warningStore}
																			onManualEdit={markCellManual}
																			onResetCell={resetCell}
																			onRevertCell={revertCell}
																		/>
																	</Box>
																)
															})
														: null}

													{/* Sub-row BASE cell */}
													{(() => {
														const subBaseIndex =
															mpRowIndex * itemsPerRoom +
															(configureLowerOccupancy ? numLowerCols : 0)
														return (
															<Box
																as="td"
																padding="2"
																paddingX="6"
																textAlign="center"
																verticalAlign="top"
																borderRightWidth="1"
																borderBottomWidth="1"
																style={{
																	borderColor: GRID_BORDER_COLOR,
																	backgroundColor: RATE_CELL_BACKGROUND,
																	position: "relative",
																}}
															>
																<RoomRateCell
																	cellIdx={subBaseIndex}
																	initialValue={
																		initialGridRates[subBaseIndex]
																			?.rate_before_tax
																	}
																	isDerived={true}
																	hasBaseline={hasBaseline}
																	warningStore={warningStore}
																	onManualEdit={markCellManual}
																	onResetCell={resetCell}
																	onRevertCell={revertCell}
																/>
															</Box>
														)
													})()}

													{/* Sub-row Extra Cols */}
													{roomCols.map((col, colIndex) => {
														const colOffsetInRoom =
															(configureLowerOccupancy ? numLowerCols : 0) +
															1 +
															colIndex
														const idx =
															mpRowIndex * itemsPerRoom + colOffsetInRoom
														return (
															<Box
																as="td"
																key={col.key}
																padding="2"
																paddingX="6"
																textAlign="center"
																verticalAlign="top"
																borderRightWidth="1"
																borderBottomWidth="1"
																style={{
																	borderColor: GRID_BORDER_COLOR,
																	backgroundColor: RATE_CELL_BACKGROUND,
																	position: "relative",
																}}
															>
																<RoomRateCell
																	cellIdx={idx}
																	initialValue={
																		initialGridRates[idx]?.rate_before_tax
																	}
																	isDerived={true}
																	hasBaseline={hasBaseline}
																	warningStore={warningStore}
																	onManualEdit={markCellManual}
																	onResetCell={resetCell}
																	onRevertCell={revertCell}
																	onChange={handleBaseRateChange}
																/>
															</Box>
														)
													})}
												</Box>
											)
										})}
									</Fragment>
								)
							})}
						</Box>
					</Box>
				</Box>

				{/* Add Room Dropdown below table */}
				{removedRoomTypes.length > 0 ? (
					<Inline justifyContent="start">
						<Dropdown noCaret>
							<Dropdown.ToggleButton level="secondary" size="sm">
								<Icons.Plus />
								Add Room
							</Dropdown.ToggleButton>
							<Dropdown.Menu style={{ top: "auto", bottom: "100%" }}>
								{removedRoomTypes.map((rt, index) => {
									const rtBaseOcc = getRoomBaseOccupancy(rt)
									return (
										<Fragment key={rt.id}>
											{index > 0 ? (
												<Dropdown.MenuItemDivider marginY="0" />
											) : null}
											<Dropdown.MenuItem
												onClick={(e) => {
													e.preventDefault()
													onRestoreRoomType(rt.id)
												}}
											>
												<Stack gap="1" paddingRight="4">
													<Text>{rt.name}</Text>
													<Text fontSize="sm" color="muted">
														Max {rt.max_adult_occupancy || rtBaseOcc}A |{" "}
														{rt.max_child_occupancy || 2}C
													</Text>
												</Stack>
											</Dropdown.MenuItem>
										</Fragment>
									)
								})}
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				) : null}
			</Stack>
		</form>
	)
}

interface IRateSheetNameFormSchema {
	name: string
}

function EditRateSheetNameForm({
	initialName,
	placeholder = "e.g. Summer 2025",
	onSubmit,
}: {
	initialName?: string
	placeholder?: string
	onSubmit: (name: string) => void
}) {
	return (
		<Form<IRateSheetNameFormSchema>
			initialValues={{ name: initialName || "" }}
			validate={validateFormValues(rateSheetNameValidationSchema)}
			onSubmit={withServerErrors(async ({ name }) => {
				onSubmit(name)
			})}
		>
			{({ handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<TextInputField
							label="Name"
							name="name"
							type="text"
							placeholder={placeholder}
							autoFocus
						/>
						<SubmissionError />
						<Inline justifyContent="end" gap="2">
							<Button type="submit" status="primary" level="primary">
								{initialName ? "Update" : "Next"}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

// Owns the dialog, the caller owns whatever opens it
export function EditRateSheetNameInDialog({
	initialName,
	placeholder,
	onSuccess,
	children,
}: {
	initialName?: string
	placeholder?: string
	onSuccess: (name: string) => void
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ open })}
			<Dialog
				open={isOpen}
				onClose={close}
				centered
				sm
				title={initialName ? "Update Name" : "New Rate Sheet"}
			>
				<DialogBody>
					<EditRateSheetNameForm
						initialName={initialName}
						placeholder={placeholder}
						onSubmit={(name) => {
							close()
							onSuccess(name)
						}}
					/>
				</DialogBody>
			</Dialog>
		</>
	)
}

// The name in the step headers. A sheet that has lost its name shows an
// "Add name" button in its place, so it can be given one again before saving.
function EditRateSheetName({
	initialName,
	onSuccess,
}: {
	initialName?: string
	onSuccess: (name: string) => void
}) {
	return (
		<EditRateSheetNameInDialog initialName={initialName} onSuccess={onSuccess}>
			{({ open }) => (
				<Text
					fontSize="sm"
					fontWeight="normal"
					color="muted"
					onClick={open}
					cursor="pointer"
				>
					{initialName || "Add Name"}{" "}
					{initialName ? <Icons.Pencil /> : <Icons.Plus />}
				</Text>
			)}
		</EditRateSheetNameInDialog>
	)
}

function RoomsMultiSelect({
	selectedRooms,
	roomTypes,
	onChange,
}: {
	selectedRooms: TOtaRoomType[]
	roomTypes: TOtaRoomType[]
	onChange: (newRooms: TOtaRoomType[], allSelected: boolean) => void
}) {
	const [isOpen, setIsOpen] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!isOpen) return
		const handleClick = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
			}
		}
		document.addEventListener("click", handleClick, true)
		return () => document.removeEventListener("click", handleClick, true)
	}, [isOpen])

	const selected = selectedRooms || []
	const isAllSelected =
		selected.length === 0 || selected.length === roomTypes.length

	const updateSelectedRooms = (newRooms: TOtaRoomType[]) => {
		const allSelected =
			!newRooms || newRooms.length === 0 || newRooms.length === roomTypes.length
		const valueToStore = allSelected ? [] : newRooms
		onChange(valueToStore, allSelected)
	}

	const toggleAll = () => {
		updateSelectedRooms([])
	}

	return (
		<Stack gap="2">
			<Box ref={dropdownRef} display="inlineBlock">
				<Dropdown noCaret>
					<Dropdown.Toggle
						onClick={(e) => {
							e.preventDefault()
							setIsOpen(!isOpen)
						}}
					>
						<Text whiteSpace="nowrap">
							Rooms:{" "}
							{isAllSelected ? "All Rooms" : `${selected.length} Selected`}
							<Icons.ChevronDown />
						</Text>
					</Dropdown.Toggle>
					<Dropdown.Menu
						style={{
							maxHeight: "10rem",
							display: isOpen ? "block" : "none",
						}}
						overflowY="auto"
					>
						<Dropdown.MenuItem
							onClick={(e) => {
								e.preventDefault()
								toggleAll()
							}}
						>
							<Inline gap="3" alignItems="center">
								<CheckboxInput checked={isAllSelected} readOnly tabIndex={-1} />
								<Box as="span">All Rooms</Box>
							</Inline>
						</Dropdown.MenuItem>
						{roomTypes.map((rt) => {
							const isChecked =
								isAllSelected || selected.some((s) => s.id === rt.id)
							return (
								<Dropdown.MenuItem
									key={rt.id}
									onClick={(e) => {
										e.preventDefault()
										if (isAllSelected) {
											updateSelectedRooms(
												roomTypes.filter((r) => r.id !== rt.id)
											)
										} else {
											if (isChecked) {
												const newSelected = selected.filter(
													(s) => s.id !== rt.id
												)
												updateSelectedRooms(newSelected)
											} else {
												const newSelected = [...selected, rt]
												if (newSelected.length === roomTypes.length) {
													updateSelectedRooms([])
												} else {
													updateSelectedRooms(newSelected)
												}
											}
										}
									}}
								>
									<Inline gap="3" alignItems="center">
										<CheckboxInput checked={isChecked} readOnly tabIndex={-1} />
										<Box as="span">{rt.name}</Box>
									</Inline>
								</Dropdown.MenuItem>
							)
						})}
					</Dropdown.Menu>
				</Dropdown>
			</Box>
			{!isAllSelected && selected.length > 0 ? (
				<Inline gap="2" flexWrap="wrap">
					{selected.map((rt) => (
						<Badge
							primary
							key={rt.id}
							onClick={() => {
								const newSelected = selected.filter((s) => s.id !== rt.id)
								updateSelectedRooms(newSelected)
							}}
							style={{ cursor: "pointer" }}
						>
							<Inline gap="1" title={rt.name}>
								<CheckboxInput checked readOnly tabIndex={-1} />
								<Text
									fontSize="sm"
									style={{ maxWidth: "5rem" }}
									textOverflow="truncate"
									fontWeight="normal"
									color="black"
								>
									{rt.name}
								</Text>
							</Inline>
						</Badge>
					))}
				</Inline>
			) : null}
		</Stack>
	)
}

export function ConnectedRateSheetPreview({
	onBack,
	onSubmit,
	submitting,
	isEdit,
	original,
}: {
	onBack?: () => void
	onSubmit: () => void
	submitting?: boolean
	isEdit?: boolean
	original?: TOriginalSheet
}) {
	const { value: name, onChange: onNameChange } =
		useFieldValue<INewRateSheetFormSchema["name"]>("name")
	const { value: hotels } =
		useFieldValue<INewRateSheetFormSchema["hotels"]>("hotels")
	const { value: hotelPolicies } =
		useFieldValue<INewRateSheetFormSchema["hotelPolicies"]>("hotelPolicies")
	const { value: hotelPaymentPolicies } = useFieldValue<
		INewRateSheetFormSchema["hotelPaymentPolicies"]
	>("hotelPaymentPolicies")
	const { value: seasons } =
		useFieldValue<INewRateSheetFormSchema["seasons"]>("seasons")

	return (
		<RateSheetPreview
			onBack={onBack}
			onSubmit={onSubmit}
			submitting={submitting}
			isEdit={isEdit}
			original={original}
			onNameChange={onNameChange}
			data={{ name, hotels, hotelPolicies, hotelPaymentPolicies, seasons }}
		/>
	)
}

// The wizard's step 3. A saved sheet is rendered by `RateSheetDetail` instead,
// which reads the sheet from the API rather than from the form.
export function RateSheetPreview({
	onBack,
	onSubmit,
	submitting,
	isEdit = false,
	original,
	onNameChange,
	data,
}: {
	onBack?: () => void
	onSubmit?: (e?: React.SyntheticEvent<HTMLFormElement>) => void
	submitting?: boolean
	// Turns the comparison on. The baseline itself lives in `original`, which
	// the wizard captures once when it mounts — see `buildOriginalSheet`.
	isEdit?: boolean
	original?: TOriginalSheet
	onNameChange?: (name: string) => void
	data?: INewRateSheetFormSchema
}) {
	const isMobile = useIsMobile()
	const {
		name,
		hotels = [],
		hotelPolicies = {},
		hotelPaymentPolicies = {},
		seasons = [],
	} = data || {}
	const { policyHeads } = useSheetPolicyOptions()
	const {
		activeHotelId,
		activeSeasonIdx,
		setSelectedHotelId,
		setSelectedSeasonIdx,
	} = useSheetSelection(hotels, seasons)

	const hotelDetailsById = useSheetHotelDetails(hotels)

	const seasonsMeta = useMemo(
		() =>
			seasons.map(({ _uid, name: seasonName, intervals }) => ({
				_uid,
				name: seasonName,
				intervals,
			})),
		[seasons]
	)

	// The summary reads blocks by season x hotel, the same shape step 2 keeps
	// them in, so both steps count the same things.
	const ratesRef = useMemo(() => buildSheetRatesRef(seasons), [seasons])

	const summary = useMemo(
		() =>
			computeSheetSummary({
				name,
				originalName: original?.name,
				hotels,
				hotelDetailsById,
				seasons: seasonsMeta,
				hotelPolicies,
				hotelPaymentPolicies,
				ratesRef,
				originalRatesRef: original?.rates,
				originalSeasons: original?.seasons,
				originalHotels: original?.hotels,
				originalHotelPolicies: original?.hotelPolicies,
				originalHotelPaymentPolicies: original?.hotelPaymentPolicies,
				policyHeads,
				isEdit,
			}),
		[
			name,
			hotels,
			hotelDetailsById,
			seasonsMeta,
			hotelPolicies,
			hotelPaymentPolicies,
			policyHeads,
			ratesRef,
			original,
			isEdit,
		]
	)

	const goToBlock = useCallback(
		(hotelId?: number, seasonIdx?: number) => {
			if (hotelId !== undefined) setSelectedHotelId(hotelId)
			if (seasonIdx !== undefined) setSelectedSeasonIdx(seasonIdx)
		},
		[setSelectedHotelId, setSelectedSeasonIdx]
	)

	// The same reading the status bar puts on the screen beside this button: an
	// edited sheet with nothing to report has nothing to save. A sheet being
	// created is never in this state — it has no baseline to differ from, so it
	// records no changes at all and would otherwise be unsaveable. A sheet that
	// has lost its name still can be saved, as that is how it gets one back.
	const hasNothingToSave = isEdit && !!name && summary.changes.length === 0

	const activeHotel = hotels.find((hotel) => Number(hotel.id) === activeHotelId)
	const activeSeason = seasons[activeSeasonIdx]
	const activeSeasonKey = activeSeason?._uid || activeSeasonIdx
	const activeBlock = activeHotel
		? ratesRef.get(rateKey(activeSeasonKey, Number(activeHotel.id))) ||
			ratesRef.get(rateKey(activeSeasonIdx, Number(activeHotel.id)))
		: undefined
	const baselineBlock =
		isEdit && activeHotel
			? original?.rates.get(rateKey(activeSeasonKey, Number(activeHotel.id))) ||
				original?.rates.get(rateKey(activeSeasonIdx, Number(activeHotel.id)))
			: undefined

	return (
		<>
			<StepHeader
				onBack={() => onBack?.()}
				backTitle={
					<Inline gap="2" alignItems="center">
						<Inline
							size="5"
							alignItems="center"
							justifyContent="center"
							borderWidth="1"
							rounded="full"
							color="muted"
							style={{ borderColor: "#60646C" }}
						>
							<Text fontSize="sm" color="muted">
								2
							</Text>
						</Inline>
						<Text fontSize="sm" color="muted" fontWeight="semibold">
							Seasons & Rates
						</Text>
					</Inline>
				}
				currentStepNumber={3}
				currentTitle={
					<>
						<Text color="primary" fontSize="md" fontWeight="semibold">
							Preview
						</Text>
						{onNameChange ? (
							<EditRateSheetName initialName={name} onSuccess={onNameChange} />
						) : (
							<Text fontSize="sm" color="muted">
								{name}
							</Text>
						)}
					</>
				}
				statusBar={
					<SheetStatusBar
						summary={summary}
						isEdit={isEdit}
						onGoToBlock={goToBlock}
					/>
				}
				actionButton={
					// A sheet that has lost its name is asked for one before it is saved
					<EditRateSheetNameInDialog
						initialName={name}
						onSuccess={(newName) => {
							onNameChange?.(newName)
							onSubmit?.()
						}}
					>
						{({ open }) => (
							<Tooltip
								content={hasNothingToSave ? "No changes to save" : undefined}
								disabled={!hasNothingToSave}
							>
								<Button
									level="primary"
									type="button"
									disabled={submitting || hasNothingToSave}
									onClick={name || !onNameChange ? () => onSubmit?.() : open}
									style={{ opacity: submitting || hasNothingToSave ? 0.5 : 1 }}
								>
									{submitting ? <Icons.Spinner /> : null}
									{submitting ? "Saving..." : "Save"}
								</Button>
							</Tooltip>
						)}
					</EditRateSheetNameInDialog>
				}
			/>

			{hotels.length === 0 || seasons.length === 0 ? (
				<Stack
					alignItems="center"
					justifyContent="center"
					gap="2"
					paddingY="16"
					paddingX="6"
				>
					<Icons.Bed size="12" color="subtle" />
					<Text color="muted">
						{hotels.length === 0
							? "No hotels selected."
							: "No seasons configured."}
					</Text>
				</Stack>
			) : (
				<Tabs
					layout={isMobile ? "col" : "row"}
					style={{ minHeight: "calc(100vh - 12rem)" }}
				>
					<TabsList
						position={isMobile ? undefined : "sticky"}
						style={{
							minWidth: isMobile ? undefined : "12rem",
							maxWidth: isMobile ? undefined : "16rem",
							padding: 0,
							rowGap: 0,
						}}
						flexShrink="0"
						minHeight="full"
					>
						{isMobile ? null : (
							<>
								<Inline
									alignItems="center"
									padding="4"
									paddingLeft="6"
									bgColor="white"
								>
									<Text
										fontWeight="semibold"
										color="muted"
										style={{ lineHeight: "28px" }}
									>
										{`Seasons (${seasons.length})`}{" "}
										<Icons.ArrowLeft rotate="180" />
									</Text>
								</Inline>
								<Inline
									paddingLeft="6"
									alignItems="center"
									color="muted"
									gap="1"
									fontWeight="semibold"
									bgColor="white"
									padding="4"
								>
									<Text
										style={{ lineHeight: "28px" }}
									>{`Hotels (${hotels.length})`}</Text>
									<Box>
										<Icons.ArrowLeft rotate="270" />
									</Box>
								</Inline>
							</>
						)}
						{hotels.map((hotel) => {
							const hotelId = Number(hotel.id)
							return (
								<HotelSidebarItem
									key={hotel.id}
									hotel={hotel}
									active={activeHotelId === hotelId}
									onClick={() => setSelectedHotelId(hotelId)}
									indicators={summary.byHotel.get(hotelId)}
								/>
							)
						})}
					</TabsList>

					<TabContent active flex="1" minWidth="0" style={{ padding: 0 }}>
						<Stack>
							<Tabs layout="col">
								<TabsList
									flex="1"
									borderBottomWidth={isMobile ? "1" : "0"}
									style={{ paddingInline: 0 }}
								>
									{seasons.map((season, sIdx) => (
										<TabItem
											key={season._uid || sIdx}
											active={activeSeasonIdx === sIdx}
											onClick={() => setSelectedSeasonIdx(sIdx)}
											bgColor={activeSeasonIdx === sIdx ? "primary" : undefined}
											style={{
												borderBottom: isMobile ? 0 : undefined,
											}}
										>
											<Stack paddingX={isMobile ? "1" : "2"}>
												<Inline gap="2" alignItems="center">
													<Text
														fontWeight="semibold"
														color={
															activeSeasonIdx === sIdx ? "primary" : undefined
														}
													>
														{season.name}
													</Text>
													<SeasonTabIndicators
														indicators={summary.bySeason.get(
															rateKey(season._uid || sIdx, activeHotelId)
														)}
													/>
												</Inline>
												<Text fontSize="xs" color="muted" fontWeight="normal">
													{pluralize(season.intervals.length, "Interval")}
												</Text>
											</Stack>
										</TabItem>
									))}
								</TabsList>
							</Tabs>

							{activeSeason?.intervals?.length ? (
								<SeasonIntervals intervals={activeSeason.intervals} />
							) : null}

							{activeHotel ? (
								<RateSheetPreviewBlock
									hotel={hotelDetailsById.get(activeHotelId) || activeHotel}
									block={activeBlock}
									baselineBlock={baselineBlock}
									isEdit={isEdit}
									isMobile={isMobile}
								/>
							) : null}
						</Stack>
					</TabContent>
				</Tabs>
			)}
		</>
	)
}

// Everything priced for one hotel in one season: the policies it sells under
// and the rates themselves.
function RateSheetPreviewBlock({
	hotel,
	block,
	baselineBlock,
	isEdit,
	isMobile,
}: {
	hotel: IHotel
	block: IRateBlockSchema | undefined
	baselineBlock: IRateBlockSchema | undefined
	isEdit: boolean
	isMobile: boolean
}) {
	// The columns and rows the block was priced with, read off the block itself.
	// The same helper feeds `computeBlockSummary`, so the table below cannot show
	// a diff the sheet's change count does not see. The baseline widens the rows
	// to rooms the sheet no longer prices, so a removed one is struck through
	// rather than silently gone.
	const { roomTypes, roomCols, lowerCols } = useMemo(
		() => blockGridOverrides(hotel, block, isEdit ? baselineBlock : undefined),
		[hotel, block, baselineBlock, isEdit]
	)

	if (!block) {
		return (
			<Stack padding={isMobile ? "3" : "6"}>
				<Text color="muted">Nothing priced for this hotel in this season.</Text>
			</Stack>
		)
	}

	return (
		<Stack gap="6" padding={isMobile ? "3" : "6"}>
			<Inline gap="6" alignItems="center" flexWrap="wrap">
				<Heading as="h4" color="primary">
					Policies
				</Heading>
				{block.cancellation_policy ? (
					<PolicyChip kind="cancellation" policy={block.cancellation_policy} />
				) : (
					<Text color="muted">No cancellation policy</Text>
				)}
				{block.payment_policy ? (
					<PolicyChip kind="payment" policy={block.payment_policy} />
				) : (
					<Text color="muted">No payment policy</Text>
				)}
			</Inline>

			<Stack gap="6">
				<Heading as="h4" color="primary">
					Rates
				</Heading>
				<RoomRatesPreviewTable
					hotel={hotel}
					rates={block.rates}
					initialRates={baselineBlock?.rates}
					mealPlans={block.meal_plans}
					roomTypes={roomTypes}
					roomCols={roomCols}
					lowerCols={lowerCols}
					colChanges={roomColChanges(block, isEdit ? baselineBlock : undefined)}
					configureLowerOccupancy={Boolean(
						block.is_lower_occupancy_rate_configured
					)}
					initialConfigureLowerOccupancy={Boolean(
						baselineBlock?.is_lower_occupancy_rate_configured
					)}
					isEdit={isEdit}
					currency={hotel.currency}
				/>
			</Stack>
		</Stack>
	)
}

type TPolicyOption = { id: number; name: string }

export function PolicyKindIcon({ kind }: { kind: TPolicyKind }) {
	return kind === "cancellation" ? (
		<Icons.CalendarCross size="4" color="muted" />
	) : (
		<Icons.BankNotes size="4" color="muted" />
	)
}

// Colour dot per policy. Left off for now — will be enabled later.
// export function PolicyColorDot({
// 	policy,
// 	size = "10px",
// }: {
// 	policy: TAnyPolicy | null | undefined
// 	size?: string
// }) {
// 	if (!policy) return null
// 	return (
// 		<Box
// 			rounded="full"
// 			flexShrink="0"
// 			style={{
// 				backgroundColor: policyColor(policy),
// 				height: size,
// 				width: size,
// 			}}
// 		/>
// 	)
// }

// "Update to Latest (v6)" — the sheet is pinned to a version its head has moved
// past. Blocking, so it is styled as an error rather than as a hint.
export function UpdateToLatestLink({
	policy,
	heads,
	onUpdate,
}: {
	policy: TAnyPolicy | null | undefined
	heads: TAnyPolicyDetail[] | undefined
	onUpdate: (latest: TAnyPolicy) => void
}) {
	if (!isPolicyStale(policy, heads)) return null
	const latest = latestPolicyFor(policy, heads)
	if (!latest) return null
	return (
		<Inline
			gap="1"
			alignItems="center"
			color="danger"
			cursor="pointer"
			onClick={() => onUpdate(latest)}
		>
			<Icons.CircleAlert size="4" />
			<Text fontSize="sm" fontWeight="semibold">
				Update to Latest (v{latest.version})
			</Text>
		</Inline>
	)
}

// The policy name as it reads outside an editable control: the name, and the
// pinned version when it is not the head's latest.
export function PolicyChip({
	kind,
	policy,
	isStale = false,
}: {
	kind: TPolicyKind
	policy: Pick<TAnyPolicy, "head_name" | "version"> | null | undefined
	isStale?: boolean
}) {
	return (
		<Inline gap="2" alignItems="center">
			<Box>
				<PolicyKindIcon kind={kind} />
			</Box>
			{/* Colour dot. Left off for now — will be enabled later. */}
			{/* <PolicyColorDot policy={policy} /> */}
			<Text whiteSpace="nowrap" fontWeight={isStale ? "semibold" : "normal"}>
				{policy?.head_name || "None"}
			</Text>
			{isStale && policy ? <VersionBadge version={policy.version} /> : null}
		</Inline>
	)
}

export function VersionBadge({ version }: { version: number }) {
	if (!version) return null
	return (
		<Box
			paddingX="1"
			rounded="md"
			bgColor="inset"
			borderWidth="1"
			title={`Version ${version}`}
		>
			<Text fontSize="xs" color="muted" fontWeight="semibold">
				v{version}
			</Text>
		</Box>
	)
}

// The editable control. `onClear` renders the ✕ that drops a block level
// override; `onRevert` renders the ↺ that puts back what the sheet was last
// saved with. Both are omitted where they do not apply — step 1 has no hotel
// policy to fall back to, and a sheet being created has nothing to revert to.
export function PolicySelect({
	kind,
	policy,
	options,
	heads,
	onChange,
	onClear,
	onRevert,
	isOverridden = false,
	maxWidth,
	selectKey,
	fetchOnMount = true,
}: {
	kind: TPolicyKind
	policy: TAnyPolicy | null
	options?: TPolicyOption[]
	heads?: TAnyPolicyDetail[] | undefined
	onChange: (policy: TAnyPolicy | null) => void
	onClear?: () => void
	onRevert?: () => void
	isOverridden?: boolean
	maxWidth?: "sm" | "md"
	// Remounts the control when the thing it is editing changes underneath it
	selectKey?: string | number
	fetchOnMount?: boolean
}) {
	const xhr = useXHR()
	// Addressed by head, so a pinned older version still matches its option
	const selectValue = policy
		? {
				id: Number(policyHeadId(policy) ?? policy.id),
				name: policyHeadName(policy) || "",
			}
		: null
	const stale = isPolicyStale(policy, heads)
	// The version is only worth the space when it is not the latest — every
	// freshly picked policy is, and badging all of them says nothing.
	const showVersion = Boolean(policy && stale)
	const actionCount = (onClear ? 1 : 0) + (onRevert ? 1 : 0)
	// Room for the badge and the actions on the right
	const paddingRight = 2 + actionCount * 1.5 + (showVersion ? 2.5 : 0)

	return (
		<Stack gap="1">
			<Box position="relative">
				{/* Colour dot on the selected policy. Left off for now — will be
				    enabled later. */}
				{/* {policy ? (
					<Box
						position="absolute"
						style={{
							left: "0.75rem",
							top: "50%",
							transform: "translateY(-50%)",
							marginTop: "0.75rem",
							zIndex: 1,
							pointerEvents: "none",
						}}
					>
						<PolicyColorDot policy={policy} />
					</Box>
				) : null} */}
				<AsyncSelect
					key={selectKey}
					maxWidth={maxWidth}
					cacheKey={`rate-sheet-policy-heads-${kind}`}
					fetchOnMount={fetchOnMount}
					label={
						<Inline gap="1" alignItems="center" marginBottom="1">
							<Box>
								<PolicyKindIcon kind={kind} />
							</Box>
							<Text>{policyKindLabel(kind)}</Text>
						</Inline>
					}
					value={selectValue}
					defaultOptions={heads && heads.length > 0 ? heads : options}
					borderColor={isOverridden ? "warning" : undefined}
					bgColor={isOverridden ? "warning" : undefined}
					style={policy ? { paddingRight: `${paddingRight}rem` } : undefined}
					perFetchLimit={15}
					fetch={(q, { page }) => {
						const fetcher =
							kind === "cancellation"
								? cancellationPoliciesXHR(xhr).get({ limit: 15, page, q })
								: paymentPoliciesXHR(xhr).get({ limit: 15, page, q })
						return fetcher.then((resp) => resp.data)
					}}
					onChange={(option) => {
						const picked = option as
							| (TPolicyOption & { latest_policy?: TAnyPolicy })
							| null
							| undefined
						if (!picked) {
							onChange(null)
							return
						}
						if (picked.latest_policy) {
							const swrKey =
								kind === "cancellation"
									? "/cancellation-policy-heads"
									: "/payment-policy-heads"
							mutate(
								swrKey,
								(current: { data?: TAnyPolicyDetail[] } | undefined) => {
									if (!current || !current.data) return current
									const exists = current.data.some(
										(h) => Number(h.id) === Number(picked.id)
									)
									if (exists) return current
									return {
										...current,
										data: [...current.data, picked as TAnyPolicyDetail],
									}
								},
								false
							)
							onChange(
								withPolicyHead(
									picked.latest_policy as TAnyPolicy,
									picked as TAnyPolicyDetail
								)
							)
							return
						}
						onChange(pickPolicyHead(picked.id, heads))
					}}
					optionRenderer={({ option }: { option: TPolicyOption }) => (
						<Inline gap="2" alignItems="center">
							{/* Colour dot per option. Left off for now — will be enabled
							    later. */}
							{/* <Box
								size="3"
								rounded="full"
								flexShrink="0"
								style={{
									backgroundColor: policyColor({
										head_id: option.id,
									} as TAnyPolicy),
								}}
							/> */}
							<span>{option.name}</span>
						</Inline>
					)}
				/>
				{policy ? (
					<Box
						position="absolute"
						style={{
							right: "2rem",
							top: "50%",
							transform: "translateY(-50%)",
							marginTop: "0.75rem",
							zIndex: 1,
						}}
					>
						<Inline gap="1" alignItems="center">
							{showVersion ? <VersionBadge version={policy.version} /> : null}
							{onRevert ? (
								<Box
									cursor="pointer"
									title="Revert to the last saved policy"
									onClick={onRevert}
								>
									<Icons.Rotate size="4" color="muted" />
								</Box>
							) : null}
							{onClear ? (
								<Box
									cursor="pointer"
									title="Remove the override and use the hotel's policy"
									onClick={onClear}
								>
									<Icons.Cancel size="4" color="muted" />
								</Box>
							) : null}
						</Inline>
					</Box>
				) : null}
			</Box>
		</Stack>
	)
}

// The rules of the pinned version, read only. Cancellation and payment policies
// describe their rules differently enough that only the surrounding metadata is
// shared — a base block, then one per check-in date range, then the free text.
export function PolicyRulesPreview({
	kind,
	policy,
}: {
	kind: TPolicyKind
	policy: TAnyPolicy | null
}) {
	const resolved = usePinnedPolicyVersion(kind, policy)
	if (!resolved) return null

	const ruleSets =
		kind === "cancellation"
			? (resolved as TCancellationPolicy).rules || []
			: (resolved as TPaymentPolicy).rule_sets || []
	const baseRule = ruleSets.find(
		(rule) => rule.valid_from == null && rule.valid_to == null
	)
	const dateRules = ruleSets.filter(
		(rule) => rule.valid_from != null || rule.valid_to != null
	)

	return (
		<Stack gap="4">
			{baseRule ? <PolicyRuleBlock kind={kind} rule={baseRule} isBase /> : null}
			{dateRules.map((rule) => (
				<PolicyRuleBlock key={rule.id} kind={kind} rule={rule} isBase={false} />
			))}
			{resolved.additional_info ? (
				<Stack gap="2">
					<Text fontWeight="semibold">Additional information</Text>
					<Markdown>{resolved.additional_info}</Markdown>
				</Stack>
			) : null}
		</Stack>
	)
}

function PolicyRuleBlock({
	kind,
	rule,
	isBase,
}: {
	kind: TPolicyKind
	rule: TCancellationPolicyRule | TPaymentRuleSet
	isBase: boolean
}) {
	if (kind === "cancellation") {
		return (
			<PolicyRuleCard rule={rule as TCancellationPolicyRule} isBase={isBase} />
		)
	}
	const ruleSet = rule as TPaymentRuleSet
	return (
		<RuleBlock
			isBase={isBase}
			rules={ruleSet.rules}
			readOnly
			validFrom={ruleSet.valid_from ? parseDate(ruleSet.valid_from) : null}
			validTo={ruleSet.valid_to ? parseDate(ruleSet.valid_to) : null}
		/>
	)
}

export interface ISeasonIntervalItem {
	from?: Date | string | null
	to?: Date | string | null
	days: TDay[] | { day: number; name: TDay }[] | string[]
}

const DAYS_LIST: { label: string; name: TDay }[] = [
	{ label: "S", name: "Sunday" },
	{ label: "M", name: "Monday" },
	{ label: "T", name: "Tuesday" },
	{ label: "W", name: "Wednesday" },
	{ label: "T", name: "Thursday" },
	{ label: "F", name: "Friday" },
	{ label: "S", name: "Saturday" },
]

function getNormalizedDays(
	days: TDay[] | { day: number; name: TDay }[] | string[]
): string[] {
	if (!Array.isArray(days)) return []
	return days
		.map((d) => (typeof d === "string" ? d : d?.name || ""))
		.filter(Boolean)
}

export function IntervalDays({ days }: { days: ISeasonIntervalItem["days"] }) {
	const normalizedDays = getNormalizedDays(days)
	if (normalizedDays.length === 7) {
		return (
			<Text fontSize="xs" color="muted">
				All 7 Days
			</Text>
		)
	}
	return (
		<Inline gap="1" alignItems="center">
			{DAYS_LIST.map((d, index) => {
				const isSelected = normalizedDays.some(
					(dayName) => dayName.toLowerCase() === d.name.toLowerCase()
				)
				return (
					<Inline
						key={index}
						alignItems="center"
						justifyContent="center"
						borderWidth="1"
						borderColor={isSelected ? undefined : "emphasis"}
						style={isSelected ? { borderColor: "#3A5BC7" } : undefined}
						rounded="full"
						size="4"
					>
						<Text
							fontSize="xs"
							color={isSelected ? "primary" : "subtle"}
							style={{ fontSize: "10px", lineHeight: "1" }}
						>
							{d.label}
						</Text>
					</Inline>
				)
			})}
		</Inline>
	)
}

export function SeasonIntervals({
	intervals,
}: {
	intervals?: ISeasonIntervalItem[]
}) {
	const [isHovered, setIsHovered] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const badgeRef = useRef<HTMLDivElement>(null)
	const [visibleCount, setVisibleCount] = useState(intervals?.length || 0)

	useLayoutEffect(() => {
		const updateCount = () => {
			const container = containerRef.current
			if (!container || !intervals || intervals.length === 0) return
			const children = Array.from(container.children) as HTMLElement[]
			if (children.length === 0) return

			const baseTop = children[0].offsetTop
			const containerWidth = container.clientWidth
			const paddingRight =
				parseFloat(window.getComputedStyle(container).paddingRight) || 16
			const badgeWidth = badgeRef.current?.offsetWidth || 140
			const maxAllowedRight = containerWidth - paddingRight - badgeWidth - 12

			const line1Items: HTMLElement[] = []
			for (let i = 0; i < intervals.length; i++) {
				const child = children[i]
				if (!child) continue
				if (child.offsetTop > baseTop + 10) {
					break
				}
				line1Items.push(child)
			}

			if (line1Items.length === intervals.length) {
				setVisibleCount(intervals.length)
				return
			}

			let count = 0
			for (let i = 0; i < line1Items.length; i++) {
				const child = line1Items[i]
				const childRight = child.offsetLeft + child.offsetWidth
				if (childRight <= maxAllowedRight) {
					count++
				} else {
					break
				}
			}

			setVisibleCount(Math.max(1, count))
		}

		updateCount()

		let resizeObserver: ResizeObserver | undefined
		if (containerRef.current && typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(() => {
				updateCount()
			})
			resizeObserver.observe(containerRef.current)
		}

		window.addEventListener("resize", updateCount)
		return () => {
			window.removeEventListener("resize", updateCount)
			resizeObserver?.disconnect()
		}
	}, [intervals])

	if (!intervals || intervals.length === 0) {
		return null
	}

	const remainingCount = intervals.length - visibleCount

	return (
		<Inline
			ref={containerRef}
			position="relative"
			paddingX="4"
			paddingY="3"
			gap="6"
			alignItems="center"
			flexWrap="wrap"
			bgColor={isHovered ? "white" : "subtle"}
			rounded={isHovered ? "lg" : undefined}
			borderBottomColor="default"
			borderWidth="1"
			borderColor={isHovered ? "default" : "transparent"}
			overflow="hidden"
			cursor={remainingCount > 0 ? "pointer" : undefined}
			style={{
				transition:
					"max-height 300ms ease, background-color 200ms ease, border-color 200ms ease",
				maxHeight: isHovered ? "1000px" : "4.5rem",
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onTouchStartCapture={() => setIsHovered(true)}
			onTouchEndCapture={() => setIsHovered(false)}
		>
			{intervals.map((interval, iIdx) => {
				return (
					<Inline
						key={iIdx}
						gap="2"
						alignItems="center"
						style={{
							visibility:
								!isHovered && iIdx >= visibleCount ? "hidden" : undefined,
						}}
					>
						<Inline
							size="6"
							alignItems="center"
							justifyContent="center"
							borderWidth="1"
							rounded="full"
							color="muted"
							style={{ flexShrink: 0, borderColor: "#60646C" }}
						>
							<Text fontSize="sm" fontWeight="semibold">
								{iIdx + 1}
							</Text>
						</Inline>
						<Inline gap="2">
							<Box>
								<Icons.CalendarDays size="3" />
							</Box>
							<Stack gap="1">
								<Inline gap="1" alignItems="center">
									{interval.from || interval.to ? (
										<Text fontSize="sm" color="black">
											{formatDate(interval.from || "", "DD MMM, YYYY")} &rarr;{" "}
											{formatDate(interval.to || "", "DD MMM, YYYY")}
										</Text>
									) : (
										<Text fontSize="sm" color="muted">
											No dates set
										</Text>
									)}
								</Inline>
								<IntervalDays days={interval.days} />
							</Stack>
						</Inline>
					</Inline>
				)
			})}

			{remainingCount > 0 && !isHovered ? (
				<Box
					ref={badgeRef}
					style={{
						position: "absolute",
						right: "1rem",
						top: "50%",
						transform: "translateY(-50%)",
						paddingLeft: "0.5rem",
					}}
				>
					<Text color="primary" fontSize="sm" fontWeight="semibold">
						+{remainingCount} more{" "}
						{remainingCount === 1 ? "Interval" : "Intervals"}
					</Text>
				</Box>
			) : null}
		</Inline>
	)
}
